import type { FinancialTabTranslations } from "@/components/app/company-overview/financial/i18n";
import type { BalanceWizardTranslations } from "@/components/app/balance-wizard/i18n";
import type { AlertsTabTranslations } from "@/components/app/company-overview/alerts/i18n";

export type Language = "pt" | "es" | "en";

export const translations = {
  pt: {
    nav: {
      services: "360º",
      plans: "Planos",
      about: "Sobre",
      contact: "Fale Conosco",
      blog: "Blog",
      login: "Entrar",
      startFree: "Solicitar Demonstração",
    },
    hero: {
      badge: "Distribuidor Oficial Coface no Brasil",
      title: "Análise de Crédito Global:",
      titleHighlight: "Compradores, Fornecedores e Gestão de Cobranças",
      description:
        "Monitore scores e obtenha recomendações de crédito para compradores e fornecedores em mais de 190 países. Plataforma integrada pay-per-use con IA. Proteja seu capital de giro, otimize cobranças B2B e venda com segurança no Brasil e no exterior.",
      cta: "Explorar Plataforma",
      ctaSecondary: "Agendar Demo",
    },
    stats: {
      countries: "Países Cobertos",
      companies: "Empresas Monitoradas",
      experience: "Anos de Experiência Coface",
      recovery: "Taxa de Recuperação",
    },
    problems: {
      title: "Proteja-se com Acesso a Dados e Monitoramento Contínuo",
      subtitle:
        "Empresas brasileiras perdem milhões em exportações e importações todo ano por uma razão simples: confiam em parceiros que nunca investigaram.",
      defaultRisk: {
        title: "Risco Crescente de Inadimplência",
        description:
          "Sua carteira de clientes parece saudável, mas o risco de inadimplência pode estar escondido. Sem monitoramento ativo, você só descobre o problema quando o pagamento atrasa.",
      },
      countryRisk: {
        title: "Bloqueios por Risco-País",
        description:
          "Mesmo uma empresa legítima pode estar em uma região sob sanções ou instabilidade política. Sem análise atualizada, sua operação pode parar no meio da transação.",
      },
      supplyChain: {
        title: "Ruptura na Cadeia de Suprimentos",
        description:
          "Um fornecedor estratégico entra em dificuldades financeiras e sua produção para. Sem monitoramento contínuo, você só descobre quando o insumo crítico não chega.",
      },
      collection: {
        title: "Dificuldade na Recuperação de Dívidas",
        description:
          "Cobrar de um cliente é um processo exaustivo que pode destruir a relação comercial. Abordagens amadoras raramente entendem a complexidade do mercado B2B.",
      },
    },
    solution: {
      title: "A Solução 360º para Gestão de Risco Empresarial",
      subtitle:
        "Integramos a base de dados global da Coface e a expertise da Traderisk em uma única plataforma para você Analisar, Monitorar e Recuperar.",
      buyer: {
        title: "Buyer Study - Análise de Compradores",
        subtitle: "Decisões Baseadas em Dados",
        description:
          "Pare de vender às cegas. Com o Score Coface (DRA), você tem uma classificação de risco instantânea de qualquer empresa no Brasil e em mais de 190 países.",
      },
      supply: {
        title: "Supply - Gestão de Fornecedores",
        subtitle: "Monitore sua Cadeia de Suprimentos",
        description:
          "O risco não está só nos seus clientes. Nossa plataforma monitora a saúde financeira dos seus fornecedores 24/7. Qualquer mudança gera um alerta automático.",
      },
      recovery: {
        title: "Recovery - Gestão de Cobranças",
        subtitle: "Cobrança Profissional",
        description:
          "A inadimplência ocorreu? Ative o serviço de cobrança da Coface diretamente pela nossa plataforma. Recuperamos seus créditos de forma amigável ou judicial.",
      },
    },
    coface: {
      title: "Da base de dados da Coface para o seu negócio:",
      titleHighlight: "risco sob controle, em qualquer lugar do mundo.",
      description:
        "A Coface é referência mundial em gestão de risco corporativo. A Traderisk 360º transforma essa base global em uma experiência acessível e integrada.",
      features: [
        "Dashboard de risco para controlar sua carteira",
        "IA que interpreta relatórios de crédito",
        "Atualizações periódicas de risco-país",
        "Alertas automáticos e monitoramento contínuo",
      ],
      stats: {
        experience: "Anos de Experiência",
        revenue: "Receita Anual",
        countries: "Países",
      },
    },
    plans: {
      title: "Confira Nossos Planos",
      subtitle:
        "Compare o investimento necessário para essa transformação na sua operação",
      basic: {
        name: "Básico",
        description: "Uso ocasional",
        price: "Grátis",
        priceDetail: "Ideal para conhecer o sistema",
        features: [
          "Acesso à plataforma",
          "Consultas avulsas (pay-per-use)",
          "CRM Export (limite 20 leads/negócios)",
          "Pagamento via Pix/Cartão",
          "Suporte padrão",
        ],
        cta: "Solicitar Demonstração",
      },
      professional: {
        name: "Profissional",
        description: "Uso recorrente",
        price: "R$ 199,99",
        priceDetail: "/mês",
        popular: "Mais Popular",
        features: [
          "Todas funcionalidades Básico",
          "Traderisk 360º Buyer Study completo",
          "Traderisk 360º Supply completo",
          "Traderisk 360º Recovery básico",
          "CRM Export ilimitado",
          "Dashboard de gestão de carteira",
          "Análise interpretativa com IA",
          "Alertas automáticos",
          "Pagamento via faturamento mensal",
          "Suporte prioritário",
        ],
        cta: "Começar Agora",
      },
      enterprise: {
        name: "Enterprise",
        description: "Operações de grande porte",
        price: "Sob demanda",
        priceDetail: "Perfeito para grandes equipes",
        features: [
          "Todas funcionalidades Professional",
          "Traderisk 360º Recovery completo",
          "CRM Export ilimitado",
          "Integração API com ERP/CRM",
          "Solução Whitelabel para parceiros",
          "Dashboard executivo personalizado",
          "Gerente de conta dedicado",
          "SLA garantizado",
        ],
        cta: "Falar com Especialista",
      },
    },
    cta: {
      title: "Pare de apostar. Comece a decidir com inteligência.",
      description:
        "Cadastre-se gratuitamente na Traderisk 360º e faça sua primeira análise de crédito em minutos.",
      primary: "Solicitar Demonstração",
      secondary: "Agendar Demo",
    },
    form: {
      title: "Análise Gratuita de Risco País e Setorial",
      subtitle:
        "Receba um estudo personalizado sobre o risco de um país e setor específico. Entrega em até 24 horas, por e-mail.",
      name: "Nome Completo",
      email: "Email Corporativo",
      sector: "Setor de Interesse",
      country: "País a ser Avaliado",
      selectSector: "Selecione o setor",
      selectCountry: "Selecione o país",
      submit: "Solicitar Estudo Grátis",
      success: "Solicitação enviada com sucesso!",
    },
    footer: {
      description:
        "Transformando dados globais de crédito em decisões inteligentes para empresas brasileiras.",
      services: "360º",
      company: "Empresa",
      contact: "Fale Conosco",
      legal: "Legal",
      privacy: "Política de Privacidade",
      terms: "Termos de Uso",
      cookies: "Política de Cookies",
      rights: "Todos os direitos reservados.",
      address: "Endereço",
    },
    popup: {
      title: "Análise Gratuita de Risco País e Setorial",
      subtitle:
        "Receba um estudo personalizado sobre o risco de um país e setor específico",
      name: "Nome Completo",
      email: "Email Corporativo",
      sector: "Setor de Interesse",
      country: "País a ser Avaliado",
      submit: "Solicitar Estudo Grátis",
      later: "Talvez depois",
    },
    demo: {
      title: "Agende uma Demo",
      subtitle:
        "Escolha a melhor data e hora para conhecer a plataforma Traderisk 360º.",
      meta: {
        title: "Agendar Demo - TradeRisk 360º",
        description:
          "Agende uma demonstração personalizada da plataforma TradeRisk 360º.",
      },
      features: {
        personalized: {
          title: "Demonstração Personalizada",
          description:
            "Apresentação focada nas necessidades específicas da sua empresa",
        },
        consulting: {
          title: "Consultoria Especializada",
          description: "Tire suas dúvidas com especialistas em gestão de risco",
        },
        flexibility: {
          title: "Flexibilidade de Horário",
          description: "Escolha o melhor horário para você e sua equipe",
        },
      },
      form: {
        title: "Agende uma Demo",
        subtitle:
          "Escolha a melhor data e hora para conhecer a plataforma Traderisk 360º.",
        name: "Nome Completo",
        namePlaceholder: "Seu nome completo",
        email: "Email",
        emailPlaceholder: "seu@email.com",
        country: "País",
        selectCountry: "Selecione o país",
        phone: "Telefone (com código do país)",
        company: "Empresa",
        companyPlaceholder: "Nome da empresa",
        position: "Cargo",
        positionPlaceholder: "Seu cargo",
        employees: "Número de funcionários",
        selectEmployees: "Selecione",
        preferredDate: "Data preferida",
        selectDate: "Selecione a data",
        preferredTime: "Horário preferido",
        selectTime: "Selecione o horário",
        meetingType: "Tipo de Demonstração",
        selectMeetingType: "Selecione o tipo",
        notes: "Observações (opcional)",
        notesPlaceholder: "Alguma observação adicional?",
        noteLabel: "Nota:",
        noteText:
          "Este é um agendamento prévio. Nossa equipe entrará em contato para confirmar a disponibilidade e enviar o link da reunião (para demonstrações online) ou confirmar o endereço (para demonstrações presenciais).",
        submit: "Confirmar Agendamento",
        submitting: "Agendando...",
        successTitle: "Agendamento recebido!",
        successMessage:
          "Nossa equipe entrará em contato para confirmar a disponibilidade.",
        errorTitle: "Erro ao agendar",
        errorMessage: "Por favor, tente novamente.",
      },
    },
  },
  es: {
    nav: {
      services: "360º",
      plans: "Planes",
      about: "Nosotros",
      contact: "Contáctenos",
      blog: "Blog",
      login: "Ingresar",
      startFree: "Empezar Gratis",
    },
    hero: {
      badge: "Distribuidor Oficial Coface en Latinoamérica",
      title: "Análisis de Crédito Global:",
      titleHighlight: "Compradores, Proveedores y Gestión de Cobranzas",
      description:
        "Monitorea scores y obtén recomendaciones de crédito para compradores y proveedores en más de 190 países. Plataforma integrada pay-per-use con IA. Protege tu capital de trabajo, optimiza cobranzas B2B y vende con seguridad.",
      cta: "Explorar Plataforma",
      ctaSecondary: "Agendar Demo",
    },
    stats: {
      countries: "Países Cubiertos",
      companies: "Empresas Monitoreadas",
      experience: "Años de Experiencia Coface",
      recovery: "Tasa de Recuperación",
    },
    problems: {
      title: "Protégete con Acceso a Datos y Monitoreo Continuo",
      subtitle:
        "Las empresas latinoamericanas pierden millones en exportaciones e importaciones cada año por una razón simple: confían en socios que nunca investigaron.",
      defaultRisk: {
        title: "Riesgo Creciente de Morosidad",
        description:
          "Tu cartera de clientes parece saludable, pero el riesgo de morosidad puede estar oculto. Sin monitoreo activo, solo descubres el problema cuando el pago se retrasa.",
      },
      countryRisk: {
        title: "Bloqueos por Riesgo-País",
        description:
          "Incluso una empresa legítima puede estar en una región bajo sanciones o inestabilidad política. Sin análisis actualizado, tu operación puede detenerse.",
      },
      supplyChain: {
        title: "Ruptura en la Cadena de Suministro",
        description:
          "Un proveedor estratégico entra en dificultades financieras y tu producción se detiene. Sin monitoreo continuo, solo lo descubres cuando el insumo crítico no llega.",
      },
      collection: {
        title: "Dificultad en la Recuperación de Deudas",
        description:
          "Cobrar a un cliente es un proceso agotador que puede destruir la relación comercial. Los enfoques amateur rara vez entienden la complejidad del mercado B2B.",
      },
    },
    solution: {
      title: "La Solución 360º para Gestión de Riesgo Empresarial",
      subtitle:
        "Integramos la base de datos global de Coface y la experiencia de Traderisk en una única plataforma para Analizar, Monitorear y Recuperar.",
      buyer: {
        title: "Buyer Study - Análisis de Compradores",
        subtitle: "Decisiones Basadas en Datos",
        description:
          "Deja de vender a ciegas. Con el Score Coface (DRA), tienes una clasificación de riesgo instantánea de cualquier empresa en más de 190 países.",
      },
      supply: {
        title: "Supply - Gestión de Proveedores",
        subtitle: "Monitorea tu Cadena de Suministro",
        description:
          "El riesgo no está solo en tus clientes. Nuestra plataforma monitorea la salud financiera de tus proveedores 24/7.",
      },
      recovery: {
        title: "Recovery - Gestión de Cobranzas",
        subtitle: "Cobranza Profesional",
        description:
          "¿Ocurrió la morosidad? Activa el servicio de cobranza de Coface directamente desde nuestra plataforma.",
      },
    },
    coface: {
      title: "De la base de datos de Coface a tu negocio:",
      titleHighlight: "riesgo bajo control, en cualquier lugar del mundo.",
      description:
        "Coface es referencia mundial en gestión de riesgo corporativo. Traderisk 360º transforma esa base global en una experiencia accesible e integrada.",
      features: [
        "Dashboard de riesgo para controlar tu cartera",
        "IA que interpreta informes de crédito",
        "Actualizaciones periódicas de riesgo-país",
        "Alertas automáticas y monitoreo continuo",
      ],
      stats: {
        experience: "Años de Experiencia",
        revenue: "Ingresos Anuales",
        countries: "Países",
      },
    },
    plans: {
      title: "Consulta Nuestros Planes",
      subtitle:
        "Compara la inversión necesaria para esta transformación en tu operación",
      basic: {
        name: "Básico",
        description: "Uso ocasional",
        price: "Gratis",
        priceDetail: "Ideal para conocer el sistema",
        features: [
          "Acceso a la plataforma",
          "Consultas individuales (pay-per-use)",
          "CRM Export (límite 20 leads/negocios)",
          "Pago con tarjeta",
          "Soporte estándar",
        ],
        cta: "Empezar Gratis",
      },
      professional: {
        name: "Profesional",
        description: "Uso recurrente",
        price: "USD 39",
        priceDetail: "/mes",
        popular: "Más Popular",
        features: [
          "Todas las funcionalidades Básico",
          "Traderisk 360º Buyer Study completo",
          "Traderisk 360º Supply completo",
          "Traderisk 360º Recovery básico",
          "CRM Export ilimitado",
          "Dashboard de gestión de cartera",
          "Análisis interpretativo con IA",
          "Alertas automáticas",
          "Pago mediante facturación mensual",
          "Soporte prioritario",
        ],
        cta: "Empezar Ahora",
      },
      enterprise: {
        name: "Enterprise",
        description: "Operaciones de gran escala",
        price: "Bajo demanda",
        priceDetail: "Perfecto para grandes equipos",
        features: [
          "Todas las funcionalidades Professional",
          "Traderisk 360º Recovery completo",
          "CRM Export ilimitado",
          "Integración API con ERP/CRM",
          "Solución Whitelabel para socios",
          "Dashboard ejecutivo personalizado",
          "Gerente de cuenta dedicado",
          "SLA garantizado",
        ],
        cta: "Hablar con Especialista",
      },
    },
    cta: {
      title: "Deja de apostar. Empieza a decidir con inteligencia.",
      description:
        "Regístrate gratis en Traderisk 360º y haz tu primer análisis de crédito en minutos.",
      primary: "Empezar Gratis",
      secondary: "Agendar Demo",
    },
    form: {
      title: "Análisis Gratuito de Riesgo País y Sectorial",
      subtitle:
        "Recibe un estudio personalizado sobre el riesgo de un país y sector específico. Entrega en hasta 24 horas, por correo electrónico.",
      name: "Nombre Completo",
      email: "Email Corporativo",
      sector: "Sector de Interés",
      country: "País a Evaluar",
      selectSector: "Seleccione el sector",
      selectCountry: "Seleccione el país",
      submit: "Solicitar Estudio Gratis",
      success: "¡Solicitud enviada con éxito!",
    },
    footer: {
      description:
        "Transformando datos globales de crédito en decisiones inteligentes para empresas latinoamericanas.",
      services: "360º",
      company: "Empresa",
      contact: "Contáctenos",
      legal: "Legal",
      privacy: "Política de Privacidad",
      terms: "Términos de Uso",
      cookies: "Política de Cookies",
      rights: "Todos los derechos reservados.",
      address: "Dirección",
    },
    popup: {
      title: "Análisis Gratuito de Riesgo País y Sectorial",
      subtitle:
        "Recibe un estudio personalizado sobre el riesgo de un país y sector específico",
      name: "Nombre Completo",
      email: "Email Corporativo",
      sector: "Sector de Interés",
      country: "País a Evaluar",
      submit: "Solicitar Estudio Gratis",
      later: "Quizás después",
    },
    demo: {
      title: "Agendar una Demo",
      subtitle:
        "Elige la mejor fecha y hora para conocer la plataforma Traderisk 360º.",
      meta: {
        title: "Agendar Demo - TradeRisk 360º",
        description:
          "Agenda una demostración personalizada de la plataforma TradeRisk 360º.",
      },
      features: {
        personalized: {
          title: "Demostración Personalizada",
          description:
            "Presentación enfocada en las necesidades específicas de tu empresa",
        },
        consulting: {
          title: "Consultoría Especializada",
          description:
            "Resuelve tus dudas con especialistas en gestión de riesgo",
        },
        flexibility: {
          title: "Flexibilidad de Horario",
          description: "Elige el mejor horario para ti y tu equipo",
        },
      },
      form: {
        title: "Agendar una Demo",
        subtitle:
          "Elige la mejor fecha y hora para conocer la plataforma Traderisk 360º.",
        name: "Nombre Completo",
        namePlaceholder: "Tu nombre completo",
        email: "Email",
        emailPlaceholder: "tu@email.com",
        country: "País",
        selectCountry: "Selecciona el país",
        phone: "Teléfono (con código de país)",
        company: "Empresa",
        companyPlaceholder: "Nombre de la empresa",
        position: "Cargo",
        positionPlaceholder: "Tu cargo",
        employees: "Número de empleados",
        selectEmployees: "Selecciona",
        preferredDate: "Fecha preferida",
        selectDate: "Selecciona la fecha",
        preferredTime: "Hora preferida",
        selectTime: "Selecciona la hora",
        meetingType: "Tipo de Demostración",
        selectMeetingType: "Selecciona el tipo",
        notes: "Notas (opcional)",
        notesPlaceholder: "¿Alguna observación adicional?",
        noteLabel: "Nota:",
        noteText:
          "Esta es una reserva previa. Nuestro equipo se pondrá en contacto para confirmar la disponibilidad y enviar el enlace de la reunión (para demostraciones en línea) o confirmar la dirección (para demostraciones presenciales).",
        submit: "Confirmar Cita",
        submitting: "Agendando...",
        successTitle: "¡Reserva recibida!",
        successMessage:
          "Nuestro equipo se pondrá en contacto para confirmar la disponibilidad.",
        errorTitle: "Error al agendar",
        errorMessage: "Por favor, inténtalo de nuevo.",
      },
    },
  },
  en: {
    nav: {
      services: "360º",
      plans: "Plans",
      about: "About",
      contact: "Contact Us",
      blog: "Blog",
      login: "Login",
      startFree: "Start Free",
    },
    hero: {
      badge: "Official Coface Distributor",
      title: "Global Credit Analysis:",
      titleHighlight: "Buyers, Suppliers and Collection Management",
      description:
        "Monitor scores and get credit recommendations for buyers and suppliers in over 190 countries. Integrated pay-per-use platform with AI. Protect your working capital, optimize B2B collections and sell safely worldwide.",
      cta: "Explore Platform",
      ctaSecondary: "Request Demo",
    },
    stats: {
      countries: "Countries Covered",
      companies: "Companies Monitored",
      experience: "Years of Coface Experience",
      recovery: "Recovery Rate",
    },
    problems: {
      title: "Protect Yourself with Data Access and Continuous Monitoring",
      subtitle:
        "Companies lose millions in exports and imports every year for one simple reason: they trust partners they never investigated.",
      defaultRisk: {
        title: "Growing Default Risk",
        description:
          "Your customer portfolio looks healthy, but default risk may be hidden. Without active monitoring, you only discover the problem when payment is delayed.",
      },
      countryRisk: {
        title: "Country Risk Blocks",
        description:
          "Even a legitimate company may be in a region under sanctions or political instability. Without updated analysis, your operation may stop mid-transaction.",
      },
      supplyChain: {
        title: "Supply Chain Disruption",
        description:
          "A strategic supplier gets into financial difficulties and your production stops. Without continuous monitoring, you only discover when the critical input does not arrive.",
      },
      collection: {
        title: "Difficulty Recovering Debts",
        description:
          "Collecting from a customer is an exhausting process that can destroy the business relationship. Amateur approaches rarely understand the complexity of the B2B market.",
      },
    },
    solution: {
      title: "The 360º Solution for Enterprise Risk Management",
      subtitle:
        "We integrate Coface's global database and Traderisk's expertise into a single platform to Analyze, Monitor and Recover.",
      buyer: {
        title: "Buyer Study - Buyer Analysis",
        subtitle: "Data-Driven Decisions",
        description:
          "Stop selling blindly. With Coface's Score (DRA), you have an instant risk rating of any company in over 190 countries.",
      },
      supply: {
        title: "Supply - Supplier Management",
        subtitle: "Monitor Your Supply Chain",
        description:
          "The risk is not only in your customers. Our platform monitors your suppliers' financial health 24/7.",
      },
      recovery: {
        title: "Recovery - Collection Management",
        subtitle: "Professional Collection",
        description:
          "Did default occur? Activate Coface's collection service directly through our platform.",
      },
    },
    coface: {
      title: "From Coface's database to your business:",
      titleHighlight: "risk under control, anywhere in the world.",
      description:
        "Coface is the world reference in corporate risk management. Traderisk 360º transforms this global base into an accessible and integrated experience.",
      features: [
        "Risk dashboard to control your portfolio",
        "AI that interprets credit reports",
        "Periodic country risk updates",
        "Automatic alerts and continuous monitoring",
      ],
      stats: {
        experience: "Years of Experience",
        revenue: "Annual Revenue",
        countries: "Countries",
      },
    },
    plans: {
      title: "Check Our Plans",
      subtitle:
        "Compare the investment needed for this transformation in your operation",
      basic: {
        name: "Basic",
        description: "Occasional use",
        price: "Free",
        priceDetail: "Ideal to know the system",
        features: [
          "Platform access",
          "One-time queries (pay-per-use)",
          "CRM Export (limit 20 leads/deals)",
          "Card payment",
          "Standard support",
        ],
        cta: "Start Free",
      },
      professional: {
        name: "Professional",
        description: "Recurring use",
        price: "USD 39",
        priceDetail: "/month",
        popular: "Most Popular",
        features: [
          "All Basic features",
          "Traderisk 360º Buyer Study complete",
          "Traderisk 360º Supply complete",
          "Traderisk 360º Recovery basic",
          "Unlimited CRM Export",
          "Portfolio Management Dashboard",
          "AI interpretive analysis",
          "Automatic alerts",
          "Monthly billing payment",
          "Priority support",
        ],
        cta: "Start Now",
      },
      enterprise: {
        name: "Enterprise",
        description: "Large-scale operations",
        price: "On demand",
        priceDetail: "Perfect for large teams",
        features: [
          "All Professional features",
          "Traderisk 360º Recovery complete",
          "Unlimited CRM Export",
          "API integration with ERP/CRM",
          "Whitelabel solution for partners",
          "Personalized executive dashboard",
          "Dedicated account manager",
          "Guaranteed SLA",
        ],
        cta: "Talk to Specialist",
      },
    },
    cta: {
      title: "Stop gambling. Start deciding with intelligence.",
      description:
        "Register for free at Traderisk 360º and do your first credit analysis in minutes.",
      primary: "Start Free",
      secondary: "Schedule Demo",
    },
    form: {
      title: "Free Country and Sector Risk Analysis",
      subtitle:
        "Receive a personalized study on the risk of a specific country and sector. Delivery within 24 hours, by email.",
      name: "Full Name",
      email: "Corporate Email",
      sector: "Sector of Interest",
      country: "Country to Evaluate",
      selectSector: "Select sector",
      selectCountry: "Select country",
      submit: "Request Free Study",
      success: "Request sent successfully!",
    },
    footer: {
      description:
        "Transforming global credit data into intelligent decisions for companies worldwide.",
      services: "360º",
      company: "Company",
      contact: "Contact Us",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      cookies: "Cookie Policy",
      rights: "All rights reserved.",
      address: "Address",
    },
    popup: {
      title: "Free Country and Sector Risk Analysis",
      subtitle:
        "Receive a personalized study on the risk of a specific country and sector",
      name: "Full Name",
      email: "Corporate Email",
      sector: "Sector of Interest",
      country: "Country to Evaluate",
      submit: "Request Free Study",
      later: "Maybe later",
    },
    demo: {
      title: "Schedule a Demo",
      subtitle:
        "Choose the best date and time to learn about the Traderisk 360º platform.",
      meta: {
        title: "Schedule Demo - TradeRisk 360º",
        description:
          "Schedule a personalized demonstration of the TradeRisk 360º platform.",
      },
      features: {
        personalized: {
          title: "Personalized Demonstration",
          description: "Presentation focused on your company's specific needs",
        },
        consulting: {
          title: "Expert Consulting",
          description:
            "Get your questions answered by risk management specialists",
        },
        flexibility: {
          title: "Schedule Flexibility",
          description: "Choose the best time for you and your team",
        },
      },
      form: {
        title: "Schedule a Demo",
        subtitle:
          "Choose the best date and time to learn about the Traderisk 360º platform.",
        name: "Full Name",
        namePlaceholder: "Your full name",
        email: "Email",
        emailPlaceholder: "your@email.com",
        country: "Country",
        selectCountry: "Select country",
        phone: "Phone (with country code)",
        company: "Company",
        companyPlaceholder: "Company name",
        position: "Position",
        positionPlaceholder: "Your position",
        employees: "Number of employees",
        selectEmployees: "Select",
        preferredDate: "Preferred date",
        selectDate: "Select a date",
        preferredTime: "Preferred time",
        selectTime: "Select a time",
        meetingType: "Meeting Type",
        selectMeetingType: "Select type",
        notes: "Notes (optional)",
        notesPlaceholder: "Any additional observations?",
        noteLabel: "Note:",
        noteText:
          "This is a preliminary booking. Our team will contact you to confirm availability and send the meeting link (for online demos) or confirm the address (for in-person demos).",
        submit: "Confirm Appointment",
        submitting: "Scheduling...",
        successTitle: "Booking received!",
        successMessage: "Our team will contact you to confirm availability.",
        errorTitle: "Scheduling error",
        errorMessage: "Please try again.",
      },
    },
    // APP TRANSLATIONS
    app: {
      nav: {
        dashboard: "Dashboard",
        executive: "Executive",
        crmExport: "CRM Export",
        companies: "Companies",
        buyers: "Buyers",
        suppliers: "Suppliers",
        portfolioUpdates: "Portfolio Updates",
        sectorRisk: "Sector Risk",
        riskMap: "Risk Map",
        comparison: "Comparison",
        simulador360: "Simulator 360",
        simuladorChoque: "Shock Simulator",
        visaoBrasil: "Brazil View",
        setoresVariaveis: "Sectors × Variables",
        matrizDependencia: "Dependency Matrix",
        reports: "Reports",
        collections: "Collections",
        billing: "Billing",
        profile: "My Profile",
        settings: "Settings",
        help: "Help",
        support: "Support",
        logout: "Logout",
      },
      dashboard: {
        title: "Dashboard",
        subtitle: "Overview of your commercial risk portfolio",
        noCompanies: "No companies registered",
        addCompany: "Add Company",
        monitoredCompanies: "Monitored Companies",
        averageScore: "Average Score",
        activeAlerts: "Active Alerts",
        totalExposure: "Total Exposure",
        bestScore: "Best Score",
        highestLimit: "Highest Limit",
        highExposure: "High Exposure",
        recentlyUpdated: "Recently Updated",
        recentCompanies: "Recent Companies",
        viewAll: "View all",
        viewDetails: "View details",
        sectorRisk: "Sector Risk",
        buyer: "Buyer",
        supplier: "Supplier",
        countries: "countries",
        last7days: "last 7 days",
        ofLimit: "of limit",
        open: "open",
      },
      companies: {
        title: "Companies",
        subtitle: "Manage your monitored companies (buyers and suppliers)",
        addCompany: "Add Company",
        total: "Total",
        buyers: "Buyers",
        suppliers: "Suppliers",
        buyer: "Buyer",
        supplier: "Supplier",
        exposure: "Exposure",
        limit: "Recommendation",
        rating: "Rating",
        trend: "Trend",
        updated: "Updated",
        country: "Country",
        searchPlaceholder: "Search by name or national ID...",
        allTypes: "All types",
        allCountries: "All countries",
        actions: "Actions",
        viewDetails: "View details",
        generateReport: "Generate URBA Report",
        aiAnalysis: "AI Analysis",
        remove: "Remove",
        noCompaniesFound: "No companies found",
        adjustFilters: "Try adjusting the filters or add a new company.",
        showing: "Showing",
        of: "of",
        companies: "companies",
        page: "Page",
        previous: "Previous",
        next: "Next",
        list: {
          import: "Import companies",
          export: "Export",
          columns: "Columns",
          limitUsage: "Limit usage",
          rowsPerPage: "Rows per page",
          totalExposure: "Total exposure",
          totalRecommendation: "Total recommendation",
          sheetName: "Companies",
          all: "All",
          recent: "Recent changes",
          highExposure: "High exposure",
          unlinked: "Unlinked",
          search: "Search by name or national ID",
          sector: "Sector",
          status: "Status",
          type: "Type",
          noScore: "No score",
          monitoring: "Monitoring",
          inProgress: "In progress",
          notVerified: "Not verified",
          unclassified: "Unclassified",
          exporting: "Exporting…",
          selectedCompanies: "Select companies on this page",
          company: "Company",
          waitingCoface: "Waiting for Coface",
          noRecommendation: "No recommendation",
          noExposure: "No exposure reported",
          recentChanges: "Recent changes",
          notLinkedCoface: "Not linked to Coface",
          removeBlocked: "Company has linked reports",
          rows: "Showing {from}–{to} of {total} companies",
          noRows: "No companies found. Try adjusting the filters.",
          emptyTitle: "No companies in your portfolio yet",
          emptyHint:
            "Add companies to track their risk, ratings and credit limits.",
          noResultsLimit:
            "The list may be incomplete due to the results limit.",
          loadError: "Could not load companies.",
          deleted: "Company deleted",
          deletedDescription: "The company was removed from monitoring.",
          deleteError: "Error deleting",
          deleteErrorDescription: "Could not remove the company.",
          exportUnavailable: "Export unavailable",
          exportLimit: "Refine the filters to export at most 5,000 companies.",
          exportError: "Could not export",
          exportTryAgain: "Please try again later.",
          manualTitle: "Manual registrations",
          manualPending: "{count} {company} awaiting TradeRisk team validation",
          hide: "Hide",
          viewRegistrations: "View registrations →",
          filterStatus: "Filter status:",
          allStatuses: "All",
          requestedAt: "Requested on {date}",
          note: "Note: {reason}",
          noRegistration: "No registration with this status.",
          statusPending: "Under review",
          statusSent: "Registering in database",
          statusExists: "Already in database",
          statusFinished: "Completed",
        },
      },
      buyers: {
        title: "Buyers",
        subtitle: "Manage your monitored buyers",
        addBuyer: "Add Buyer",
        totalBuyers: "Total Buyers",
        noBuyersFound: "No buyers found",
        adjustFilters: "Try adjusting the filters or add a new buyer.",
        diversification: "Diversification",
      },
      suppliers: {
        title: "Suppliers",
        subtitle: "Manage your monitored suppliers",
        addSupplier: "Add Supplier",
        totalSuppliers: "Total Suppliers",
        noSuppliersFound: "No suppliers found",
        adjustFilters: "Try adjusting the filters or add a new supplier.",
        diversification: "Diversification",
      },
      addCompany: {
        title: "Add Company",
        subtitle: "Register a new company for monitoring",
        step1: { title: "Identification", description: "Search in Coface" },
        step2: {
          title: "Type and Sector",
          description: "Classification and sector risk",
        },
        step3: { title: "Context", description: "Entry questionnaire" },
        step4: { title: "Confirmation", description: "Review and register" },
        country: "Country",
        selectCountry: "Select country",
        searchMethod: "Search Method",
        byFiscalCode: "By Fiscal Code",
        byName: "By Company Name",
        search: "Search in Coface",
        noResults: "No results",
        registerManually: "Register manually",
        requiredFields: "Required fields",
        selectCountryMessage: "Select the country.",
        selectCompanyMessage:
          "Select a company from the list or register manually.",
        companyName: "Company Name",
        fiscalCode: "Fiscal Code",
        enterFiscalCode: "Enter fiscal code",
        enterCompanyName: "Enter company name",
        searching: "Searching...",
        searchResults: "Search Results",
        selectedCompany: "Selected Company",
        manualEntry: "Manual Entry",
        companyType: "Company Type",
        sectorRisk: "Sector Risk",
        selectSector: "Select sector",
        riskLevel: "Risk Level",
        outlook: "Outlook",
        companySaved: "Company registered",
        companyAddedSuccess: "was successfully added.",
        errorRegistering: "Error registering",
        back: "Back",
        next: "Next",
        save: "Save",
        saving: "Saving...",
      },
      riskMap: {
        title: "Global Risk Map",
        subtitle: "Country risk assessment based on Coface ratings",
        lowRisk: "Low Risk (A1-A2)",
        mediumRisk: "Medium Risk (A3-B)",
        highRisk: "High Risk (C-D)",
        extremeRisk: "Extreme Risk (E)",
        countryRisk: "Country Risk",
        businessClimate: "Business Climate",
        region: "Region",
        continent: "Continent",
        companiesIn: "Companies in",
        noCompaniesInCountry: "No companies monitored in this country",
        clickForDetails: "Click on a country on the map to see details",
        allCountries: "All Countries",
        moreCompanies: "more companies",
        searchPlaceholder: "Search country by name or region...",
        noResults: "No countries found",
        loadingDetails: "Loading Coface details...",
        orSearchAbove: "or use the search above",
        details: {
          strengths: "Strengths",
          weaknesses: "Weaknesses",
          sectorRisks: "Sector Risks",
          economicIndicators: "Economic Indicators",
          source: "Source",
          updatedAt: "Consulted on",
          notAvailable:
            "Detailed data not available for this country in the Coface database.",
          // Chaves = rotulos brutos como vem no scraping da Coface (idioma
          // original, ingles); usados para traduzir a coluna de rotulos da
          // tabela de projecoes economicas sem mexer no dado raspado.
          economicLabels: {
            "GDP growth(%)": "GDP growth (%)",
            "Inflation(yearly average,%)": "Inflation (yearly average, %)",
            "Budget balance(% GDP)": "Budget balance (% GDP)",
            "Current account balance(% GDP)": "Current account balance (% GDP)",
            "Public debt / GDP(% GDP)": "Public debt / GDP (% GDP)",
          },
        },
      },
      companyDetail: {
        exposureRisk: {
          sections: "Exposure and risk tab sections",
          anchors: {
            exposure: "Exposure",
            countrySector: "Country and sector risk",
            map: "Map",
            kyc: "KYC",
          },
          sources: "Sources: Coface URBA · exposure provided by your team",
          exposure: {
            title: "Current exposure",
            question:
              "How much your company has to receive from this customer today",
            approx: "≈ {value}",
            usage: "Coface recommendation usage",
            usageOf: "{usage}% of {limit}",
            usageAria: "Recommendation usage: {usage}%",
            mark0: "0",
            markAttention: "70% attention",
            markLimit: "100% = recommended limit",
            available: "Available in recommendation",
            exceeds: "Exceeds recommendation",
            recommendation: "Coface recommendation",
            status: "Status",
            statusWithin: "Within limit",
            statusNear: "Near limit",
            statusAbove: "Above limit",
            statusNone: "Not provided",
            statusUnavailable: "No recommendation",
            noneText:
              "Provide the current exposure to track recommendation usage.",
          },
          layers: {
            title: "Where the risk comes from",
            description:
              "Credit risk adds up three layers: the company, the sector it operates in and the country where it is located.",
            company: "Company",
            sector: "Sector",
            country: "Country",
            companyDetail: "Coface score {score}/10 · Rating {rating}",
            companyDetailNoRating: "Coface score {score}/10",
            noData: "No data",
            sectorLevel: "{name} ({abbr})",
            countryLevel: "{level} · {label}",
            tones: {
              low: "low",
              moderate: "moderate",
              elevated: "elevated",
              high: "high",
            },
            summary: {
              company:
                "Risk comes mainly from the company itself ({ref}). Country and sector are favorable.",
              companyWithExternal:
                "Risk comes mainly from the company itself ({ref}). Country and sector also require attention.",
              external:
                "The company's risk is {companyTone}; the main external factor is the {factor}.",
              factorCountry: "country ({level})",
              factorSector: "sector ({abbr})",
              tie: "Company, sector and country have similar {tone} risk.",
              refRating: "rating {rating}",
              refScore: "score {score}/10",
            },
          },
          period: "Coface · {year} Q{quarter}",
          trend: {
            stableFor: "Stable for {n} quarters",
            stable: "Stable",
            improvedFull: "Improved in {year} Q{quarter} ({from} → {to})",
            worsenedFull: "Worsened in {year} Q{quarter} ({from} → {to})",
          },
          country: {
            title: "Country risk · {country}",
            businessClimate: "Business climate {level}",
            scaleMin: "A1 · lowest risk",
            scaleMax: "E · highest risk",
            note: "Assesses the average default of companies in the country, considering economy, politics and business climate.",
            scaleAria: "Country risk scale, current level {level}",
            shortLabels: {
              A1: "Very low risk",
              A2: "Low risk",
              A3: "Satisfactory risk",
              A4: "Reasonable risk",
              B: "Moderate risk",
              C: "High risk",
              D: "Very high risk",
              E: "Extreme risk",
            },
            descriptions: {
              A1: "Very stable economy and solid institutions; the probability of corporate default is very low.",
              A2: "Stable political and economic environment; corporate default is low and payments are usually on time.",
              A3: "Stable economy and solid institutions; corporate payment behavior is generally good.",
              A4: "Generally favorable environment, but with some economic or political weakness that may affect payments.",
              B: "Acceptable business environment, with economic and political instability that may affect corporate payment behavior.",
              C: "Very uncertain economic and political environment with significant weaknesses; payment delays are frequent.",
              D: "High-risk economic and political environment with weak institutions; corporate default is very high.",
              E: "Extreme-risk environment with a severe economic or political crisis; corporate payments are highly unpredictable.",
            },
          },
          sector: {
            title: "Sector risk · {sector}",
            nace: "NACE {code} · {label}",
            levels: { L: "Low", M: "Medium", H: "High", VH: "Very high" },
            scaleLabels: {
              L: "L · Low",
              M: "M · Medium",
              H: "H · High",
              VH: "VH · Very high",
            },
            scaleMin: "Low",
            scaleMax: "Very high",
            note: "Assesses the default risk of companies in the sector in the region, based on Coface's payment experience.",
            scaleAria: "Sector risk scale, current level {level}",
            notIdentified: "Sector not identified",
            descriptions: {
              L: "Sector with stable demand and good financial health; corporate default is low.",
              M: "Sector with moderate risk; margins sensitive to costs, interest rates and exchange rates require monitoring.",
              H: "Sector under financial pressure; payment delays are more frequent than average.",
              VH: "Sector in difficulty, with widespread financial weakness and high default.",
            },
          },
        },
        creditAnalysis: {
          anchors: {
            score: "Score and probability",
            recommendation: "Credit recommendation",
            market: "Market scores",
            payment: "Payment index",
          },
          source: "Source: Coface URBA · queried {date}",
          sourceNoDate: "Source: Coface URBA",
          queryDate: "Query {date}",
          sections: "Credit analysis sections",
          quodUnavailable: "Quod Score data unavailable.",
          scaleHint: "↑ Higher = Lower risk",
          dra: {
            title: "Coface Score (DRA)",
            description:
              "A score from 0 to 10 assigned by Coface. The higher the score, the lower the chance that the company will not pay. Score {score} = {probability} probability of default within 12 months.",
            scaleTitle: "DRA RISK SCALE",
            link: "Understand each score from 0 to 10 →",
            risk: [
              "Insolvency proceedings or debt restructuring",
              "Extremely high risk of insolvency",
              "Very high risk of insolvency",
              "High risk",
              "Significant risk",
              "Medium-high risk",
              "Acceptable risk",
              "Moderate risk",
              "Low risk",
              "Very low risk",
              "Excellent risk",
            ],
            scaleLabels: [
              "Insolvency",
              "Extreme",
              "High",
              "Significant",
              "Moderate",
              "Low",
              "Excellent",
            ],
          },
          table: {
            title: "Understand each Coface score",
            description:
              "Official Coface classification and the probability of default within 12 months for each score.",
            colScore: "Score",
            colClass: "Coface classification",
            colProb: "12-month prob.",
            thisCompany: "This company",
          },
          quod: {
            title: "Quod Score",
            description:
              "A score from 300 to 1000 calculated by Quod using Positive Registry data: on-time payments, delays, indebtedness, and credit searches. The higher the score, the lower the risk.",
            scaleTitle: "QUOD RISK SCALE",
            link: "How does the Quod Score work? →",
            risk: [
              "Very high risk",
              "High risk",
              "Medium risk",
              "Low risk",
              "Very low risk",
            ],
            scaleLabels: ["Very high", "High", "Medium", "Low", "Very low"],
          },
          quodIndicators: {
            title: "Quod Score · indicators",
            description:
              "{score}/1000 · {label}. The {count} Positive Registry indicators that make up the score (0 to 100 each).",
            unavailable: "Quod Score data unavailable.",
            items: {
              onTimePayments: {
                name: "On-time payments",
                subtitle: "Commitments paid on time · 12 months",
              },
              currentDelinquency: {
                name: "Current delinquency",
                subtitle: "Time overdue for outstanding debts",
              },
              debtDelinquency: {
                name: "Debt – delinquency",
                subtitle: "Current overdue contracts",
              },
              emergencyCredit: {
                name: "Emergency credit",
                subtitle: "Revolving credit use · 180 days",
              },
              contractingProfile: {
                name: "Contracting profile",
                subtitle: "Type of credit product contracted",
              },
              creditSearch: {
                name: "Credit searches",
                subtitle: "Frequency of new contracts",
              },
              relationship: {
                name: "Relationship",
                subtitle: "Stability with creditors · 180 days",
              },
            },
            risk: {
              low: "LOW RISK",
              medium: "MEDIUM RISK",
              high: "HIGH RISK",
              none: "NO DATA",
            },
          },
          gaugeLabel: "Score {score} out of {max} · {label}",
          gaugeUnavailable: "Score unavailable",
          recommendation: {
            title: "Credit recommendation",
            rating: "Rating {rating}",
            limitLabel: "Credit limit recommended by Coface",
            usage: "Limit usage",
            exposure: "Current exposure {exposure} · available {available}",
            exposureUnknown: "Exposure not provided",
            exceeded: "Current exposure {exposure} · exceeds by {amount}",
            equivalent: "Equivalent in {currency}",
            rate: "rate {rate} · {date}",
            rateNoDate: "rate {rate}",
            compact: { thousand: "K", million: "M" },
            decision: {
              favorable: {
                title: "Coface decision: favorable",
                text: "Rating {rating}: Coface recommends granting credit.",
              },
              favorableNoRating: {
                title: "Coface decision: favorable",
                text: "Limit recommended by Coface for this company.",
              },
              conditional: {
                title: "Coface decision: conditional",
                text: "Rating {rating}: grant with conditions (guarantees, shorter term or partial limit).",
              },
              unfavorable: {
                title: "Coface decision: unfavorable",
                text: "Coface does not recommend granting credit.",
              },
            },
          },
          payment: {
            title: "Payment index",
            window: "Coface · last 60 months",
            originalDescription: "Original Coface description",
            translations: {},
            options: {
              none: {
                title: "No negative experiences",
                text: "No relevant delay reported",
              },
              some: {
                title: "Some negative experiences",
                text: "Occasional delays reported",
              },
              considerable: {
                title: "Considerable negative experiences",
                text: "Recurring or relevant delays",
              },
            },
          },
          history: {
            credit: "Recommendation history",
            score: "DRA score evolution",
            creditSubtitle: "Coface · last 12 months",
            scoreSubtitle: "Coface · last 6 months",
            empty: "No history yet",
            point: "pt",
            points: "pts",
            expandCredit: "View credit recommendation",
            expandPayment: "View payment index",
            expandScore: "View score evolution",
          },
          detail: {
            recommendation: {
              title: "Coface credit recommendation",
              issued: "Issued on {date}",
              limitLabel: "Maximum limit recommended for trade credit",
              issuedValue: "Value issued by Coface in {currencyName} ({code})",
              issuedValueCode:
                "Value issued by Coface in {code} · maximum simultaneous exposure",
              pd: "PD {range}",
              currencyNames: {
                BRL: "Brazilian reais",
                USD: "US dollars",
                EUR: "euros",
              },
              originalValue: "Original Coface value: {original}",
              converted:
                "Original Coface value: {original} · converted at the rate of {date} ({rate})",
              simultaneous: "maximum simultaneous exposure",
              equivalentToday: "Equivalent today",
              evolution: "12-month evolution",
              currentUsage: "Current usage",
              available: "Available",
              unknown: "Not provided",
              exceeds: "Exceeds by {amount}",
              decisionSuggested: "Suggested decision",
              grant: "Grant credit",
              conditional: "Conditional credit",
              deny: "Do not grant credit",
              rating: "Coface rating",
              notInformed: "Not provided",
              opinion: "Original Coface opinion",
              scaleTitle: "Coface rating scale",
              lowerRisk: "↑ A1 = lower risk",
              scaleDescription:
                "The rating measures the company's probability of default (PD) over 12 months. It defines the suggested credit decision for the recommended limit.",
              decisionBands: {
                grant: "Grant credit · A1–A4",
                conditional: "Conditional credit · B–C",
                deny: "Do not grant · D, E, X",
              },
              note: "Conditional: grant with guarantees, shorter term or partial limit. X: Coface does not recommend granting credit (limit {zero}).",
              howToRead: "How to read this recommendation",
              methodology: "Understand Coface methodology",
              ratingLevels: {
                A1: "Minimum risk",
                A2: "Very low risk",
                A3: "Low risk",
                A4: "Low-medium risk",
                B: "Medium risk",
                C: "Medium-high risk",
                D: "High risk",
                E: "Very high risk",
                X: "No limit",
              },
              items: {
                limit: {
                  title: "Recommended limit",
                  description:
                    "Maximum amount Coface considers prudent to keep open with this company at the same time (invoices and orders combined).",
                },
                rating: "Rating {rating}",
                ratingRange:
                  "{label}: between {min} and {max} chance of default over the next 12 months.",
                ratingAbove:
                  "{label}: above {min} chance of default over the next 12 months.",
                ratingX:
                  "No limit: Coface does not recommend granting credit to this company.",
                decision: {
                  grant: {
                    title: "Decision: grant",
                    description:
                      "A1 to A4 indicate granting up to the limit. B–C require conditions; D, E or X indicate not granting.",
                  },
                  conditional: {
                    title: "Decision: conditional",
                    description:
                      "B and C require conditions: guarantees, shorter term or partial limit. A1–A4 indicate granting; D, E or X, not granting.",
                  },
                  deny: {
                    title: "Decision: do not grant",
                    description:
                      "D, E or X indicate not granting credit. A1–A4 indicate granting; B–C require conditions.",
                  },
                },
                currency: {
                  title: "Currency and exchange",
                  same: "Value issued in {currencyName}. Equivalents in {targetA} and {targetB} use the daily rate and vary with exchange rates.",
                  different:
                    "Coface issues in {sourceName} ({original}). We convert to {displayName} at the daily rate; the amount in {displaySymbol} varies with exchange rates.",
                },
              },
            },
          },
        },
        riskScore: "Risk Score",
        trend: "Trend",
        exposure: "Exposure",
        creditLimit: "Credit Recommendation",
        summary: "Summary",
        cofaceData: "Coface Data",
        contextualData: "Contextual Data",
        history: "History",
        generalInfo: "General Information",
        nationalId: "National ID",
        country: "Country",
        type: "Type",
        buyer: "Buyer",
        supplier: "Supplier",
        exportPdf: "Export PDF URBA360",
        textReport: "Text Report",
        buyReportPdf: "Buy PDF Report",
        aiAnalysis: "AI Analysis",
        updateData: "Update URBA 360 data",
        companyNotFound: "Company not found",
        backToList: "Back to list",
        lastUpdate: "Last update",
        notLinkedToCoface:
          "This company is not linked to Coface. Link it to get detailed data.",
        noDataAvailable: "No data available",
      },
      dialogs: {
        delete: {
          title: "Delete Company",
          description: "This action is irreversible",
          aboutToDelete: "You are about to delete",
          typeBuyer: "Buyer",
          typeSupplier: "Supplier",
          willRemove: "This action will remove",
          allData: "All company data",
          ratingHistory: "Rating history",
          contextualData: "Contextual data",
          typeToConfirm: "Type DELETE to confirm",
          confirmWord: "DELETE",
          cancel: "Cancel",
          deleting: "Deleting...",
          deleteCompany: "Delete Company",
        },
        purchase: {
          title: "Purchase Report",
          description: "Confirm purchase of PDF report",
          price: "Price",
          confirm: "Confirm Purchase",
          purchasing: "Purchasing...",
        },
        sync: {
          title: "Confirm Update",
          description: "You are about to update company data",
          warning: "Consumption warning",
          willConsume: "This operation will consume",
          fromCredits: "of credits from your account.",
          continueUpdate: "Do you want to continue with the update?",
          cancel: "Cancel",
          confirmUpdate: "Confirm Update",
        },
      },
      messages: {
        companySaved: "Company registered",
        companyDeleted: "Company deleted",
        companyRemovedFromMonitoring:
          "The company was removed from monitoring.",
        dataSaved: "Data saved",
        dataUpdated: "Data updated",
        syncSuccess: "Data synced successfully",
        syncError: "Sync error",
        apiKeyNotConfigured: "API Key not configured",
        error: "Error",
        success: "Success",
        errorDeleting: "Error deleting",
      },
      sectorRisk: {
        title: "Sector Risk",
        subtitle: "Risk analysis by sector",
        low: "Low",
        medium: "Medium",
        mediumHigh: "Medium-High",
        high: "High",
        outlookPositive: "Positive",
        outlookNegative: "Negative",
        outlookStable: "Stable",
        sectors: "Sectors",
        history: "History",
        totalSectors: "Total Sectors",
        monitoredCompanies: "Monitored Companies",
        criticalSectors: "Critical Sectors",
      },
      common: {
        loading: "Loading...",
        save: "Save",
        cancel: "Cancel",
        confirm: "Confirm",
        next: "Next",
        back: "Back",
        search: "Search",
        searchByName: "Search by name...",
        filter: "Filter",
        export: "Export",
        download: "Download",
        page: "Page",
        of: "of",
        showing: "Showing",
        results: "results",
        previous: "Previous",
        diversification: "Diversification",
        totalExposure: "Total Exposure",
        countries: "Countries",
        allCountries: "All countries",
      },
      profile: {
        title: "My Profile",
        subtitle: "Manage your personal information",
        personalInfo: "Personal Information",
        updateContactInfo: "Update your contact and identification details",
        fullName: "Full Name",
        phone: "Phone",
        position: "Position / Role",
        email: "Email",
        emailCannotChange: "Email cannot be changed",
        saveChanges: "Save Changes",
        saving: "Saving...",
        accountInfo: "Account Information",
        userId: "User ID",
        tenantRole: "Tenant Role",
        admin: "Administrator",
        user: "User",
        status: "Status",
        active: "Active",
        inactive: "Inactive",
        memberSince: "Member since",
        profileUpdated: "Profile updated",
        infoSaved: "Your information was saved successfully.",
        errorUpdating: "Error updating",
      },
      settings: {
        title: "Settings",
        subtitle: "Manage your organization and team",
        organization: "Organization",
        orgInfo: "Your company information",
        name: "Name",
        cnpj: "Tax ID",
        type: "Type",
        directClient: "Direct Client",
        partner: "Partner",
        status: "Status",
        active: "Active",
        trial: "Trial Period",
        pending: "Pending",
        team: "Team",
        manageTeam: "Manage your organization members",
        inviteUser: "Invite User",
        inviteNew: "Invite New User",
        inviteDescription: "Send an email invitation to add a member",
        email: "Email",
        sendInvite: "Send Invite",
        cancel: "Cancel",
        noName: "No name",
        noPosition: "No position",
        removeAdmin: "Remove Admin",
        makeAdmin: "Make Admin",
        noUsersFound: "No users found",
        accessLevels: "Access Levels",
        accessDescription: "Understand each role's permissions",
        viewAllCompanies: "View all companies",
        addEditCompanies: "Add and edit companies",
        requestReports: "Request reports and analyses",
        viewBilling: "View billing and consumption",
        manageUsers: "Manage team users",
        inviteSent: "Invite sent",
        inviteSentTo: "An invitation was sent to",
        statusUpdated: "Status updated",
        roleUpdated: "Role updated",
        integrations: "Integrations",
      },
      billing: {
        title: "Billing",
        subtitle: "Track your consumption and invoices",
        currentPlan: "Current Plan",
        monthlyConsumption: "Monthly Consumption",
        totalConsumption: "Total Consumption",
        exchangeRate: "Exchange Rate USD",
        sinceBeginning: "since the beginning",
        consultations: "queries",
        trialPeriod: "Trial Period",
        trialEndsIn: "Your trial period ends in",
        days: "days",
        day: "day",
        hirePlan: "Subscribe Plan",
        postPaid: "Post-paid",
        prePaid: "Pre-paid",
        consumptionHistory: "Consumption History",
        allConsultations: "All queries performed",
        date: "Date",
        service: "Service",
        company: "Company",
        valueUsd: "Value (USD)",
        valueBrl: "Value (Local)",
        urbaReport: "URBA Report",
        aiAnalysis: "AI Analysis",
        courtesy: "Courtesy",
        billed: "Billed",
        pending: "Pending",
        noConsumption: "No consumption recorded",
        paymentMethod: "Payment Method",
        cardRegistered: "Card registered",
        managedViaStripe: "Managed via Stripe",
        manage: "Manage",
        noPaymentMethod: "No payment method registered",
        addCard: "Add Card",
        billingData: "Billing Data",
        companyName: "Company Name",
        billingType: "Billing Type",
        monthlyPostPaid: "Monthly Post-paid",
      },
      crm: {
        title: "Export CRM",
        subtitle: "Manage your export opportunities",
        newOpportunity: "New Opportunity",
        refresh: "Refresh",
        totalInPipeline: "Total in Pipeline",
        activeOpportunities: "Active Opportunities",
        closedDeals: "Closed Deals",
        conversionRate: "Conversion Rate",
        searchOpportunities: "Search opportunities...",
        allStages: "All stages",
        stages: {
          prospection: "Prospecting",
          qualification: "Qualification",
          proposal: "Proposal",
          negotiation: "Negotiation",
          closing: "Closing",
          won: "Won",
          lost: "Lost",
        },
        noOpportunity: "No opportunities",
        loading: "Loading...",
        edit: "Edit",
        moveTo: "Move to",
        editOpportunity: "Edit Opportunity",
        newExportOpportunity: "New Export Opportunity",
        fillData: "Fill in the commercial opportunity data",
        general: "General",
        export: "Export",
        buyer: "Buyer",
        opportunityTitle: "Opportunity Title",
        stage: "Stage",
        priority: "Priority",
        priorities: {
          low: "Low",
          medium: "Medium",
          high: "High",
          urgent: "Urgent",
        },
        estimatedValue: "Estimated Value (USD)",
        expectedClose: "Expected Close",
        description: "Description",
        destinationCountry: "Destination Country",
        incoterm: "Incoterm",
        paymentCondition: "Payment Terms",
        transportModal: "Transport Mode",
        estimatedVolume: "Estimated Volume",
        ncmCode: "HS Code",
        productDescription: "Product Description",
        registeredBuyer: "Registered Buyer",
        selectBuyer: "Select a company",
        orRegisterNew: "or register a new one",
        buyerName: "Buyer Name",
        contactName: "Contact Name",
        contactEmail: "Contact Email",
        contactPhone: "Contact Phone",
        opportunityDetails: "Opportunity details...",
        buyerRequired: "Buyer required",
        selectOrRegister: "Select a registered company or register a new one.",
        opportunityUpdated: "Opportunity updated",
        opportunityCreated: "Opportunity created",
        opportunityDeleted: "Opportunity deleted",
        activityAdded: "Activity added",
        movedTo: "Moved to",
        stageChangedFrom: "Stage changed from",
        to: "to",
        cancel: "Cancel",
        save: "Save",
      },
    },
  },
};

export const translations_es_app = {
  app: {
    nav: {
      dashboard: "Dashboard",
      executive: "Executive",
      crmExport: "CRM Export",
      companies: "Empresas",
      buyers: "Compradores",
      suppliers: "Proveedores",
      portfolioUpdates: "Actualizaciones Cartera",
      sectorRisk: "Riesgo Sectorial",
      riskMap: "Mapa de Riesgo",
      comparison: "Comparativo",
      simulador360: "Simulador 360",
      simuladorChoque: "Simulador de Choque",
      visaoBrasil: "Visión Brasil",
      setoresVariaveis: "Sectores × Variables",
      matrizDependencia: "Matriz de Dependencia",
      reports: "Reportes",
      collections: "Cobranzas",
      billing: "Facturación",
      profile: "Mi Perfil",
      settings: "Configuración",
      help: "Ayuda",
      support: "Soporte",
      logout: "Salir",
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Visión general de tu portafolio de riesgo comercial",
      noCompanies: "Ninguna empresa registrada",
      addCompany: "Agregar Empresa",
      monitoredCompanies: "Empresas Monitoreadas",
      averageScore: "Score Promedio",
      activeAlerts: "Alertas Activas",
      totalExposure: "Exposición Total",
      bestScore: "Mejor Score",
      highestLimit: "Mayor Límite",
      highExposure: "Exposición Alta",
      recentlyUpdated: "Actualizadas Recientemente",
      recentCompanies: "Empresas Recientes",
      viewAll: "Ver todas",
      viewDetails: "Ver detalles",
      sectorRisk: "Riesgo Sectorial",
      buyer: "Comprador",
      supplier: "Proveedor",
      countries: "países",
      last7days: "últimos 7 días",
      ofLimit: "del límite",
      open: "abierto",
    },
    companies: {
      title: "Empresas",
      subtitle:
        "Gestiona tus empresas monitoreadas (compradores y proveedores)",
      addCompany: "Agregar Empresa",
      total: "Total",
      buyers: "Compradores",
      suppliers: "Proveedores",
      buyer: "Comprador",
      supplier: "Proveedor",
      exposure: "Exposición",
      limit: "Recomendación",
      rating: "Rating",
      trend: "Tendencia",
      updated: "Actualizado",
      country: "País",
      searchPlaceholder: "Buscar por nombre o ID nacional...",
      allTypes: "Todos los tipos",
      allCountries: "Todos los países",
      actions: "Acciones",
      viewDetails: "Ver detalles",
      generateReport: "Generar Reporte URBA",
      aiAnalysis: "Análisis con IA",
      remove: "Eliminar",
      noCompaniesFound: "Ninguna empresa encontrada",
      adjustFilters: "Intenta ajustar los filtros o agrega una nueva empresa.",
      showing: "Mostrando",
      of: "de",
      companies: "empresas",
      page: "Página",
      previous: "Anterior",
      next: "Siguiente",
      list: {
        import: "Importar empresas",
        export: "Exportar",
        columns: "Columnas",
        limitUsage: "Uso del límite",
        rowsPerPage: "Filas por página",
        totalExposure: "Exposición total",
        totalRecommendation: "Recomendación total",
        sheetName: "Empresas",
        all: "Todas",
        recent: "Cambios recientes",
        highExposure: "Exposición alta",
        unlinked: "No vinculadas",
        search: "Buscar por nombre o ID nacional",
        sector: "Sector",
        status: "Estado",
        type: "Tipo",
        noScore: "Sin score",
        monitoring: "En monitoreo",
        inProgress: "En curso",
        notVerified: "No verificado",
        unclassified: "Sin clasificar",
        exporting: "Exportando…",
        selectedCompanies: "Seleccionar empresas de la página",
        company: "Empresa",
        waitingCoface: "Esperando a Coface",
        noRecommendation: "Sin recomendación",
        noExposure: "Sin exposición informada",
        recentChanges: "Cambios recientes",
        notLinkedCoface: "No vinculada a Coface",
        removeBlocked: "La empresa tiene informes vinculados",
        rows: "Mostrando {from}–{to} de {total} empresas",
        noRows: "No se encontraron empresas. Intenta ajustar los filtros.",
        emptyTitle: "Aún no hay empresas en tu cartera",
        emptyHint:
          "Agrega empresas para seguir su riesgo, ratings y límites de crédito.",
        noResultsLimit:
          "La lista puede estar incompleta debido al límite de resultados.",
        loadError: "No fue posible cargar las empresas.",
        deleted: "Empresa eliminada",
        deletedDescription: "La empresa fue eliminada del monitoreo.",
        deleteError: "Error al eliminar",
        deleteErrorDescription: "No fue posible eliminar la empresa.",
        exportUnavailable: "Exportación no disponible",
        exportLimit:
          "Refina los filtros para exportar como máximo 5.000 empresas.",
        exportError: "No fue posible exportar",
        exportTryAgain: "Inténtalo de nuevo más tarde.",
        manualTitle: "Registros manuales",
        manualPending:
          "{count} {company} esperando la validación del equipo TradeRisk",
        hide: "Ocultar",
        viewRegistrations: "Ver registros →",
        filterStatus: "Filtrar estado:",
        allStatuses: "Todos",
        requestedAt: "Solicitado el {date}",
        note: "Nota: {reason}",
        noRegistration: "Ningún registro con este estado.",
        statusPending: "En análisis",
        statusSent: "Registrando en la base",
        statusExists: "Ya existe en la base",
        statusFinished: "Finalizado",
      },
    },
    buyers: {
      title: "Compradores",
      subtitle: "Gestiona tus compradores monitoreados",
      addBuyer: "Agregar Comprador",
      totalBuyers: "Total de Compradores",
      noBuyersFound: "Ningún comprador encontrado",
      adjustFilters: "Intenta ajustar los filtros o agrega un nuevo comprador.",
      diversification: "Diversificación",
    },
    suppliers: {
      title: "Proveedores",
      subtitle: "Gestiona tus proveedores monitoreados",
      addSupplier: "Agregar Proveedor",
      totalSuppliers: "Total de Proveedores",
      noSuppliersFound: "Ningún proveedor encontrado",
      adjustFilters: "Intenta ajustar los filtros o agrega un nuevo proveedor.",
      diversification: "Diversificación",
    },
    addCompany: {
      title: "Agregar Empresa",
      subtitle: "Registra una nueva empresa para monitoreo",
      step1: { title: "Identificación", description: "Buscar en Coface" },
      step2: {
        title: "Tipo y Sector",
        description: "Clasificación y riesgo sectorial",
      },
      step3: { title: "Contexto", description: "Cuestionario de entrada" },
      step4: { title: "Confirmación", description: "Revisión y registro" },
      country: "País",
      selectCountry: "Selecciona el país",
      searchMethod: "Método de Búsqueda",
      byFiscalCode: "Por Código Fiscal",
      byName: "Por Razón Social",
      search: "Buscar en Coface",
      noResults: "Sin resultados",
      registerManually: "Registrar manualmente",
      requiredFields: "Campos requeridos",
      selectCountryMessage: "Selecciona el país.",
      selectCompanyMessage:
        "Selecciona una empresa de la lista o registra manualmente.",
      companyName: "Razón Social",
      fiscalCode: "Código Fiscal",
      enterFiscalCode: "Ingresa el código fiscal",
      enterCompanyName: "Ingresa la razón social",
      searching: "Buscando...",
      searchResults: "Resultados de Búsqueda",
      selectedCompany: "Empresa Seleccionada",
      manualEntry: "Registro Manual",
      companyType: "Tipo de Empresa",
      sectorRisk: "Riesgo Sectorial",
      selectSector: "Selecciona el sector",
      riskLevel: "Nivel de Riesgo",
      outlook: "Perspectiva",
      companySaved: "Empresa registrada",
      companyAddedSuccess: "fue agregada exitosamente.",
      errorRegistering: "Error al registrar",
      back: "Volver",
      next: "Siguiente",
      save: "Guardar",
      saving: "Guardando...",
    },
    riskMap: {
      title: "Mapa de Riesgo Global",
      subtitle: "Evaluación de riesgo país basada en ratings Coface",
      lowRisk: "Riesgo Bajo (A1-A2)",
      mediumRisk: "Riesgo Medio (A3-B)",
      highRisk: "Riesgo Alto (C-D)",
      extremeRisk: "Riesgo Extremo (E)",
      countryRisk: "Riesgo País",
      businessClimate: "Clima Empresarial",
      region: "Región",
      continent: "Continente",
      companiesIn: "Empresas en",
      noCompaniesInCountry: "Ninguna empresa monitoreada en este país",
      clickForDetails: "Haz clic en un país en el mapa para ver detalles",
      allCountries: "Todos los Países",
      moreCompanies: "más empresas",
      searchPlaceholder: "Buscar país por nombre o región...",
      noResults: "No se encontraron países",
      loadingDetails: "Cargando detalles de Coface...",
      orSearchAbove: "o usa la búsqueda arriba",
      details: {
        strengths: "Fortalezas",
        weaknesses: "Debilidades",
        sectorRisks: "Riesgos Sectoriales",
        economicIndicators: "Indicadores Económicos",
        source: "Fuente",
        updatedAt: "Consultado en",
        notAvailable:
          "Datos detallados no disponibles para este país en la base de Coface.",
        // Claves = rotulos originales tal como vienen del scraping de Coface
        // (idioma original, ingles); usados para traducir la columna de
        // rotulos de la tabla de proyecciones economicas sin tocar el dato raspado.
        economicLabels: {
          "GDP growth(%)": "Crecimiento del PIB (%)",
          "Inflation(yearly average,%)": "Inflación (promedio anual, %)",
          "Budget balance(% GDP)": "Balance fiscal (% del PIB)",
          "Current account balance(% GDP)":
            "Saldo en cuenta corriente (% del PIB)",
          "Public debt / GDP(% GDP)": "Deuda pública / PIB (% del PIB)",
        },
      },
    },
    companyDetail: {
      exposureRisk: {
        sections: "Secciones de la pestaña Exposición y riesgo",
        anchors: {
          exposure: "Exposición",
          countrySector: "Riesgo país y sector",
          map: "Mapa",
          kyc: "KYC",
        },
        sources: "Fuentes: Coface URBA · exposición informada por su equipo",
        exposure: {
          title: "Exposición actual",
          question: "Cuánto tiene su empresa por cobrar a este cliente hoy",
          approx: "≈ {value}",
          usage: "Uso de la recomendación Coface",
          usageOf: "{usage}% de {limit}",
          usageAria: "Uso de la recomendación: {usage}%",
          mark0: "0",
          markAttention: "70% atención",
          markLimit: "100% = límite recomendado",
          available: "Disponible en la recomendación",
          exceeds: "Excede la recomendación",
          recommendation: "Recomendación Coface",
          status: "Estado",
          statusWithin: "Dentro del límite",
          statusNear: "Cerca del límite",
          statusAbove: "Por encima del límite",
          statusNone: "No informada",
          statusUnavailable: "Sin recomendación",
          noneText:
            "Informe la exposición actual para acompañar el uso de la recomendación.",
        },
        layers: {
          title: "De dónde viene el riesgo",
          description:
            "El riesgo de crédito suma tres capas: la empresa, el sector en el que actúa y el país donde está.",
          company: "Empresa",
          sector: "Sector",
          country: "País",
          companyDetail: "Puntuación Coface {score}/10 · Rating {rating}",
          companyDetailNoRating: "Puntuación Coface {score}/10",
          noData: "Sin dato",
          sectorLevel: "{name} ({abbr})",
          countryLevel: "{level} · {label}",
          tones: {
            low: "bajo",
            moderate: "moderado",
            elevated: "elevado",
            high: "alto",
          },
          summary: {
            company:
              "El riesgo viene principalmente de la propia empresa ({ref}). País y sector son favorables.",
            companyWithExternal:
              "El riesgo viene principalmente de la propia empresa ({ref}). País y sector también requieren atención.",
            external:
              "El riesgo de la empresa es {companyTone}; el principal factor externo es el {factor}.",
            factorCountry: "país ({level})",
            factorSector: "sector ({abbr})",
            tie: "Empresa, sector y país tienen un riesgo {tone} similar.",
            refRating: "rating {rating}",
            refScore: "puntuación {score}/10",
          },
        },
        period: "Coface · {year} T{quarter}",
        trend: {
          stableFor: "Estable hace {n} trimestres",
          stable: "Estable",
          improvedFull: "Mejoró en {year} T{quarter} ({from} → {to})",
          worsenedFull: "Empeoró en {year} T{quarter} ({from} → {to})",
        },
        country: {
          title: "Riesgo país · {country}",
          businessClimate: "Entorno de negocios {level}",
          scaleMin: "A1 · menor riesgo",
          scaleMax: "E · mayor riesgo",
          note: "Evalúa el promedio de impago de las empresas del país, considerando economía, política y entorno de negocios.",
          scaleAria: "Escala de riesgo país, nivel actual {level}",
          shortLabels: {
            A1: "Riesgo muy bajo",
            A2: "Riesgo bajo",
            A3: "Riesgo satisfactorio",
            A4: "Riesgo razonable",
            B: "Riesgo moderado",
            C: "Riesgo alto",
            D: "Riesgo muy alto",
            E: "Riesgo extremo",
          },
          descriptions: {
            A1: "Economía muy estable e instituciones sólidas; la probabilidad de impago de las empresas es muy baja.",
            A2: "Entorno político y económico estable; el impago de las empresas es bajo y los pagos suelen ser puntuales.",
            A3: "Economía estable e instituciones sólidas; el comportamiento de pago de las empresas es generalmente bueno.",
            A4: "Entorno generalmente favorable, pero con alguna fragilidad económica o política que puede afectar los pagos.",
            B: "Entorno de negocios aceptable, con inestabilidad económica y política que puede afectar el comportamiento de pago de las empresas.",
            C: "Entorno económico y político muy incierto, con fragilidades relevantes; los retrasos de pago son frecuentes.",
            D: "Entorno económico y político de alto riesgo, con instituciones frágiles; el impago de las empresas es muy alto.",
            E: "Entorno de riesgo extremo, con una crisis económica o política grave; el pago de las empresas es altamente imprevisible.",
          },
        },
        sector: {
          title: "Riesgo sectorial · {sector}",
          nace: "NACE {code} · {label}",
          levels: { L: "Bajo", M: "Medio", H: "Alto", VH: "Muy alto" },
          scaleLabels: {
            L: "L · Bajo",
            M: "M · Medio",
            H: "H · Alto",
            VH: "VH · Muy alto",
          },
          scaleMin: "Bajo",
          scaleMax: "Muy alto",
          note: "Evalúa el riesgo de impago de las empresas del sector en la región, con base en la experiencia de pagos de Coface.",
          scaleAria: "Escala de riesgo sectorial, nivel actual {level}",
          notIdentified: "Sector no identificado",
          descriptions: {
            L: "Sector con demanda estable y buena salud financiera; el impago de las empresas es bajo.",
            M: "Sector con riesgo moderado; los márgenes sensibles a costos, tasas de interés y tipo de cambio requieren seguimiento.",
            H: "Sector bajo presión financiera; los retrasos de pago son más frecuentes que el promedio.",
            VH: "Sector en dificultad, con fragilidad financiera generalizada e impago elevado.",
          },
        },
      },
      creditAnalysis: {
        anchors: {
          score: "Score y probabilidad",
          recommendation: "Recomendación de crédito",
          market: "Scores de mercado",
          payment: "Índice de pago",
        },
        source: "Fuente: Coface URBA · consulta {date}",
        sourceNoDate: "Fuente: Coface URBA",
        queryDate: "Consulta {date}",
        sections: "Secciones del análisis de crédito",
        quodUnavailable: "Datos del Score Quod no disponibles.",
        scaleHint: "↑ Mayor = Menor riesgo",
        dra: {
          title: "Score Coface (DRA)",
          description:
            "Calificación de 0 a 10 otorgada por Coface. Cuanto mayor sea, menor será la probabilidad de que la empresa no pague. Calificación {score} = {probability} de probabilidad de incumplimiento en 12 meses.",
          scaleTitle: "ESCALA DE RIESGO DRA",
          link: "Entienda cada calificación de 0 a 10 →",
          risk: [
            "Procesos de insolvencia o reestructuración de deudas",
            "Riesgo extremadamente alto de insolvencia",
            "Riesgo muy alto de insolvencia",
            "Riesgo alto",
            "Riesgo significativo",
            "Riesgo medio-alto",
            "Riesgo aceptable",
            "Riesgo moderado",
            "Riesgo bajo",
            "Riesgo muy bajo",
            "Riesgo excelente",
          ],
          scaleLabels: [
            "Insolvencia",
            "Extremo",
            "Alto",
            "Significativo",
            "Moderado",
            "Bajo",
            "Excelente",
          ],
        },
        table: {
          title: "Entienda cada calificación del score Coface",
          description:
            "Clasificación oficial de Coface y la probabilidad de incumplimiento en 12 meses para cada calificación.",
          colScore: "Nota",
          colClass: "Clasificación Coface",
          colProb: "Prob. 12 meses",
          thisCompany: "Esta empresa",
        },
        quod: {
          title: "Score Quod",
          description:
            "Calificación de 300 a 1000 calculada por Quod con datos del Registro Positivo: pagos puntuales, retrasos, endeudamiento y búsquedas de crédito. Cuanto mayor sea, menor será el riesgo.",
          scaleTitle: "ESCALA DE RIESGO QUOD",
          link: "Cómo funciona el Score Quod →",
          risk: [
            "Riesgo muy alto",
            "Riesgo alto",
            "Riesgo medio",
            "Riesgo bajo",
            "Riesgo muy bajo",
          ],
          scaleLabels: ["Muy alto", "Alto", "Medio", "Bajo", "Muy bajo"],
        },
        quodIndicators: {
          title: "Score Quod · indicadores",
          description:
            "{score}/1000 · {label}. Los {count} indicadores del Registro Positivo que componen la calificación (de 0 a 100 cada uno).",
          unavailable: "Datos del Score Quod no disponibles.",
          items: {
            onTimePayments: {
              name: "Pagos puntuales",
              subtitle: "Compromisos pagados puntualmente · 12 meses",
            },
            currentDelinquency: {
              name: "Mora vigente",
              subtitle: "Tiempo de retraso de las deudas pendientes",
            },
            debtDelinquency: {
              name: "Endeudamiento – mora",
              subtitle: "Contratos vigentes en mora",
            },
            emergencyCredit: {
              name: "Crédito de emergencia",
              subtitle: "Uso de crédito rotativo · 180 días",
            },
            contractingProfile: {
              name: "Perfil de contratación",
              subtitle: "Tipo de producto de crédito contratado",
            },
            creditSearch: {
              name: "Búsqueda de crédito",
              subtitle: "Frecuencia de nuevos contratos",
            },
            relationship: {
              name: "Relación",
              subtitle: "Estabilidad con los acreedores · 180 días",
            },
          },
          risk: {
            low: "RIESGO BAJO",
            medium: "RIESGO MEDIO",
            high: "RIESGO ALTO",
            none: "SIN DATOS",
          },
        },
        gaugeLabel: "Calificación {score} de {max} · {label}",
        gaugeUnavailable: "Calificación no disponible",
        recommendation: {
          title: "Recomendación de crédito",
          rating: "Rating {rating}",
          limitLabel: "Límite recomendado por Coface",
          usage: "Uso del límite",
          exposure: "Exposición actual {exposure} · disponible {available}",
          exposureUnknown: "Exposición no informada",
          exceeded: "Exposición actual {exposure} · excede en {amount}",
          equivalent: "Equivalente en {currency}",
          rate: "cotización {rate} · {date}",
          rateNoDate: "cotización {rate}",
          compact: { thousand: "mil", million: "M" },
          decision: {
            favorable: {
              title: "Decisión Coface: favorable",
              text: "Rating {rating}: Coface recomienda conceder el crédito.",
            },
            favorableNoRating: {
              title: "Decisión Coface: favorable",
              text: "Límite recomendado por Coface para esta empresa.",
            },
            conditional: {
              title: "Decisión Coface: condicional",
              text: "Rating {rating}: conceder con condiciones (garantías, plazo menor o límite parcial).",
            },
            unfavorable: {
              title: "Decisión Coface: desfavorable",
              text: "Coface no recomienda conceder crédito.",
            },
          },
        },
        payment: {
          title: "Índice de pago",
          window: "Coface · últimos 60 meses",
          originalDescription: "Descripción original de Coface",
          translations: {
            "no negative payment experience reported over the last 60 months":
              "Ninguna experiencia negativa de pago reportada en los últimos 60 meses.",
            "some negative payment experiences reported over the last 60 months":
              "Algunas experiencias negativas de pago reportadas en los últimos 60 meses.",
            "considerable negative payment experiences reported over the last 60 months":
              "Experiencias negativas de pago considerables reportadas en los últimos 60 meses.",
          },
          options: {
            none: {
              title: "Sin experiencias negativas",
              text: "No se informó ningún retraso relevante",
            },
            some: {
              title: "Algunas experiencias negativas",
              text: "Se informaron retrasos puntuales",
            },
            considerable: {
              title: "Experiencias negativas considerables",
              text: "Retrasos recurrentes o relevantes",
            },
          },
        },
        history: {
          credit: "Historial de la recomendación",
          score: "Evolución de la puntuación DRA",
          creditSubtitle: "Coface · últimos 12 meses",
          scoreSubtitle: "Coface · últimos 6 meses",
          empty: "Aún sin historial",
          point: "pto",
          points: "ptos",
          expandCredit: "Ver recomendación de crédito",
          expandPayment: "Ver índice de pago",
          expandScore: "Ver evolución de la puntuación",
        },
        detail: {
          recommendation: {
            title: "Recomendación de crédito Coface",
            issued: "Emitida el {date}",
            limitLabel: "Límite máximo recomendado para crédito comercial",
            issuedValue: "Valor emitido por Coface en {currencyName} ({code})",
            issuedValueCode:
              "Valor emitido por Coface en {code} · exposición simultánea máxima",
            pd: "PD {range}",
            currencyNames: { BRL: "reales", USD: "dólares", EUR: "euros" },
            originalValue: "Valor original Coface: {original}",
            converted:
              "Valor original Coface: {original} · convertido por la cotización de {date} ({rate})",
            simultaneous: "exposición simultánea máxima",
            equivalentToday: "Equivalente hoy",
            evolution: "Evolución 12 meses",
            currentUsage: "Uso actual",
            available: "Disponible",
            unknown: "No informada",
            exceeds: "Excede en {amount}",
            decisionSuggested: "Decisión sugerida",
            grant: "Conceder crédito",
            conditional: "Crédito condicional",
            deny: "No conceder crédito",
            rating: "Rating Coface",
            notInformed: "No informado",
            opinion: "Opinión original de Coface",
            scaleTitle: "Escala del rating Coface",
            lowerRisk: "↑ A1 = menor riesgo",
            scaleDescription:
              "El rating mide la probabilidad de incumplimiento (PD) de la empresa en 12 meses. Define la decisión de crédito sugerida para el límite recomendado.",
            decisionBands: {
              grant: "Conceder crédito · A1–A4",
              conditional: "Crédito condicional · B–C",
              deny: "No conceder · D, E, X",
            },
            note: "Condicional: conceder con garantías, plazo menor o límite parcial. X: Coface no recomienda conceder crédito (límite {zero}).",
            howToRead: "Cómo leer esta recomendación",
            methodology: "Entienda la metodología Coface",
            ratingLevels: {
              A1: "Riesgo mínimo",
              A2: "Riesgo muy bajo",
              A3: "Riesgo bajo",
              A4: "Riesgo bajo-medio",
              B: "Riesgo medio",
              C: "Riesgo medio-alto",
              D: "Riesgo alto",
              E: "Riesgo muy alto",
              X: "Sin límite",
            },
            items: {
              limit: {
                title: "Límite recomendado",
                description:
                  "Valor máximo que Coface considera prudente mantener abierto con esta empresa al mismo tiempo (suma de facturas y pedidos).",
              },
              rating: "Rating {rating}",
              ratingRange:
                "{label}: entre {min} y {max} de probabilidad de incumplimiento en los próximos 12 meses.",
              ratingAbove:
                "{label}: por encima de {min} de probabilidad de incumplimiento en los próximos 12 meses.",
              ratingX:
                "Sin límite: Coface no recomienda conceder crédito a esta empresa.",
              decision: {
                grant: {
                  title: "Decisión: conceder",
                  description:
                    "A1 a A4 indican conceder hasta el límite. B–C requieren condiciones; D, E o X indican no conceder.",
                },
                conditional: {
                  title: "Decisión: condicional",
                  description:
                    "B y C requieren condiciones: garantías, plazo menor o límite parcial. A1–A4 indican conceder; D, E o X, no conceder.",
                },
                deny: {
                  title: "Decisión: no conceder",
                  description:
                    "D, E o X indican no conceder crédito. A1–A4 indican conceder; B–C requieren condiciones.",
                },
              },
              currency: {
                title: "Moneda y cambio",
                same: "Valor emitido en {currencyName}. Equivalentes en {targetA} y {targetB} usan la cotización del día y varían con el cambio.",
                different:
                  "Coface emite en {sourceName} ({original}). Convertimos a {displayName} por la cotización del día; el valor en {displaySymbol} varía con el cambio.",
              },
            },
          },
        },
      },
      riskScore: "Score de Riesgo",
      trend: "Tendencia",
      exposure: "Exposición",
      creditLimit: "Recomendación de Crédito",
      summary: "Resumen",
      cofaceData: "Datos Coface",
      contextualData: "Datos Contextuales",
      history: "Historial",
      generalInfo: "Información General",
      nationalId: "ID Nacional",
      country: "País",
      type: "Tipo",
      buyer: "Comprador",
      supplier: "Proveedor",
      exportPdf: "Exportar PDF URBA360",
      textReport: "Reporte de Texto",
      buyReportPdf: "Comprar Reporte PDF",
      aiAnalysis: "Análisis con IA",
      updateData: "Actualizar datos URBA 360",
      companyNotFound: "Empresa no encontrada",
      backToList: "Volver a la lista",
      lastUpdate: "Última actualización",
      notLinkedToCoface:
        "Esta empresa no está vinculada a Coface. Vincúlala para obtener datos detallados.",
      noDataAvailable: "Sin datos disponibles",
    },
    dialogs: {
      delete: {
        title: "Eliminar Empresa",
        description: "Esta acción es irreversible",
        aboutToDelete: "Estás a punto de eliminar",
        typeBuyer: "Comprador",
        typeSupplier: "Proveedor",
        willRemove: "Esta acción eliminará",
        allData: "Todos los datos de la empresa",
        ratingHistory: "Historial de ratings",
        contextualData: "Datos contextuales",
        typeToConfirm: "Escribe ELIMINAR para confirmar",
        confirmWord: "ELIMINAR",
        cancel: "Cancelar",
        deleting: "Eliminando...",
        deleteCompany: "Eliminar Empresa",
      },
      purchase: {
        title: "Comprar Reporte",
        description: "Confirma la compra del reporte PDF",
        price: "Precio",
        confirm: "Confirmar Compra",
        purchasing: "Comprando...",
      },
      sync: {
        title: "Confirmar Actualización",
        description: "Estás a punto de actualizar los datos de la empresa",
        warning: "Aviso de consumo",
        willConsume: "Esta operación consumirá",
        fromCredits: "de créditos de tu cuenta.",
        continueUpdate: "¿Deseas continuar con la actualización?",
        cancel: "Cancelar",
        confirmUpdate: "Confirmar Actualización",
      },
    },
    messages: {
      companySaved: "Empresa registrada",
      companyDeleted: "Empresa eliminada",
      companyRemovedFromMonitoring: "La empresa fue removida del monitoreo.",
      dataSaved: "Datos guardados",
      dataUpdated: "Datos actualizados",
      syncSuccess: "Datos sincronizados exitosamente",
      syncError: "Error de sincronización",
      apiKeyNotConfigured: "API Key no configurada",
      error: "Error",
      success: "Éxito",
      errorDeleting: "Error al eliminar",
    },
    sectorRisk: {
      title: "Riesgo Sectorial",
      subtitle: "Análisis de riesgo por sector",
      low: "Bajo",
      medium: "Medio",
      mediumHigh: "Medio-Alto",
      high: "Alto",
      outlookPositive: "Positiva",
      outlookNegative: "Negativa",
      outlookStable: "Estable",
      sectors: "Sectores",
      history: "Historial",
      totalSectors: "Total de Sectores",
      monitoredCompanies: "Empresas Monitoreadas",
      criticalSectors: "Sectores Críticos",
    },
    common: {
      loading: "Cargando...",
      save: "Guardar",
      cancel: "Cancelar",
      confirm: "Confirmar",
      next: "Siguiente",
      back: "Volver",
      search: "Buscar",
      searchByName: "Buscar por nombre...",
      filter: "Filtrar",
      export: "Exportar",
      download: "Descargar",
      page: "Página",
      of: "de",
      showing: "Mostrando",
      results: "resultados",
      previous: "Anterior",
      diversification: "Diversificación",
      totalExposure: "Exposición Total",
      countries: "Países",
      allCountries: "Todos los países",
    },
    profile: {
      title: "Mi Perfil",
      subtitle: "Gestiona tu información personal",
      personalInfo: "Información Personal",
      updateContactInfo: "Actualiza tus datos de contacto e identificación",
      fullName: "Nombre Completo",
      phone: "Teléfono",
      position: "Cargo / Función",
      email: "Email",
      emailCannotChange: "El email no puede ser modificado",
      saveChanges: "Guardar Cambios",
      saving: "Guardando...",
      accountInfo: "Información de la Cuenta",
      userId: "ID del Usuario",
      tenantRole: "Rol en el Tenant",
      admin: "Administrador",
      user: "Usuario",
      status: "Estado",
      active: "Activo",
      inactive: "Inactivo",
      memberSince: "Miembro desde",
      profileUpdated: "Perfil actualizado",
      infoSaved: "Tu información fue guardada exitosamente.",
      errorUpdating: "Error al actualizar",
    },
    settings: {
      title: "Configuración",
      subtitle: "Gestiona tu organización y equipo",
      organization: "Organización",
      orgInfo: "Información de tu empresa",
      name: "Nombre",
      cnpj: "RUC/NIT",
      type: "Tipo",
      directClient: "Cliente Directo",
      partner: "Socio",
      status: "Estado",
      active: "Activo",
      trial: "Período de Prueba",
      pending: "Pendiente",
      team: "Equipo",
      manageTeam: "Gestiona los miembros de tu organización",
      inviteUser: "Invitar Usuario",
      inviteNew: "Invitar Nuevo Usuario",
      inviteDescription:
        "Envía una invitación por email para agregar un miembro",
      email: "Email",
      sendInvite: "Enviar Invitación",
      cancel: "Cancelar",
      noName: "Sin nombre",
      noPosition: "Sin cargo",
      removeAdmin: "Quitar Admin",
      makeAdmin: "Hacer Admin",
      noUsersFound: "Ningún usuario encontrado",
      accessLevels: "Niveles de Acceso",
      accessDescription: "Entiende los permisos de cada rol",
      viewAllCompanies: "Ver todas las empresas",
      addEditCompanies: "Agregar y editar empresas",
      requestReports: "Solicitar reportes y análisis",
      viewBilling: "Ver facturación y consumo",
      manageUsers: "Gestionar usuarios del equipo",
      inviteSent: "Invitación enviada",
      inviteSentTo: "Una invitación fue enviada a",
      statusUpdated: "Estado actualizado",
      roleUpdated: "Rol actualizado",
      integrations: "Integraciones",
    },
    billing: {
      title: "Facturación",
      subtitle: "Acompaña tu consumo y facturas",
      currentPlan: "Plan Actual",
      monthlyConsumption: "Consumo del Mes",
      totalConsumption: "Consumo Total",
      exchangeRate: "Tipo de Cambio USD",
      sinceBeginning: "desde el inicio",
      consultations: "consultas",
      trialPeriod: "Período de Prueba",
      trialEndsIn: "Tu período de prueba termina en",
      days: "días",
      day: "día",
      hirePlan: "Contratar Plan",
      postPaid: "Post-pago",
      prePaid: "Pre-pago",
      consumptionHistory: "Historial de Consumo",
      allConsultations: "Todas las consultas realizadas",
      date: "Fecha",
      service: "Servicio",
      company: "Empresa",
      valueUsd: "Valor (USD)",
      valueBrl: "Valor (Local)",
      urbaReport: "Reporte URBA",
      aiAnalysis: "Análisis con IA",
      courtesy: "Cortesía",
      billed: "Facturado",
      pending: "Pendiente",
      noConsumption: "Ningún consumo registrado",
      paymentMethod: "Método de Pago",
      cardRegistered: "Tarjeta registrada",
      managedViaStripe: "Gestionado via Stripe",
      manage: "Gestionar",
      noPaymentMethod: "Ningún método de pago registrado",
      addCard: "Agregar Tarjeta",
      billingData: "Datos de Facturación",
      companyName: "Razón Social",
      billingType: "Tipo de Cobro",
      monthlyPostPaid: "Post-pago Mensual",
    },
    crm: {
      title: "CRM de Exportación",
      subtitle: "Gestiona tus oportunidades de exportación",
      newOpportunity: "Nueva Oportunidad",
      refresh: "Actualizar",
      totalInPipeline: "Total en Pipeline",
      activeOpportunities: "Oportunidades Activas",
      closedDeals: "Negocios Cerrados",
      conversionRate: "Tasa de Conversión",
      searchOpportunities: "Buscar oportunidades...",
      allStages: "Todas las etapas",
      stages: {
        prospection: "Prospección",
        qualification: "Calificación",
        proposal: "Propuesta",
        negotiation: "Negociación",
        closing: "Cierre",
        won: "Ganado",
        lost: "Perdido",
      },
      noOpportunity: "Ninguna oportunidad",
      loading: "Cargando...",
      edit: "Editar",
      moveTo: "Mover a",
      editOpportunity: "Editar Oportunidad",
      newExportOpportunity: "Nueva Oportunidad de Exportación",
      fillData: "Completa los datos de la oportunidad comercial",
      general: "General",
      export: "Exportación",
      buyer: "Comprador",
      opportunityTitle: "Título de la Oportunidad",
      stage: "Etapa",
      priority: "Prioridad",
      priorities: {
        low: "Baja",
        medium: "Media",
        high: "Alta",
        urgent: "Urgente",
      },
      estimatedValue: "Valor Estimado (USD)",
      expectedClose: "Previsión de Cierre",
      description: "Descripción",
      destinationCountry: "País de Destino",
      incoterm: "Incoterm",
      paymentCondition: "Condición de Pago",
      transportModal: "Modal de Transporte",
      estimatedVolume: "Volumen Estimado",
      ncmCode: "Código NCM/HS",
      productDescription: "Descripción del Producto",
      registeredBuyer: "Comprador Registrado",
      selectBuyer: "Selecciona una empresa",
      orRegisterNew: "o registra una nueva",
      buyerName: "Nombre del Comprador",
      contactName: "Nombre del Contacto",
      contactEmail: "Email del Contacto",
      contactPhone: "Teléfono del Contacto",
      opportunityDetails: "Detalles de la oportunidad...",
      buyerRequired: "Comprador requerido",
      selectOrRegister:
        "Selecciona una empresa registrada o registra una nueva.",
      opportunityUpdated: "Oportunidad actualizada",
      opportunityCreated: "Oportunidad creada",
      opportunityDeleted: "Oportunidad eliminada",
      activityAdded: "Actividad agregada",
      movedTo: "Movido a",
      stageChangedFrom: "Etapa cambiada de",
      to: "a",
      cancel: "Cancelar",
      save: "Guardar",
    },
  },
};

export const translations_pt_app = {
  app: {
    nav: {
      dashboard: "Dashboard",
      executive: "Executive",
      crmExport: "CRM Exportação",
      companies: "Empresas",
      buyers: "Compradores",
      suppliers: "Fornecedores",
      portfolioUpdates: "Atualizações da Carteira",
      sectorRisk: "Risco Setorial",
      riskMap: "Mapa de Risco",
      comparison: "Comparação",
      simulador360: "Simulador 360",
      simuladorChoque: "Simulador de Choque",
      visaoBrasil: "Visão Brasil",
      setoresVariaveis: "Setores × Variáveis",
      matrizDependencia: "Matriz de Dependência",
      reports: "Relatórios",
      collections: "Cobranças",
      billing: "Faturamento",
      profile: "Perfil",
      settings: "Configurações",
      help: "Ajuda",
      support: "Suporte",
      logout: "Sair",
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Visão geral do seu portfólio de risco comercial",
      noCompanies: "Nenhuma empresa cadastrada",
      addCompany: "Adicionar Empresa",
      monitoredCompanies: "Empresas Monitoradas",
      averageScore: "Score Médio",
      activeAlerts: "Alertas Ativos",
      totalExposure: "Exposição Total",
      bestScore: "Melhor Score",
      highestLimit: "Maior Limite",
      highExposure: "Exposição Alta",
      recentlyUpdated: "Atualizadas Recentemente",
      recentCompanies: "Empresas Recentes",
      viewAll: "Ver todas",
      viewDetails: "Ver detalhes",
      sectorRisk: "Risco Setorial",
      buyer: "Comprador",
      supplier: "Fornecedor",
      countries: "países",
      last7days: "últimos 7 dias",
      ofLimit: "do limite",
      open: "em aberto",
    },
    companies: {
      title: "Empresas",
      subtitle:
        "Gerencie suas empresas monitoradas (compradores e fornecedores)",
      addCompany: "Adicionar Empresa",
      total: "Total",
      buyers: "Compradores",
      suppliers: "Fornecedores",
      buyer: "Comprador",
      supplier: "Fornecedor",
      exposure: "Exposição",
      limit: "Recomendação",
      rating: "Rating",
      trend: "Tendência",
      updated: "Atualizado",
      country: "País",
      searchPlaceholder: "Buscar por nome ou ID nacional...",
      allTypes: "Todos os tipos",
      allCountries: "Todos os países",
      actions: "Ações",
      viewDetails: "Ver detalhes",
      generateReport: "Gerar Relatório URBA",
      aiAnalysis: "Análise com IA",
      remove: "Remover",
      noCompaniesFound: "Nenhuma empresa encontrada",
      adjustFilters: "Tente ajustar os filtros ou adicione uma nova empresa.",
      showing: "Mostrando",
      of: "de",
      companies: "empresas",
      page: "Página",
      previous: "Anterior",
      next: "Próxima",
      list: {
        import: "Importar empresas",
        export: "Exportar",
        columns: "Colunas",
        limitUsage: "Uso do limite",
        rowsPerPage: "Linhas por página",
        totalExposure: "Exposição total",
        totalRecommendation: "Recomendação total",
        sheetName: "Empresas",
        all: "Todas",
        recent: "Alterações recentes",
        highExposure: "Exposição alta",
        unlinked: "Não vinculadas",
        search: "Buscar por nome ou CNPJ / ID nacional",
        sector: "Setor",
        status: "Status",
        type: "Tipo",
        noScore: "Sem score",
        monitoring: "Em monitoramento",
        inProgress: "Em andamento",
        notVerified: "Não verificado",
        unclassified: "Não classificado",
        exporting: "Exportando…",
        selectedCompanies: "Selecionar empresas da página",
        company: "Empresa",
        waitingCoface: "Aguardando Coface",
        noRecommendation: "Sem recomendação",
        noExposure: "Sem exposição informada",
        recentChanges: "Alterações recentes",
        notLinkedCoface: "Não vinculado à Coface",
        removeBlocked: "Empresa possui relatórios vinculados",
        rows: "Mostrando {from}–{to} de {total} empresas",
        noRows: "Nenhuma empresa encontrada. Tente ajustar os filtros.",
        emptyTitle: "Nenhuma empresa na carteira ainda",
        emptyHint:
          "Adicione empresas para acompanhar risco, rating e limites de crédito.",
        noResultsLimit:
          "A lista pode estar incompleta devido ao limite de resultados.",
        loadError: "Não foi possível carregar as empresas.",
        deleted: "Empresa excluída",
        deletedDescription: "A empresa foi removida do monitoramento.",
        deleteError: "Erro ao excluir",
        deleteErrorDescription: "Não foi possível remover a empresa.",
        exportUnavailable: "Exportação indisponível",
        exportLimit:
          "Refine os filtros para exportar no máximo 5.000 empresas.",
        exportError: "Não foi possível exportar",
        exportTryAgain: "Tente novamente mais tarde.",
        manualTitle: "Cadastros manuais",
        manualPending:
          "{count} {company} aguardando validação da equipe TradeRisk",
        hide: "Ocultar",
        viewRegistrations: "Ver cadastros →",
        filterStatus: "Filtrar status:",
        allStatuses: "Todos",
        requestedAt: "Solicitado em {date}",
        note: "Observação: {reason}",
        noRegistration: "Nenhum cadastro nesse status.",
        statusPending: "Em análise",
        statusSent: "Cadastrando na base",
        statusExists: "Já existe na base",
        statusFinished: "Finalizado",
      },
    },
    buyers: {
      title: "Compradores",
      subtitle: "Gerencie seus compradores monitorados",
      addBuyer: "Adicionar Comprador",
      totalBuyers: "Total de Compradores",
      noBuyersFound: "Nenhum comprador encontrado",
      adjustFilters: "Tente ajustar os filtros ou adicione um novo comprador.",
      diversification: "Diversificação",
    },
    suppliers: {
      title: "Fornecedores",
      subtitle: "Gerencie seus fornecedores monitorados",
      addSupplier: "Adicionar Fornecedor",
      totalSuppliers: "Total de Fornecedores",
      noSuppliersFound: "Nenhum fornecedor encontrado",
      adjustFilters: "Tente ajustar os filtros ou adicione um novo fornecedor.",
      diversification: "Diversificação",
    },
    addCompany: {
      title: "Adicionar Empresa",
      subtitle: "Cadastre uma nova empresa para monitoramento",
      step1: { title: "Identificação", description: "Buscar na Coface" },
      step2: {
        title: "Tipo e Setor",
        description: "Classificação e risco setorial",
      },
      step3: { title: "Contexto", description: "Questionário de entrada" },
      step4: { title: "Confirmação", description: "Revisão e cadastro" },
      country: "País",
      selectCountry: "Selecione o país",
      searchMethod: "Método de Busca",
      byFiscalCode: "Por Código Fiscal (CNPJ)",
      byName: "Por Razão Social",
      search: "Buscar na Coface",
      noResults: "Nenhum resultado",
      registerManually: "Cadastrar manualmente",
      requiredFields: "Campos obrigatórios",
      selectCountryMessage: "Selecione o país.",
      selectCompanyMessage:
        "Selecione uma empresa da lista ou cadastre manualmente.",
      companyName: "Razão Social",
      fiscalCode: "Código Fiscal",
      enterFiscalCode: "Informe o código fiscal",
      enterCompanyName: "Informe a razão social",
      searching: "Buscando...",
      searchResults: "Resultados da Busca",
      selectedCompany: "Empresa Selecionada",
      manualEntry: "Cadastro Manual",
      companyType: "Tipo de Empresa",
      sectorRisk: "Risco Setorial",
      selectSector: "Selecione o setor",
      riskLevel: "Nível de Risco",
      outlook: "Perspectiva",
      companySaved: "Empresa cadastrada",
      companyAddedSuccess: "foi adicionada com sucesso.",
      errorRegistering: "Erro ao cadastrar",
      back: "Voltar",
      next: "Próximo",
      save: "Salvar",
      saving: "Salvando...",
    },
    riskMap: {
      title: "Mapa de Risco Global",
      subtitle: "Avaliação de risco país baseada nos ratings Coface",
      lowRisk: "Risco Baixo (A1-A2)",
      mediumRisk: "Risco Médio (A3-B)",
      highRisk: "Risco Alto (C-D)",
      extremeRisk: "Risco Extremo (E)",
      countryRisk: "Risco País",
      businessClimate: "Ambiente Empresarial",
      region: "Região",
      continent: "Continente",
      companiesIn: "Empresas em",
      noCompaniesInCountry: "Nenhuma empresa monitorada neste país",
      clickForDetails: "Clique em um país no mapa para ver detalhes",
      allCountries: "Todos os Países",
      moreCompanies: "mais empresas",
      searchPlaceholder: "Buscar país por nome ou região...",
      noResults: "Nenhum país encontrado",
      loadingDetails: "Carregando detalhes da Coface...",
      orSearchAbove: "ou use a busca acima",
      details: {
        strengths: "Pontos Fortes",
        weaknesses: "Pontos Fracos",
        sectorRisks: "Riscos Setoriais",
        economicIndicators: "Indicadores Econômicos",
        source: "Fonte",
        updatedAt: "Consultado em",
        notAvailable:
          "Dados detalhados não disponíveis para este país na base Coface.",
        // Chaves = rótulos originais tal como vêm do scraping da Coface
        // (idioma original, inglês); usados pra traduzir a coluna de
        // rótulos da tabela de projeções econômicas sem mexer no dado raspado.
        economicLabels: {
          "GDP growth(%)": "Crescimento do PIB (%)",
          "Inflation(yearly average,%)": "Inflação (média anual, %)",
          "Budget balance(% GDP)": "Resultado fiscal (% do PIB)",
          "Current account balance(% GDP)":
            "Saldo em conta corrente (% do PIB)",
          "Public debt / GDP(% GDP)": "Dívida pública / PIB (% do PIB)",
        },
      },
    },
    companyDetail: {
      exposureRisk: {
        sections: "Seções da aba Exposição e risco",
        anchors: {
          exposure: "Exposição",
          countrySector: "Risco país e setor",
          map: "Mapa",
          kyc: "KYC",
        },
        sources: "Fontes: Coface URBA · exposição informada pela sua equipe",
        exposure: {
          title: "Exposição atual",
          question: "Quanto sua empresa tem a receber deste cliente hoje",
          approx: "≈ {value}",
          usage: "Uso da recomendação Coface",
          usageOf: "{usage}% de {limit}",
          usageAria: "Uso da recomendação: {usage}%",
          mark0: "0",
          markAttention: "70% atenção",
          markLimit: "100% = limite recomendado",
          available: "Disponível na recomendação",
          exceeds: "Excede a recomendação",
          recommendation: "Recomendação Coface",
          status: "Status",
          statusWithin: "Dentro do limite",
          statusNear: "Próximo do limite",
          statusAbove: "Acima do limite",
          statusNone: "Não informada",
          statusUnavailable: "Sem recomendação",
          noneText:
            "Informe a exposição atual para acompanhar o uso da recomendação.",
        },
        layers: {
          title: "De onde vem o risco",
          description:
            "O risco de crédito soma três camadas: a empresa, o setor em que atua e o país onde está.",
          company: "Empresa",
          sector: "Setor",
          country: "País",
          companyDetail: "Score Coface {score}/10 · Rating {rating}",
          companyDetailNoRating: "Score Coface {score}/10",
          noData: "Sem dado",
          sectorLevel: "{name} ({abbr})",
          countryLevel: "{level} · {label}",
          tones: {
            low: "baixo",
            moderate: "moderado",
            elevated: "elevado",
            high: "alto",
          },
          summary: {
            company:
              "O risco vem principalmente da própria empresa ({ref}). País e setor são favoráveis.",
            companyWithExternal:
              "O risco vem principalmente da própria empresa ({ref}). País e setor também exigem atenção.",
            external:
              "O risco da empresa é {companyTone}; o principal fator externo é o {factor}.",
            factorCountry: "país ({level})",
            factorSector: "setor ({abbr})",
            tie: "Empresa, setor e país têm risco {tone} semelhante.",
            refRating: "rating {rating}",
            refScore: "score {score}/10",
          },
        },
        period: "Coface · {year} T{quarter}",
        trend: {
          stableFor: "Estável há {n} trimestres",
          stable: "Estável",
          improvedFull: "Melhorou em {year} T{quarter} ({from} → {to})",
          worsenedFull: "Piorou em {year} T{quarter} ({from} → {to})",
        },
        country: {
          title: "Risco país · {country}",
          businessClimate: "Ambiente de negócios {level}",
          scaleMin: "A1 · menor risco",
          scaleMax: "E · maior risco",
          note: "Avalia a média de inadimplência das empresas do país, considerando economia, política e ambiente de negócios.",
          scaleAria: "Escala de risco país, nível atual {level}",
          shortLabels: {
            A1: "Risco muito baixo",
            A2: "Risco baixo",
            A3: "Risco satisfatório",
            A4: "Risco razoável",
            B: "Risco moderado",
            C: "Risco alto",
            D: "Risco muito alto",
            E: "Risco extremo",
          },
          descriptions: {
            A1: "Economia muito estável e instituições sólidas; a probabilidade de inadimplência das empresas é muito baixa.",
            A2: "Ambiente político e econômico estável; a inadimplência das empresas é baixa e o pagamento costuma ser pontual.",
            A3: "Economia estável e instituições sólidas; comportamento de pagamento das empresas geralmente bom.",
            A4: "Ambiente geralmente favorável, mas com alguma fragilidade econômica ou política que pode afetar os pagamentos.",
            B: "Ambiente de negócios aceitável, com instabilidade econômica e política que pode afetar o comportamento de pagamento das empresas.",
            C: "Ambiente econômico e político muito incerto, com fragilidades relevantes; atrasos de pagamento são frequentes.",
            D: "Ambiente econômico e político de alto risco, com instituições frágeis; a inadimplência das empresas é muito alta.",
            E: "Ambiente de risco extremo, com crise econômica ou política grave; o pagamento das empresas é altamente imprevisível.",
          },
        },
        sector: {
          title: "Risco setorial · {sector}",
          nace: "NACE {code} · {label}",
          levels: { L: "Baixo", M: "Médio", H: "Alto", VH: "Muito alto" },
          scaleLabels: {
            L: "L · Baixo",
            M: "M · Médio",
            H: "H · Alto",
            VH: "VH · Muito alto",
          },
          scaleMin: "Baixo",
          scaleMax: "Muito alto",
          note: "Avalia o risco de inadimplência das empresas do setor na região, com base na experiência de pagamentos da Coface.",
          scaleAria: "Escala de risco setorial, nível atual {level}",
          notIdentified: "Setor não identificado",
          descriptions: {
            L: "Setor com demanda estável e boa saúde financeira; a inadimplência das empresas é baixa.",
            M: "Setor com risco moderado; margens sensíveis a custos, juros e câmbio exigem acompanhamento.",
            H: "Setor sob pressão financeira; atrasos de pagamento são mais frequentes que a média.",
            VH: "Setor em dificuldade, com fragilidade financeira generalizada e inadimplência elevada.",
          },
        },
      },
      creditAnalysis: {
        anchors: {
          score: "Score e probabilidade",
          recommendation: "Recomendação de crédito",
          market: "Scores de mercado",
          payment: "Índice de pagamento",
        },
        source: "Fonte: Coface URBA · consulta {date}",
        sourceNoDate: "Fonte: Coface URBA",
        queryDate: "Consulta {date}",
        sections: "Seções da análise de crédito",
        quodUnavailable: "Dados do Score Quod indisponíveis.",
        scaleHint: "↑ Maior = Menor risco",
        dra: {
          title: "Score Coface (DRA)",
          description:
            "Nota de 0 a 10 dada pela Coface. Quanto maior, menor a chance de a empresa não pagar. Nota {score} = {probability} de probabilidade de inadimplência em 12 meses.",
          scaleTitle: "ESCALA DE RISCO DRA",
          link: "Entenda cada nota de 0 a 10 →",
          risk: [
            "Processos de insolvência ou regulação de dívidas",
            "Risco extremamente alto de insolvência",
            "Risco muito alto de insolvência",
            "Alto risco",
            "Risco significativo",
            "Risco médio-alto",
            "Risco aceitável",
            "Risco moderado",
            "Risco baixo",
            "Risco muito baixo",
            "Excelente risco",
          ],
          scaleLabels: [
            "Insolvência",
            "Extremo",
            "Alto",
            "Significativo",
            "Moderado",
            "Baixo",
            "Excelente",
          ],
        },
        table: {
          title: "Entenda cada nota do score Coface",
          description:
            "Classificação oficial Coface e a chance de inadimplência em 12 meses para cada nota.",
          colScore: "Nota",
          colClass: "Classificação Coface",
          colProb: "Prob. 12 meses",
          thisCompany: "Esta empresa",
        },
        quod: {
          title: "Score Quod",
          description:
            "Nota de 300 a 1000 calculada pela Quod com dados do Cadastro Positivo: pagamentos em dia, atrasos, endividamento e busca por crédito. Quanto maior, menor o risco.",
          scaleTitle: "ESCALA DE RISCO QUOD",
          link: "Como funciona o Score Quod →",
          risk: [
            "Risco muito alto",
            "Risco alto",
            "Risco médio",
            "Risco baixo",
            "Risco muito baixo",
          ],
          scaleLabels: ["Muito alto", "Alto", "Médio", "Baixo", "Muito baixo"],
        },
        quodIndicators: {
          title: "Score Quod · indicadores",
          description:
            "{score}/1000 · {label}. Os {count} indicadores do Cadastro Positivo que formam a nota (0 a 100 cada).",
          unavailable: "Dados do Score Quod indisponíveis.",
          items: {
            onTimePayments: {
              name: "Pagamento em dia",
              subtitle: "Compromissos pagos pontualmente · 12 meses",
            },
            currentDelinquency: {
              name: "Atraso vigente",
              subtitle: "Tempo de atraso das dívidas em aberto",
            },
            debtDelinquency: {
              name: "Endividamento – atraso",
              subtitle: "Contratos vigentes em atraso",
            },
            emergencyCredit: {
              name: "Crédito emergencial",
              subtitle: "Uso de crédito rotativo · 180 dias",
            },
            contractingProfile: {
              name: "Perfil de contratações",
              subtitle: "Tipo de produto de crédito contratado",
            },
            creditSearch: {
              name: "Busca por crédito",
              subtitle: "Frequência de novos contratos",
            },
            relationship: {
              name: "Relacionamento",
              subtitle: "Estabilidade com os credores · 180 dias",
            },
          },
          risk: {
            low: "RISCO BAIXO",
            medium: "RISCO MÉDIO",
            high: "RISCO ALTO",
            none: "SEM DADOS",
          },
        },
        gaugeLabel: "Score {score} de {max} · {label}",
        gaugeUnavailable: "Score indisponível",
        recommendation: {
          title: "Recomendação de crédito",
          rating: "Rating {rating}",
          limitLabel: "Limite recomendado pela Coface",
          usage: "Uso do limite",
          exposure: "Exposição atual {exposure} · disponível {available}",
          exposureUnknown: "Exposição não informada",
          exceeded: "Exposição atual {exposure} · excede em {amount}",
          equivalent: "Equivalente em {currency}",
          rate: "cotação {rate} · {date}",
          rateNoDate: "cotação {rate}",
          compact: { thousand: "mil", million: "mi" },
          decision: {
            favorable: {
              title: "Decisão Coface: favorável",
              text: "Rating {rating}: a Coface indica conceder o crédito.",
            },
            favorableNoRating: {
              title: "Decisão Coface: favorável",
              text: "Limite recomendado pela Coface para esta empresa.",
            },
            conditional: {
              title: "Decisão Coface: condicional",
              text: "Rating {rating}: conceder com condições (garantias, prazo menor ou limite parcial).",
            },
            unfavorable: {
              title: "Decisão Coface: desfavorável",
              text: "A Coface não recomenda conceder crédito.",
            },
          },
        },
        payment: {
          title: "Índice de pagamento",
          window: "Coface · últimos 60 meses",
          originalDescription: "Descrição original da Coface",
          translations: {
            "no negative payment experience reported over the last 60 months":
              "Nenhuma experiência negativa de pagamento reportada nos últimos 60 meses.",
            "some negative payment experiences reported over the last 60 months":
              "Algumas experiências negativas de pagamento reportadas nos últimos 60 meses.",
            "considerable negative payment experiences reported over the last 60 months":
              "Experiências negativas de pagamento consideráveis reportadas nos últimos 60 meses.",
          },
          options: {
            none: {
              title: "Sem experiências negativas",
              text: "Nenhum atraso relevante reportado",
            },
            some: {
              title: "Algumas experiências negativas",
              text: "Atrasos pontuais reportados",
            },
            considerable: {
              title: "Experiências negativas consideráveis",
              text: "Atrasos recorrentes ou relevantes",
            },
          },
        },
        history: {
          credit: "Histórico da recomendação",
          score: "Evolução do score DRA",
          creditSubtitle: "Coface · últimos 12 meses",
          scoreSubtitle: "Coface · últimos 6 meses",
          empty: "Ainda sem histórico",
          point: "pt",
          points: "pts",
          expandCredit: "Ver recomendação de crédito",
          expandPayment: "Ver índice de pagamento",
          expandScore: "Ver evolução do score",
        },
        detail: {
          recommendation: {
            title: "Recomendação de crédito Coface",
            issued: "Emitida em {date}",
            limitLabel: "Limite máximo recomendado para crédito comercial",
            issuedValue: "Valor emitido pela Coface em {currencyName} ({code})",
            issuedValueCode:
              "Valor emitido pela Coface em {code} · exposição simultânea máxima",
            pd: "PD {range}",
            currencyNames: { BRL: "reais", USD: "dólares", EUR: "euros" },
            originalValue: "Valor original Coface: {original}",
            converted:
              "Valor original Coface: {original} · convertido pela cotação de {date} ({rate})",
            simultaneous: "exposição simultânea máxima",
            equivalentToday: "Equivalente hoje",
            evolution: "Evolução 12 meses",
            currentUsage: "Uso atual",
            available: "Disponível",
            unknown: "Não informada",
            exceeds: "Excede em {amount}",
            decisionSuggested: "Decisão sugerida",
            grant: "Conceder crédito",
            conditional: "Crédito condicional",
            deny: "Não conceder crédito",
            rating: "Rating Coface",
            notInformed: "Não informado",
            opinion: "Parecer original da Coface",
            scaleTitle: "Escala do rating Coface",
            lowerRisk: "↑ A1 = menor risco",
            scaleDescription:
              "O rating mede a probabilidade de inadimplência (PD) da empresa em 12 meses. Ele define a decisão de crédito sugerida para o limite recomendado.",
            decisionBands: {
              grant: "Conceder crédito · A1–A4",
              conditional: "Crédito condicional · B–C",
              deny: "Não conceder · D, E, X",
            },
            note: "Condicional: conceder com garantias, prazo menor ou limite parcial. X: a Coface não recomenda conceder crédito (limite {zero}).",
            howToRead: "Como ler esta recomendação",
            methodology: "Entenda a metodologia Coface",
            ratingLevels: {
              A1: "Risco mínimo",
              A2: "Risco muito baixo",
              A3: "Risco baixo",
              A4: "Risco baixo-médio",
              B: "Risco médio",
              C: "Risco médio-alto",
              D: "Risco alto",
              E: "Risco muito alto",
              X: "Sem limite",
            },
            items: {
              limit: {
                title: "Limite recomendado",
                description:
                  "Valor máximo que a Coface considera prudente manter em aberto com esta empresa ao mesmo tempo (soma de faturas e pedidos).",
              },
              rating: "Rating {rating}",
              ratingRange:
                "{label}: entre {min} e {max} de chance de inadimplência nos próximos 12 meses.",
              ratingAbove:
                "{label}: acima de {min} de chance de inadimplência nos próximos 12 meses.",
              ratingX:
                "Sem limite: a Coface não recomenda conceder crédito a esta empresa.",
              decision: {
                grant: {
                  title: "Decisão: conceder",
                  description:
                    "A1 a A4 indicam concessão até o limite. B–C pedem condições; D, E ou X indicam não conceder.",
                },
                conditional: {
                  title: "Decisão: condicional",
                  description:
                    "B e C pedem condições: garantias, prazo menor ou limite parcial. A1–A4 indicam conceder; D, E ou X, não conceder.",
                },
                deny: {
                  title: "Decisão: não conceder",
                  description:
                    "D, E ou X indicam não conceder crédito. A1–A4 indicam conceder; B–C pedem condições.",
                },
              },
              currency: {
                title: "Moeda e câmbio",
                same: "Valor emitido em {currencyName}. Equivalentes em {targetA} e {targetB} usam a cotação do dia e variam com o câmbio.",
                different:
                  "A Coface emite em {sourceName} ({original}). Convertemos para {displayName} pela cotação do dia; o valor em {displaySymbol} varia com o câmbio.",
              },
            },
          },
        },
      },
      riskScore: "Score de Risco",
      trend: "Tendência",
      exposure: "Exposição",
      creditLimit: "Recomendação de Crédito",
      summary: "Resumo",
      cofaceData: "Dados Coface",
      contextualData: "Dados Contextuais",
      history: "Histórico",
      generalInfo: "Informações Gerais",
      nationalId: "ID Nacional",
      country: "País",
      type: "Tipo",
      buyer: "Comprador",
      supplier: "Fornecedor",
      exportPdf: "Exportar PDF URBA360",
      textReport: "Relatório Texto",
      buyReportPdf: "Comprar Report PDF",
      aiAnalysis: "Análise com IA",
      updateData: "Atualizar dados URBA 360",
      companyNotFound: "Empresa não encontrada",
      backToList: "Voltar para lista",
      lastUpdate: "Última atualização",
      notLinkedToCoface:
        "Esta empresa não está vinculada à Coface. Vincule-a para obter dados detalhados.",
      noDataAvailable: "Sem dados disponíveis",
    },
    dialogs: {
      delete: {
        title: "Excluir Empresa",
        description: "Esta ação é irreversível",
        aboutToDelete: "Você está prestes a excluir",
        typeBuyer: "Comprador",
        typeSupplier: "Fornecedor",
        willRemove: "Esta ação irá remover",
        allData: "Todos os dados da empresa",
        ratingHistory: "Histórico de ratings",
        contextualData: "Dados contextuais",
        typeToConfirm: "Digite EXCLUIR para confirmar",
        confirmWord: "EXCLUIR",
        cancel: "Cancelar",
        deleting: "Excluindo...",
        deleteCompany: "Excluir Empresa",
      },
      purchase: {
        title: "Comprar Relatório",
        description: "Confirme a compra do relatório PDF",
        price: "Preço",
        confirm: "Confirmar Compra",
        purchasing: "Comprando...",
      },
      sync: {
        title: "Confirmar Atualização",
        description: "Você está prestes a atualizar os dados da empresa",
        warning: "Aviso de consumo",
        willConsume: "Esta operação consumirá",
        fromCredits: "de créditos da sua conta.",
        continueUpdate: "Deseja continuar com a atualização?",
        cancel: "Cancelar",
        confirmUpdate: "Confirmar Atualização",
      },
    },
    messages: {
      companySaved: "Empresa cadastrada",
      companyDeleted: "Empresa excluída",
      companyRemovedFromMonitoring: "A empresa foi removida do monitoramento.",
      dataSaved: "Dados salvos",
      dataUpdated: "Dados atualizados",
      syncSuccess: "Dados sincronizados com sucesso",
      syncError: "Erro na sincronização",
      apiKeyNotConfigured: "API Key não configurada",
      error: "Erro",
      success: "Sucesso",
      errorDeleting: "Erro ao excluir",
    },
    sectorRisk: {
      title: "Risco Setorial",
      subtitle: "Análise de risco por setor",
      low: "Baixo",
      medium: "Médio",
      mediumHigh: "Médio-Alto",
      high: "Alto",
      outlookPositive: "Positiva",
      outlookNegative: "Negativa",
      outlookStable: "Estável",
      sectors: "Setores",
      history: "Histórico",
      totalSectors: "Total de Setores",
      monitoredCompanies: "Empresas Monitoradas",
      criticalSectors: "Setores Críticos",
    },
    common: {
      loading: "Carregando...",
      save: "Salvar",
      cancel: "Cancelar",
      confirm: "Confirmar",
      next: "Próximo",
      back: "Voltar",
      search: "Buscar",
      searchByName: "Buscar por nome...",
      filter: "Filtrar",
      export: "Exportar",
      download: "Download",
      page: "Página",
      of: "de",
      showing: "Mostrando",
      results: "resultados",
      previous: "Anterior",
      diversification: "Diversificação",
      totalExposure: "Exposição Total",
      countries: "Países",
      allCountries: "Todos os países",
    },
    profile: {
      title: "Meu Perfil",
      subtitle: "Gerencie suas informações pessoais",
      personalInfo: "Informações Pessoais",
      updateContactInfo: "Atualize seus dados de contato e identificação",
      fullName: "Nome Completo",
      phone: "Telefone",
      position: "Cargo / Função",
      email: "Email",
      emailCannotChange: "O email não pode ser alterado",
      saveChanges: "Salvar Alterações",
      saving: "Salvando...",
      accountInfo: "Informações da Conta",
      userId: "ID do Usuário",
      tenantRole: "Função no Tenant",
      admin: "Administrador",
      user: "Usuário",
      status: "Status",
      active: "Ativo",
      inactive: "Inativo",
      memberSince: "Membro desde",
      profileUpdated: "Perfil atualizado",
      infoSaved: "Suas informações foram salvas com sucesso.",
      errorUpdating: "Erro ao atualizar",
    },
    settings: {
      title: "Configurações",
      subtitle: "Gerencie sua organização e equipe",
      organization: "Organização",
      orgInfo: "Informações da sua empresa",
      name: "Nome",
      cnpj: "CNPJ",
      type: "Tipo",
      directClient: "Cliente Direto",
      partner: "Parceiro",
      status: "Status",
      active: "Ativo",
      trial: "Período de Teste",
      pending: "Pendente",
      team: "Equipe",
      manageTeam: "Gerencie os membros da sua organização",
      inviteUser: "Convidar Usuário",
      inviteNew: "Convidar Novo Usuário",
      inviteDescription: "Envie um convite por email para adicionar um membro",
      email: "Email",
      sendInvite: "Enviar Convite",
      cancel: "Cancelar",
      noName: "Sem nome",
      noPosition: "Sem cargo",
      removeAdmin: "Remover Admin",
      makeAdmin: "Tornar Admin",
      noUsersFound: "Nenhum usuário encontrado",
      accessLevels: "Níveis de Acesso",
      accessDescription: "Entenda as permissões de cada função",
      viewAllCompanies: "Visualizar todas as empresas",
      addEditCompanies: "Adicionar e editar empresas",
      requestReports: "Solicitar relatórios e análises",
      viewBilling: "Ver faturamento e consumo",
      manageUsers: "Gerenciar usuários da equipe",
      inviteSent: "Convite enviado",
      inviteSentTo: "Um convite foi enviado para",
      statusUpdated: "Status atualizado",
      roleUpdated: "Função atualizada",
      integrations: "Integrações",
    },
    billing: {
      title: "Faturamento",
      subtitle: "Acompanhe seu consumo e faturas",
      currentPlan: "Plano Atual",
      monthlyConsumption: "Consumo do Mês",
      totalConsumption: "Consumo Total",
      exchangeRate: "Cotação USD/BRL",
      sinceBeginning: "desde o início",
      consultations: "consultas",
      trialPeriod: "Período de Teste",
      trialEndsIn: "Seu período de teste termina em",
      days: "dias",
      day: "dia",
      hirePlan: "Contratar Plano",
      postPaid: "Pós-pago",
      prePaid: "Pré-pago",
      consumptionHistory: "Histórico de Consumo",
      allConsultations: "Todas as consultas realizadas",
      date: "Data",
      service: "Serviço",
      company: "Empresa",
      valueUsd: "Valor (USD)",
      valueBrl: "Valor (R$)",
      urbaReport: "Relatório URBA",
      aiAnalysis: "Análise com IA",
      courtesy: "Cortesia",
      billed: "Faturado",
      pending: "Pendente",
      noConsumption: "Nenhum consumo registrado",
      paymentMethod: "Método de Pagamento",
      cardRegistered: "Cartão cadastrado",
      managedViaStripe: "Gerenciado via Stripe",
      manage: "Gerenciar",
      noPaymentMethod: "Nenhum método de pagamento cadastrado",
      addCard: "Adicionar Cartão",
      billingData: "Dados de Faturamento",
      companyName: "Razão Social",
      billingType: "Tipo de Cobrança",
      monthlyPostPaid: "Pós-pago Mensal",
    },
    crm: {
      title: "CRM de Exportação",
      subtitle: "Gerencie suas oportunidades de exportação",
      newOpportunity: "Nova Oportunidade",
      refresh: "Atualizar",
      totalInPipeline: "Total em Pipeline",
      activeOpportunities: "Oportunidades Ativas",
      closedDeals: "Negócios Fechados",
      conversionRate: "Taxa de Conversão",
      searchOpportunities: "Buscar oportunidades...",
      allStages: "Todas etapas",
      stages: {
        prospection: "Prospecção",
        qualification: "Qualificação",
        proposal: "Proposta",
        negotiation: "Negociação",
        closing: "Fechamento",
        won: "Ganho",
        lost: "Perdido",
      },
      noOpportunity: "Nenhuma oportunidade",
      loading: "Carregando...",
      edit: "Editar",
      moveTo: "Mover para",
      editOpportunity: "Editar Oportunidade",
      newExportOpportunity: "Nova Oportunidade de Exportação",
      fillData: "Preencha os dados da oportunidade comercial",
      general: "Geral",
      export: "Exportação",
      buyer: "Comprador",
      opportunityTitle: "Título da Oportunidade",
      stage: "Etapa",
      priority: "Prioridade",
      priorities: {
        low: "Baixa",
        medium: "Média",
        high: "Alta",
        urgent: "Urgente",
      },
      estimatedValue: "Valor Estimado (USD)",
      expectedClose: "Previsão de Fechamento",
      description: "Descrição",
      destinationCountry: "País de Destino",
      incoterm: "Incoterm",
      paymentCondition: "Condição de Pagamento",
      transportModal: "Modal de Transporte",
      estimatedVolume: "Volume Estimado",
      ncmCode: "Código NCM/HS",
      productDescription: "Descrição do Produto",
      registeredBuyer: "Comprador Cadastrado",
      selectBuyer: "Selecione uma empresa",
      orRegisterNew: "ou cadastre uma nova",
      buyerName: "Nome do Comprador",
      contactName: "Nome do Contato",
      contactEmail: "Email do Contato",
      contactPhone: "Telefone do Contato",
      opportunityDetails: "Detalhes da oportunidade...",
      buyerRequired: "Comprador obrigatório",
      selectOrRegister:
        "Selecione uma empresa cadastrada ou cadastre uma nova.",
      opportunityUpdated: "Oportunidade atualizada",
      opportunityCreated: "Oportunidade criada",
      opportunityDeleted: "Oportunidade excluída",
      activityAdded: "Atividade adicionada",
      movedTo: "Movido para",
      stageChangedFrom: "Etapa alterada de",
      to: "para",
      cancel: "Cancelar",
      save: "Salvar",
    },
  },
};

// Auth translations - PT
const auth_pt = {
  auth: {
    login: {
      pageTitle: "Login | TradeRisk360",
      metaDescription:
        "Login TradeRisk360 - Acesse o painel de monitoramento e risco de crédito para sua empresa.",
      title: "Entrar",
      subtitle: "Acesse sua conta TradeRisk360",
      email: "E-mail corporativo",
      emailPlaceholder: "voce@empresa.com.br",
      password: "Senha",
      passwordPlaceholder: "••••••••",
      forgotPassword: "Esqueci minha senha",
      submit: "Entrar",
      submitting: "Entrando...",
      noAccount: "Ainda não tem acesso?",
      requestAccess: "Solicitar acesso",
      backToSite: "Voltar ao site",
      otherOptions: "Outras opções",
      errors: {
        invalidCredentials: "E-mail ou senha incorretos",
        tooManyAttempts: "Muitas tentativas. Tente novamente mais tarde.",
        invalidEmail: "E-mail inválido.",
        invalidPassword: "Senha inválida.",
        unexpected: "Ocorreu um erro inesperado. Tente novamente.",
        loginError: "Erro no login",
        error: "Erro",
      },
      success: {
        title: "Login realizado!",
        redirecting: "Redirecionando...",
      },
      sessionExpired: "Sua sessão expirou. Por favor, faça login novamente.",
      showPassword: "Mostrar senha",
      hidePassword: "Ocultar senha",
      show: "Mostrar",
      hide: "Ocultar",
      passwordHelp: "Confira maiúsculas e o Caps Lock.",
      security: "Conexão segura · verificação em 2 etapas · LGPD",
      forgot: {
        title: "Redefinir senha",
        subtitle:
          "Informe o e-mail da sua conta. Enviamos um link válido por 1 hora.",
        send: "Enviar link de redefinição",
        sending: "Enviando...",
        help: "Não lembra o e-mail? Fale com o administrador da sua organização.",
        back: "Voltar para o login",
      },
      sent: {
        title: "Verifique seu e-mail",
        description:
          "Se existir uma conta para {email}, o link chega em alguns minutos. Confira também o spam.",
        openMail: "Abrir app de e-mail",
        resendIn: "Não chegou? Reenviar em {time}",
        resend: "Reenviar",
      },
      forgotPasswordModal: {
        enterEmail: "Informe o e-mail",
        enterEmailDescription: "Digite seu e-mail para recuperar a senha",
        emailSent: "Email enviado!",
        emailSentDescription:
          "Verifique sua caixa de entrada para redefinir a senha.",
        errorSending: "Não foi possível enviar o email de recuperação",
      },
    },
    verify: {
      pageTitle: "Verificar Email | TradeRisk360",
      title: "Conta criada!",
      description:
        "Verifique seu email para confirmar o cadastro e depois faça login.",
      loginButton: "Fazer Login",
    },
    mfa: {
      title: "Verificação em 2 etapas",
      subtitle: "Digite o código de 6 dígitos do seu app autenticador.",
      verify: "Verificar e entrar",
      cancel: "Cancelar e sair",
      paste: "Colar código",
      digit: "Dígito",
      of: "de",
      errors: {
        invalidCode: "Código inválido. Verifique e tente novamente.",
        tooManyAttempts: "Muitas tentativas. Tente novamente mais tarde.",
        generic: "Não foi possível verificar o código. Tente novamente.",
      },
    },
    noTenant: {
      pageTitle: "Aguardando Ativação | TradeRisk360",
      title: "Conta pendente",
      description:
        "Sua conta ainda não está associada a uma empresa. Entre em contato com o suporte para ativação.",
      logoutButton: "Sair e tentar outra conta",
      backToSite: "Voltar ao site",
    },
    resetPassword: {
      pageTitle: "Redefinir Senha | TradeRisk360",
      invalidLinkTitle: "Link Inválido",
      invalidLinkPageTitle: "Link Inválido | TradeRisk360",
      invalidLinkDescription:
        "O link de recuperação de senha expirou ou é inválido. Solicite um novo link.",
      backToLogin: "Voltar ao Login",
      successPageTitle: "Senha Alterada | TradeRisk360",
      successTitle: "Senha Alterada!",
      successDescription:
        "Sua senha foi redefinida com sucesso. Você será redirecionado para o login.",
      title: "Redefinir Senha",
      subtitle: "Digite sua nova senha abaixo",
      newPassword: "Nova Senha",
      confirmPassword: "Confirmar Nova Senha",
      submit: "Alterar Senha",
      submitting: "Alterando...",
      errors: {
        passwordMismatch: "As senhas não coincidem",
        passwordTooShort: "A senha deve ter pelo menos 6 caracteres",
        unexpected: "Ocorreu um erro ao redefinir a senha. Tente novamente.",
        error: "Erro",
      },
      success: {
        title: "Sucesso!",
        description: "Sua senha foi alterada com sucesso.",
      },
    },
    signup: {
      pageTitle: "Criar Conta | TradeRisk360",
      steps: {
        cnpj: "CNPJ",
        company: "Empresa",
        user: "Usuário",
        terms: "Termos",
      },
      step1: {
        title: "Validação do CNPJ",
        subtitle: "Informe o CNPJ da sua empresa para começar",
        cnpjLabel: "CNPJ da empresa",
        validateButton: "Validar",
        cnpjValid: "CNPJ Válido",
        tradeName: "Nome fantasia:",
        errors: {
          invalid: "CNPJ inválido - verifique os números digitados",
          required: "CNPJ deve ter 14 dígitos",
          notFound: "CNPJ não encontrado",
          inactive: "Empresa não está ativa na Receita Federal",
          validationError: "Erro ao validar CNPJ. Tente novamente.",
          validateFirst: "Valide o CNPJ antes de continuar",
        },
        success: {
          title: "CNPJ validado!",
          company: "Empresa:",
        },
      },
      step2: {
        title: "Qualificação",
        subtitle: "Nos ajude a entender melhor sua operação",
        companyName: "Razão Social",
        tradeName: "Nome Fantasia",
        tradeNamePlaceholder: "Nome fantasia da empresa",
        mainActivity: "Atividade Principal (CNAE)",
        phone: "Telefone",
        phonePlaceholder: "(00) 0000-0000",
        zipCode: "CEP",
        address: "Endereço",
        number: "Número",
        complement: "Complemento",
        neighborhood: "Bairro",
        city: "Cidade",
        state: "UF",
      },
      step3: {
        title: "Seus Dados",
        subtitle: "Preencha seus dados pessoais para acesso",
        fullName: "Nome completo",
        fullNamePlaceholder: "João Silva",
        email: "Email corporativo",
        emailPlaceholder: "joao@suaempresa.com.br",
        emailHint:
          "Utilize o email da sua empresa. Emails pessoais como Gmail, Hotmail, Yahoo não são permitidos.",
        emailBlockedError:
          "Por favor, utilize um email corporativo. Emails pessoais (Gmail, Hotmail, etc.) não são permitidos.",
        position: "Cargo/Posição",
        positionPlaceholder: "Ex: Gerente de Crédito",
        password: "Senha",
        confirmPassword: "Confirmar senha",
      },
      step4: {
        title: "Qualificação e Termos",
        subtitle: "Finalize seu cadastro",
        operationSection: "Sobre sua operação",
        isExporter: "Sua empresa exporta?",
        exporterOptions: {
          yes: "Sim",
          no: "Não",
          planning: "Planejando",
        },
        yearsExporting: "Há quantos anos exporta?",
        yearsOptions: {
          lessThan1: "Menos de 1 ano",
          oneToThree: "1 a 3 anos",
          threeToFive: "3 a 5 anos",
          fiveToTen: "5 a 10 anos",
          moreThan10: "Mais de 10 anos",
        },
        paymentTerms: "Prazos de pagamento",
        paymentOptions: {
          advance: "Antecipado",
          days30: "30 dias",
          days60: "60 dias",
          days90: "90 dias",
          days120plus: "120+ dias",
        },
        transportModals: "Modais de transporte utilizados",
        modals: {
          maritime: "Marítimo",
          air: "Aéreo",
          road: "Rodoviário",
          rail: "Ferroviário",
        },
        mainMarkets: "Principais mercados",
        markets: {
          latam: "América Latina",
          usa: "Estados Unidos",
          europe: "Europa",
          asia: "Ásia",
          africa: "África",
          oceania: "Oceania",
        },
        termsAccept: "Li e aceito os",
        termsOfUse: "Termos de Uso",
        and: "e o",
        serviceAgreement: "Contrato de Prestação de Serviços",
        privacyAccept: "Li e aceito a",
        privacyPolicy: "Política de Privacidade",
        summary: "Resumo do cadastro",
        company: "Empresa:",
        cnpj: "CNPJ:",
        name: "Nome:",
        email: "Email:",
        plan: "Plano:",
        trialPlan: "Trial (14 dias grátis)",
        submitButton: "Criar minha conta",
        submitting: "Criando conta...",
      },
      common: {
        back: "Voltar",
        next: "Próximo",
        select: "Selecione",
      },
      success: {
        title: "Conta criada com sucesso!",
        description: "Redirecionando para o seu painel...",
      },
      errors: {
        createAccount: "Erro ao criar conta",
        unexpected: "Ocorreu um erro. Tente novamente.",
      },
      footer: {
        hasAccount: "Já tem uma conta?",
        login: "Fazer login",
      },
    },
  },
};

// Auth translations - ES
const auth_es = {
  auth: {
    login: {
      pageTitle: "Ingresar | TradeRisk360",
      metaDescription:
        "Ingresar TradeRisk360 - Accede al panel de monitoreo y riesgo crediticio para tu empresa.",
      title: "Ingresar",
      subtitle: "Accede a tu cuenta TradeRisk360",
      email: "Correo corporativo",
      emailPlaceholder: "tu@empresa.com",
      password: "Contraseña",
      passwordPlaceholder: "••••••••",
      forgotPassword: "Olvidé mi contraseña",
      submit: "Ingresar",
      submitting: "Ingresando...",
      noAccount: "¿Aún no tienes acceso?",
      requestAccess: "Solicitar acceso",
      backToSite: "Volver al sitio",
      otherOptions: "Otras opciones",
      errors: {
        invalidCredentials: "Correo o contraseña incorrectos",
        tooManyAttempts: "Demasiados intentos. Inténtalo de nuevo más tarde.",
        invalidEmail: "Correo inválido.",
        invalidPassword: "Contraseña inválida.",
        unexpected: "Ocurrió un error inesperado. Intenta de nuevo.",
        loginError: "Error al ingresar",
        error: "Error",
      },
      success: {
        title: "¡Ingreso exitoso!",
        redirecting: "Redirigiendo...",
      },
      sessionExpired: "Tu sesión expiró. Inicia sesión nuevamente.",
      showPassword: "Mostrar contraseña",
      hidePassword: "Ocultar contraseña",
      show: "Mostrar",
      hide: "Ocultar",
      passwordHelp: "Revisa mayúsculas y Bloq Mayús.",
      security: "Conexión segura · verificación en dos pasos · LGPD",
      forgot: {
        title: "Restablecer contraseña",
        subtitle:
          "Ingresa el correo de tu cuenta. Enviamos un enlace válido por 1 hora.",
        send: "Enviar enlace de restablecimiento",
        sending: "Enviando...",
        help: "¿No recuerdas el correo? Habla con el administrador de tu organización.",
        back: "Volver al inicio de sesión",
      },
      sent: {
        title: "Revisa tu correo",
        description:
          "Si existe una cuenta para {email}, el enlace llegará en unos minutos. Revisa también el spam.",
        openMail: "Abrir aplicación de correo",
        resendIn: "¿No llegó? Reenviar en {time}",
        resend: "Reenviar",
      },
      forgotPasswordModal: {
        enterEmail: "Ingresa el correo",
        enterEmailDescription: "Escribe tu correo para recuperar la contraseña",
        emailSent: "¡Correo enviado!",
        emailSentDescription:
          "Revisa tu bandeja de entrada para restablecer la contraseña.",
        errorSending: "No fue posible enviar el correo de recuperación",
      },
    },
    verify: {
      pageTitle: "Verificar Correo | TradeRisk360",
      title: "¡Cuenta creada!",
      description:
        "Verifica tu correo para confirmar el registro y luego inicia sesión.",
      loginButton: "Iniciar Sesión",
    },
    mfa: {
      title: "Verificación en dos pasos",
      subtitle:
        "Ingresa el código de 6 dígitos de tu aplicación autenticadora.",
      verify: "Verificar e ingresar",
      cancel: "Cancelar y salir",
      paste: "Pegar código",
      digit: "Dígito",
      of: "de",
      errors: {
        invalidCode: "Código inválido. Revísalo e inténtalo de nuevo.",
        tooManyAttempts: "Demasiados intentos. Inténtalo de nuevo más tarde.",
        generic: "No fue posible verificar el código. Inténtalo de nuevo.",
      },
    },
    noTenant: {
      pageTitle: "Esperando Activación | TradeRisk360",
      title: "Cuenta pendiente",
      description:
        "Tu cuenta aún no está asociada a una empresa. Contacta al soporte para la activación.",
      logoutButton: "Salir e intentar otra cuenta",
      backToSite: "Volver al sitio",
    },
    resetPassword: {
      pageTitle: "Restablecer Contraseña | TradeRisk360",
      invalidLinkTitle: "Enlace Inválido",
      invalidLinkPageTitle: "Enlace Inválido | TradeRisk360",
      invalidLinkDescription:
        "El enlace de recuperación de contraseña expiró o es inválido. Solicita uno nuevo.",
      backToLogin: "Volver al Login",
      successPageTitle: "Contraseña Cambiada | TradeRisk360",
      successTitle: "¡Contraseña Cambiada!",
      successDescription:
        "Tu contraseña fue restablecida con éxito. Serás redirigido al login.",
      title: "Restablecer Contraseña",
      subtitle: "Ingresa tu nueva contraseña abajo",
      newPassword: "Nueva Contraseña",
      confirmPassword: "Confirmar Nueva Contraseña",
      submit: "Cambiar Contraseña",
      submitting: "Cambiando...",
      errors: {
        passwordMismatch: "Las contraseñas no coinciden",
        passwordTooShort: "La contraseña debe tener al menos 6 caracteres",
        unexpected:
          "Ocurrió un error al restablecer la contraseña. Intenta de nuevo.",
        error: "Error",
      },
      success: {
        title: "¡Éxito!",
        description: "Tu contraseña fue cambiada con éxito.",
      },
    },
    signup: {
      pageTitle: "Crear Cuenta | TradeRisk360",
      steps: {
        cnpj: "ID Fiscal",
        company: "Empresa",
        user: "Usuario",
        terms: "Términos",
      },
      step1: {
        title: "Validación de ID Fiscal",
        subtitle: "Ingresa el ID fiscal de tu empresa para comenzar",
        cnpjLabel: "ID fiscal de la empresa",
        validateButton: "Validar",
        cnpjValid: "ID Fiscal Válido",
        tradeName: "Nombre comercial:",
        errors: {
          invalid: "ID fiscal inválido - verifica los números ingresados",
          required: "El ID fiscal debe tener 14 dígitos",
          notFound: "ID fiscal no encontrado",
          inactive: "La empresa no está activa",
          validationError: "Error al validar. Intenta de nuevo.",
          validateFirst: "Valida el ID fiscal antes de continuar",
        },
        success: {
          title: "¡ID fiscal validado!",
          company: "Empresa:",
        },
      },
      step2: {
        title: "Calificación",
        subtitle: "Ayúdanos a entender mejor tu operación",
        companyName: "Razón Social",
        tradeName: "Nombre Comercial",
        tradeNamePlaceholder: "Nombre comercial de la empresa",
        mainActivity: "Actividad Principal",
        phone: "Teléfono",
        phonePlaceholder: "(00) 0000-0000",
        zipCode: "Código Postal",
        address: "Dirección",
        number: "Número",
        complement: "Complemento",
        neighborhood: "Barrio",
        city: "Ciudad",
        state: "Estado",
      },
      step3: {
        title: "Tus Datos",
        subtitle: "Completa tus datos personales para acceder",
        fullName: "Nombre completo",
        fullNamePlaceholder: "Juan García",
        email: "Correo corporativo",
        emailPlaceholder: "juan@tuempresa.com",
        emailHint:
          "Usa el correo de tu empresa. Correos personales como Gmail, Hotmail, Yahoo no están permitidos.",
        emailBlockedError:
          "Por favor, usa un correo corporativo. Los correos personales (Gmail, Hotmail, etc.) no están permitidos.",
        position: "Cargo/Posición",
        positionPlaceholder: "Ej: Gerente de Crédito",
        password: "Contraseña",
        confirmPassword: "Confirmar contraseña",
      },
      step4: {
        title: "Calificación y Términos",
        subtitle: "Finaliza tu registro",
        operationSection: "Sobre tu operación",
        isExporter: "¿Tu empresa exporta?",
        exporterOptions: {
          yes: "Sí",
          no: "No",
          planning: "Planeando",
        },
        yearsExporting: "¿Hace cuántos años exporta?",
        yearsOptions: {
          lessThan1: "Menos de 1 año",
          oneToThree: "1 a 3 años",
          threeToFive: "3 a 5 años",
          fiveToTen: "5 a 10 años",
          moreThan10: "Más de 10 años",
        },
        paymentTerms: "Plazos de pago",
        paymentOptions: {
          advance: "Anticipado",
          days30: "30 días",
          days60: "60 días",
          days90: "90 días",
          days120plus: "120+ días",
        },
        transportModals: "Modos de transporte utilizados",
        modals: {
          maritime: "Marítimo",
          air: "Aéreo",
          road: "Terrestre",
          rail: "Ferroviario",
        },
        mainMarkets: "Principales mercados",
        markets: {
          latam: "América Latina",
          usa: "Estados Unidos",
          europe: "Europa",
          asia: "Asia",
          africa: "África",
          oceania: "Oceanía",
        },
        termsAccept: "He leído y acepto los",
        termsOfUse: "Términos de Uso",
        and: "y el",
        serviceAgreement: "Contrato de Prestación de Servicios",
        privacyAccept: "He leído y acepto la",
        privacyPolicy: "Política de Privacidad",
        summary: "Resumen del registro",
        company: "Empresa:",
        cnpj: "ID Fiscal:",
        name: "Nombre:",
        email: "Correo:",
        plan: "Plan:",
        trialPlan: "Trial (14 días gratis)",
        submitButton: "Crear mi cuenta",
        submitting: "Creando cuenta...",
      },
      common: {
        back: "Volver",
        next: "Siguiente",
        select: "Selecciona",
      },
      success: {
        title: "¡Cuenta creada con éxito!",
        description: "Redirigiendo a tu panel...",
      },
      errors: {
        createAccount: "Error al crear cuenta",
        unexpected: "Ocurrió un error. Intenta de nuevo.",
      },
      footer: {
        hasAccount: "¿Ya tienes una cuenta?",
        login: "Iniciar sesión",
      },
    },
  },
};

// Auth translations - EN
const auth_en = {
  auth: {
    login: {
      pageTitle: "Login | TradeRisk360",
      metaDescription:
        "Login TradeRisk360 - Access the credit risk monitoring panel for your company.",
      title: "Login",
      subtitle: "Access your TradeRisk360 account",
      email: "Work email",
      emailPlaceholder: "you@company.com",
      password: "Password",
      passwordPlaceholder: "••••••••",
      forgotPassword: "Forgot my password",
      submit: "Login",
      submitting: "Logging in...",
      noAccount: "No access yet?",
      requestAccess: "Request access",
      backToSite: "Back to site",
      otherOptions: "Other options",
      errors: {
        invalidCredentials: "Incorrect email or password",
        tooManyAttempts: "Too many attempts. Please try again later.",
        invalidEmail: "Invalid email.",
        invalidPassword: "Invalid password.",
        unexpected: "An unexpected error occurred. Please try again.",
        loginError: "Login error",
        error: "Error",
      },
      success: {
        title: "Login successful!",
        redirecting: "Redirecting...",
      },
      sessionExpired: "Your session has expired. Please sign in again.",
      showPassword: "Show password",
      hidePassword: "Hide password",
      show: "Show",
      hide: "Hide",
      passwordHelp: "Check capitalization and Caps Lock.",
      security: "Secure connection · two-step verification · LGPD",
      forgot: {
        title: "Reset password",
        subtitle: "Enter your account email. We send a link valid for 1 hour.",
        send: "Send reset link",
        sending: "Sending...",
        help: "Do not remember the email? Talk to your organization administrator.",
        back: "Back to login",
      },
      sent: {
        title: "Check your email",
        description:
          "If an account exists for {email}, the link will arrive in a few minutes. Check spam too.",
        openMail: "Open email app",
        resendIn: "Did not receive it? Resend in {time}",
        resend: "Resend",
      },
      forgotPasswordModal: {
        enterEmail: "Enter your email",
        enterEmailDescription: "Enter your email to recover your password",
        emailSent: "Email sent!",
        emailSentDescription: "Check your inbox to reset your password.",
        errorSending: "Could not send recovery email",
      },
    },
    verify: {
      pageTitle: "Verify Email | TradeRisk360",
      title: "Account created!",
      description:
        "Check your email to confirm your registration and then log in.",
      loginButton: "Login",
    },
    mfa: {
      title: "Two-step verification",
      subtitle: "Enter the 6-digit code from your authenticator app.",
      verify: "Verify and sign in",
      cancel: "Cancel and sign out",
      paste: "Paste code",
      digit: "Digit",
      of: "of",
      errors: {
        invalidCode: "Invalid code. Check it and try again.",
        tooManyAttempts: "Too many attempts. Please try again later.",
        generic: "Could not verify the code. Please try again.",
      },
    },
    noTenant: {
      pageTitle: "Awaiting Activation | TradeRisk360",
      title: "Account pending",
      description:
        "Your account is not yet associated with a company. Contact support for activation.",
      logoutButton: "Log out and try another account",
      backToSite: "Back to site",
    },
    resetPassword: {
      pageTitle: "Reset Password | TradeRisk360",
      invalidLinkTitle: "Invalid Link",
      invalidLinkPageTitle: "Invalid Link | TradeRisk360",
      invalidLinkDescription:
        "The password recovery link has expired or is invalid. Request a new link.",
      backToLogin: "Back to Login",
      successPageTitle: "Password Changed | TradeRisk360",
      successTitle: "Password Changed!",
      successDescription:
        "Your password has been successfully reset. You will be redirected to login.",
      title: "Reset Password",
      subtitle: "Enter your new password below",
      newPassword: "New Password",
      confirmPassword: "Confirm New Password",
      submit: "Change Password",
      submitting: "Changing...",
      errors: {
        passwordMismatch: "Passwords do not match",
        passwordTooShort: "Password must be at least 6 characters",
        unexpected:
          "An error occurred while resetting the password. Please try again.",
        error: "Error",
      },
      success: {
        title: "Success!",
        description: "Your password has been successfully changed.",
      },
    },
    signup: {
      pageTitle: "Create Account | TradeRisk360",
      steps: {
        cnpj: "Tax ID",
        company: "Company",
        user: "User",
        terms: "Terms",
      },
      step1: {
        title: "Tax ID Validation",
        subtitle: "Enter your company tax ID to get started",
        cnpjLabel: "Company Tax ID",
        validateButton: "Validate",
        cnpjValid: "Tax ID Valid",
        tradeName: "Trade name:",
        errors: {
          invalid: "Invalid Tax ID - please check the numbers entered",
          required: "Tax ID must have 14 digits",
          notFound: "Tax ID not found",
          inactive: "Company is not active",
          validationError: "Error validating. Please try again.",
          validateFirst: "Validate the Tax ID before continuing",
        },
        success: {
          title: "Tax ID validated!",
          company: "Company:",
        },
      },
      step2: {
        title: "Qualification",
        subtitle: "Help us understand your business better",
        companyName: "Legal Name",
        tradeName: "Trade Name",
        tradeNamePlaceholder: "Company trade name",
        mainActivity: "Main Activity",
        phone: "Phone",
        phonePlaceholder: "(00) 0000-0000",
        zipCode: "Zip Code",
        address: "Address",
        number: "Number",
        complement: "Complement",
        neighborhood: "Neighborhood",
        city: "City",
        state: "State",
      },
      step3: {
        title: "Your Data",
        subtitle: "Fill in your personal data to access",
        fullName: "Full name",
        fullNamePlaceholder: "John Smith",
        email: "Corporate email",
        emailPlaceholder: "john@yourcompany.com",
        emailHint:
          "Use your company email. Personal emails like Gmail, Hotmail, Yahoo are not allowed.",
        emailBlockedError:
          "Please use a corporate email. Personal emails (Gmail, Hotmail, etc.) are not allowed.",
        position: "Position/Role",
        positionPlaceholder: "Ex: Credit Manager",
        password: "Password",
        confirmPassword: "Confirm password",
      },
      step4: {
        title: "Qualification and Terms",
        subtitle: "Complete your registration",
        operationSection: "About your operation",
        isExporter: "Does your company export?",
        exporterOptions: {
          yes: "Yes",
          no: "No",
          planning: "Planning",
        },
        yearsExporting: "How many years exporting?",
        yearsOptions: {
          lessThan1: "Less than 1 year",
          oneToThree: "1 to 3 years",
          threeToFive: "3 to 5 years",
          fiveToTen: "5 to 10 years",
          moreThan10: "More than 10 years",
        },
        paymentTerms: "Payment terms",
        paymentOptions: {
          advance: "Advance",
          days30: "30 days",
          days60: "60 days",
          days90: "90 days",
          days120plus: "120+ days",
        },
        transportModals: "Transport modes used",
        modals: {
          maritime: "Maritime",
          air: "Air",
          road: "Road",
          rail: "Rail",
        },
        mainMarkets: "Main markets",
        markets: {
          latam: "Latin America",
          usa: "United States",
          europe: "Europe",
          asia: "Asia",
          africa: "Africa",
          oceania: "Oceania",
        },
        termsAccept: "I have read and accept the",
        termsOfUse: "Terms of Use",
        and: "and the",
        serviceAgreement: "Service Agreement",
        privacyAccept: "I have read and accept the",
        privacyPolicy: "Privacy Policy",
        summary: "Registration summary",
        company: "Company:",
        cnpj: "Tax ID:",
        name: "Name:",
        email: "Email:",
        plan: "Plan:",
        trialPlan: "Trial (14 days free)",
        submitButton: "Create my account",
        submitting: "Creating account...",
      },
      common: {
        back: "Back",
        next: "Next",
        select: "Select",
      },
      success: {
        title: "Account created successfully!",
        description: "Redirecting to your dashboard...",
      },
      errors: {
        createAccount: "Error creating account",
        unexpected: "An error occurred. Please try again.",
      },
      footer: {
        hasAccount: "Already have an account?",
        login: "Login",
      },
    },
  },
};

// Merge auth translations into main translations
(translations.pt as any).companyDetail = {
  tabs: {
    overview: "Visão geral",
    registration: "Cadastro",
    creditAnalysis: "Análise de crédito",
    financial: "Financeiro",
    exposureRisk: "Exposição e risco",
    alerts: "Alertas",
    partners: "Sócios e grupo",
    changes: "Alterações",
    aiAnalysis: "Análise IA",
    registrationDetails: "Cadastro",
    contacts: "Contatos",
    customerHistory: "Histórico do Cliente",
    scores: "Score's",
    creditRecommendation: "Rec. Crédito Coface",
    paymentIndex: "Índice de Pagamento",
    evolution: "Evolução",
    financialCoface: "Financeiro Coface",
    financialStatements: "Demonstrativos Financeiros",
    activity: "Atividade",
    sectorCountryRisk: "Risco Setor/País Coface",
    kyc: "KYC",
    protests: "Protestos/Outros",
    processes: "Processos",
    media: "Mídia e reputação",
    partnersSub: "Sócios",
    cofacePartners: "Sócios Coface",
    economicGroup: "Grupo Econômico",
    financialAiAnalysis: "Análise Financeira IA",
  },
  header: {
    back: "Voltar para lista",
    downloadInsights: "Baixar PDF Insights",
    requestAnalysis: "Solicitar análise",
    comingSoon: "Em breve",
    registeredAt: "cadastrada em",
    buyer: "Comprador",
    supplier: "Fornecedor",
    monitoring: "Em monitoramento",
    oneRecentChange: "1 alteração recente",
    recentChanges: "{count} alterações recentes",
    enrichCompany: "Buscar dados na Coface",
    contactSupport: "Contatar Suporte",
    aiUnavailable: "A análise com IA não está habilitada para sua organização.",
    processingWait: "Aguarde a conclusão do processamento do relatório.",
    reportPdfError: "Não foi possível gerar o relatório. Tente novamente.",
    reportPdfRateLimited:
      "Muitas exportações em pouco tempo. Aguarde alguns instantes.",
  },
};
(translations.es as any).companyDetail = {
  tabs: {
    overview: "Vista general",
    registration: "Registro",
    creditAnalysis: "Análisis de crédito",
    financial: "Financiero",
    exposureRisk: "Exposición y riesgo",
    alerts: "Alertas",
    partners: "Socios y grupo",
    changes: "Cambios",
    aiAnalysis: "Análisis IA",
    registrationDetails: "Registro",
    contacts: "Contactos",
    customerHistory: "Historial del cliente",
    scores: "Puntuaciones",
    creditRecommendation: "Rec. crédito Coface",
    paymentIndex: "Índice de pago",
    evolution: "Evolución",
    financialCoface: "Financiero Coface",
    financialStatements: "Estados financieros",
    activity: "Actividad",
    sectorCountryRisk: "Riesgo sector/país Coface",
    kyc: "KYC",
    protests: "Protestas/otros",
    processes: "Procesos",
    media: "Medios y reputación",
    partnersSub: "Socios",
    cofacePartners: "Socios Coface",
    economicGroup: "Grupo económico",
    financialAiAnalysis: "Análisis financiero IA",
  },
  header: {
    back: "Volver a la lista",
    downloadInsights: "Descargar PDF Insights",
    requestAnalysis: "Solicitar análisis",
    comingSoon: "Próximamente",
    registeredAt: "registrada el",
    buyer: "Comprador",
    supplier: "Proveedor",
    monitoring: "En monitoreo",
    oneRecentChange: "1 cambio reciente",
    recentChanges: "{count} cambios recientes",
    enrichCompany: "Buscar datos en Coface",
    contactSupport: "Contactar soporte",
    aiUnavailable:
      "El análisis con IA no está habilitado para su organización.",
    processingWait: "Espere a que finalice el procesamiento del informe.",
    reportPdfError: "No fue posible generar el informe. Inténtelo de nuevo.",
    reportPdfRateLimited:
      "Demasiadas exportaciones en poco tiempo. Espere unos instantes.",
  },
};
(translations.en as any).companyDetail = {
  ...(translations.en as any).companyDetail,
  tabs: {
    overview: "Overview",
    registration: "Registration",
    creditAnalysis: "Credit analysis",
    financial: "Financial",
    exposureRisk: "Exposure and risk",
    alerts: "Alerts",
    partners: "Partners and group",
    changes: "Changes",
    aiAnalysis: "AI analysis",
    registrationDetails: "Registration",
    contacts: "Contacts",
    customerHistory: "Customer history",
    scores: "Scores",
    creditRecommendation: "Coface credit rec.",
    paymentIndex: "Payment index",
    evolution: "Evolution",
    financialCoface: "Coface financials",
    financialStatements: "Financial statements",
    activity: "Activity",
    sectorCountryRisk: "Coface sector/country risk",
    kyc: "KYC",
    protests: "Protests/other",
    processes: "Legal proceedings",
    media: "Media and reputation",
    partnersSub: "Partners",
    cofacePartners: "Coface partners",
    economicGroup: "Economic group",
    financialAiAnalysis: "AI financial analysis",
  },
  header: {
    back: "Back to list",
    downloadInsights: "Download Insights PDF",
    requestAnalysis: "Request analysis",
    comingSoon: "Coming soon",
    registeredAt: "registered on",
    buyer: "Buyer",
    supplier: "Supplier",
    monitoring: "Monitoring active",
    oneRecentChange: "1 recent change",
    recentChanges: "{count} recent changes",
    enrichCompany: "Get Coface data",
    contactSupport: "Contact support",
    aiUnavailable: "AI analysis is not enabled for your organization.",
    processingWait: "Wait for report processing to finish.",
    reportPdfError: "The report could not be generated. Please try again.",
    reportPdfRateLimited:
      "Too many exports in a short time. Please wait a moment.",
  },
};

const financialTabPt: FinancialTabTranslations = {
  sections: "Seções da aba Financeiro",
  listAnd: "e",
  anchors: {
    summary: "Resumo",
    indicators: "Indicadores",
    statements: "Demonstrativos",
    cofaceStatements: "Demonstrativos Coface",
    sourcesAndUploads: "Fontes e envios",
    sources: "Fontes",
  },
  sourceText: {
    balances: "Fontes: balanços enviados (IA)",
    manual: "Fontes: balanços enviados",
    coface: "Fonte: Coface URBA · consulta {date}",
    both: "Fontes: balanços enviados (IA) · Coface URBA",
    cofaceNoDate: "Fonte: Coface URBA",
  },
  hero: {
    healthTitle: "Saúde financeira · {year}",
    healthTitleNoYear: "Saúde financeira",
    cofaceTitle: "Dados financeiros · {year}",
    redFlags: "⚑ {n} red flags",
    redFlag: "⚑ 1 red flag",
    origin: "Origem dos dados ({n})",
    upload: "Enviar balanço",
    cofacePill: "Coface URBA · {currency}",
    lastYears: "Últimos {n} anos disponíveis · consulta {date}",
    lastYear: "Último ano disponível · consulta {date}",
    base: "Base desta análise",
    baseUpload: "{type} {year} extraído por IA em {date}",
    baseManual: "{type} {year} preenchido manualmente em {date}",
    baseNoDate: "{type} {year}",
    baseTypes: {
      full: "Balanço + DRE",
      bpOnly: "Apenas BP",
      dreOnly: "Apenas DRE",
    },
    comparedWith: " · comparado com {years}",
    baseCoface: "Demonstrativos publicados pela Coface em {currency}.",
    cofaceNoDataTitle: "Coface: sem dados financeiros",
    cofaceNoDataText:
      "Comum em empresas de capital fechado no Brasil. Por isso os indicadores usam os balanços enviados.",
    cofacePendingTitle: "Coface: dados financeiros em preparação",
    cofacePendingDate: "Previsão de entrega em {date}.",
    cofacePendingNoDate: "A Coface ainda está processando o relatório.",
    cofaceNotConsultedTitle: "Coface: não consultada",
    cofaceNotConsultedText:
      "Solicite uma análise para incluir os dados financeiros da Coface.",
    cofaceAvailableTitle: "Coface: dados financeiros disponíveis",
    cofaceAvailableText:
      "Demonstrativos {years} em {currency}. Veja a tabela Coface abaixo.",
    uploadUnavailableTitle: "Upload de balanço indisponível",
    uploadUnavailableText:
      "O upload e a entrada manual de balanço estão disponíveis apenas para empresas com CNPJ (Brasil).",
    uploadAvailableTitle: "Complete com o balanço da empresa",
    uploadAvailableText:
      "Envie o balanço ou a DRE para calcular todos os indicadores e red flags.",
  },
  kpi: {
    netRevenue: "Receita líquida",
    grossRevenue: "Receita bruta",
    operatingRevenue: "Receita operacional",
    netIncome: "Lucro líquido",
    totalAssets: "Total do ativo",
    equity: "Patrimônio líquido",
    debt: "Dívida (CP + LP)",
    bankDebt: "Dívida bancária (CP + LP)",
    netMargin: "Margem líquida",
    cash: "Caixa e equivalentes",
    vs: "{arrow} {value} vs {year}",
    vsPp: "{arrow} {value} p.p. vs {year}",
  },
  health: {
    excellent: "Excelente",
    good: "Boa",
    acceptable: "Aceitável",
    weak: "Fraca",
    critical: "Crítica",
    attention: "Atenção",
  },
  summary: {
    healthy: "Indicadores dentro da referência em {year}.",
    noData: "Ainda não há dados financeiros desta empresa.",
    positives: {
      profitability: "rentabilidade positiva",
      liquidity: "liquidez corrente saudável",
      debt: "endividamento controlado",
    },
    attentionSentence: "Atenção {items}.",
    attention: {
      liquidez_corrente: "à liquidez corrente",
      liquidez_seca: "à liquidez seca",
      liquidez_geral: "à liquidez geral",
      capacidade_pagamento: "à capacidade de pagamento",
      endividamento_geral: "ao endividamento geral",
      endividamento_cp: "à concentração de dívida no curto prazo",
      debt_equity: "à relação entre dívidas e patrimônio",
      rotacao_ativo: "à rotação do ativo",
      roa: "ao retorno sobre o ativo (ROA)",
      roe: "ao retorno sobre o patrimônio (ROE)",
      margem_liquida: "à margem líquida",
      margem_operacional: "à margem operacional",
      redFlags: "aos red flags detectados",
    },
    cofaceNoComparison: "Sem comparação de receita em {year}.",
    cofaceRevenue: {
      up: "A receita cresce",
      down: "A receita cai",
      stable: "A receita fica estável",
    },
    cofaceClause: {
      butFall: ", mas {items} caem",
      butFallOne: ", mas {items} cai",
      butRise: ", mas {items} sobem",
      butRiseOne: ", mas {items} sobe",
      alsoFall: ", e {items} também caem",
      alsoFallOne: ", e {items} também cai",
      alsoRise: ", e {items} também sobem",
      alsoRiseOne: ", e {items} também sobe",
    },
    cofaceItems: { margin: "margem", cash: "caixa" },
    cofaceDebt: {
      shortTerm: "A dívida de curto prazo subiu {pct}% em {year}.",
      longTerm: "A dívida de longo prazo subiu {pct}% em {year}.",
    },
  },
  indicators: {
    title: "Indicadores financeiros",
    titleCoface: "Indicadores calculados",
    yearSuffix: "— {year}",
    yearSelector: "Ano dos indicadores",
    cofacePill: "Calculado pelo TR360 a partir da Coface",
    cofaceNote:
      "Estes indicadores usam as mesmas regras de referência dos balanços enviados no Brasil.",
    legend: {
      within: "Dentro da referência",
      attention: "Atenção",
      outside: "Fora da referência",
      noData: "Sem dados",
    },
    groups: {
      liquidity: "LIQUIDEZ",
      debt: "ENDIVIDAMENTO",
      profitability: "RENTABILIDADE",
    },
    groupStatus: {
      healthy: "Saudável",
      moderateRisk: "Risco moderado",
      criticalAttention: "Atenção crítica",
      controlled: "Controlado",
      mediumRisk: "Médio risco",
      highRisk: "Alto risco",
      positive: "Positiva",
      negative: "Negativa",
      noData: "Sem dados",
    },
    names: {
      liquidez_corrente: "LIQUIDEZ CORRENTE",
      liquidez_seca: "LIQUIDEZ SECA",
      liquidez_geral: "LIQUIDEZ GERAL",
      capacidade_pagamento: "CAP. PAGAMENTO",
      endividamento_geral: "ENDIV. GERAL",
      endividamento_cp: "ENDIV. CP",
      debt_equity: "DEBT / EQUITY",
      rotacao_ativo: "ROTAÇÃO DO ATIVO",
      roa: "ROA",
      roe: "ROE",
      margem_liquida: "MARGEM LÍQUIDA",
      margem_operacional: "MARGEM OPERACIONAL",
    },
    reference: {
      minIdeal: "Mínimo > {min} · ideal > {ideal}",
      limitIdeal: "Limite < {max} · ideal < {ideal}",
    },
    plain: {
      currentRatio:
        "{m} {v} de ativo circulante para cada {m} {one} a pagar em até 12 meses.",
      quickRatio: "Sem contar estoques, cobre {p}% das dívidas de curto prazo.",
      generalLiquidity:
        "Os ativos cobrem {p}% de todas as dívidas (curto e longo prazo).",
      paymentCapacity:
        "O resultado operacional cobre {p}% das dívidas de curto prazo.",
      paymentCapacityNegative:
        "Resultado operacional negativo: não gera caixa para as dívidas de curto prazo.",
      totalDebt: "{p}% do ativo é financiado por terceiros.",
      shortTermDebt:
        "{p}% do ativo está comprometido com dívidas de até 12 meses.",
      debtEquity: "{m} {v} de dívidas para cada {m} {one} de patrimônio.",
      debtEquityNegativeEquity: "Patrimônio líquido negativo.",
      assetTurnover: "Cada {m} {one} de ativo gerou {m} {v} de receita no ano.",
      roa: "Lucro de {m} {v} para cada {m} 100 de ativos.",
      roaNegative: "Prejuízo de {m} {v} para cada {m} 100 de ativos.",
      roe: "Retorno de {p}% sobre o capital dos sócios.",
      roeNegative: "Prejuízo de {p}% sobre o capital dos sócios.",
      netMargin: "Sobram {m} {v} de lucro a cada {m} 100 vendidos.",
      netMarginNegative: "Prejuízo de {m} {v} a cada {m} 100 vendidos.",
      netMarginWithPrevious:
        "Sobram {m} {v} de lucro a cada {m} 100 vendidos ({prev}% em {prevYear}).",
      netMarginNegativeWithPrevious:
        "Prejuízo de {m} {v} a cada {m} 100 vendidos ({prev}% em {prevYear}).",
      operatingMargin:
        "Operação gera {m} {v} de lucro a cada {m} 100 vendidos, antes de juros e impostos.",
      operatingMarginNegative:
        "Operação dá prejuízo de {m} {v} a cada {m} 100 vendidos, antes de juros e impostos.",
    },
    noDataUpload: "Sem dados no documento enviado.",
    noDataCoface: "Sem dados na Coface.",
    barLabel: "{name}: {value}, {state}",
  },
  redFlags: {
    title: "⚑ RED FLAGS DETECTADOS ({n})",
    severity: { high: "ALTA", medium: "MÉDIA", low: "BAIXA" },
  },
  revenue: {
    title: "Receita por ano",
    statNet: "Receita líquida {year}",
    statGross: "Receita bruta {year}",
    statOperating: "Receita operacional {year}",
    cagr: "{value}% a.a.",
    ll: "LL {value} · {margin}%",
    llNoMargin: "LL {value}",
    llEmpty: "LL —",
    million: "mi",
    legend: {
      net: "Receita líquida",
      gross: "Receita bruta",
      deductions: "Deduções (receita bruta − líquida)",
      ll: "LL = lucro líquido · margem",
      operating: "Receita operacional",
      cofaceCurrency: "Valores em {currency} (Coface)",
      otherBasis: {
        net: "líquida = ano com receita líquida",
        gross: "bruta = ano só com receita bruta (sem líquida informada)",
      },
    },
    basisTag: { net: "líquida", gross: "bruta" },
    empty: "Receita não informada nos dados disponíveis.",
    chartLabel: "Receita por ano. {items}",
    barLabel:
      "{year}: receita {revenue}, deduções {deductions}, lucro líquido {netIncome}",
    barLabelNoDeductions:
      "{year}: receita {revenue}, lucro líquido {netIncome}",
  },
  monthly: {
    title: "Faturamento mensal · {year}",
    titleEmpty: "Faturamento mensal",
    stat: "Receita bruta no ano",
    months: "{n} meses",
    monthsOne: "1 mês",
    peak: "Pico: {month}",
    average: "média {value}",
    monthNames: {
      jan: "Jan",
      fev: "Fev",
      mar: "Mar",
      abr: "Abr",
      mai: "Mai",
      jun: "Jun",
      jul: "Jul",
      ago: "Ago",
      set: "Set",
      out: "Out",
      nov: "Nov",
      dez: "Dez",
    },
    footer:
      "Fonte: faturamento mensal informado no balanço enviado. A linha tracejada é a média do ano.",
    emptyTitle: "Faturamento mensal não informado",
    emptyText:
      "Envie um balancete ou declaração de faturamento com os valores por mês.",
    chartLabel:
      "Faturamento mensal de {year}: total {total} em {n} meses, pico em {month} com {peak}, média {average}.",
  },
  statements: {
    title: "Demonstrativos financeiros",
    titleCoface: "Dados financeiros Coface (últimos 3 anos)",
    origin: "⟲ Origem ({n})",
    unitSelector: "Unidade dos valores",
    unit: { brlMi: "R$ mi", brl: "R$", currencyMi: "{currency} mi" },
    intro:
      "Valores em R$ milhões. A variação compara com o ano anterior. Linhas sem valor no documento ficam ocultas.",
    introUnit:
      "Valores em reais. A variação compara com o ano anterior. Linhas sem valor no documento ficam ocultas.",
    introCoface:
      "Valores em {symbol} milhões, como publicados pela Coface. A variação compara com o ano anterior; verde = melhora, vermelho = piora.",
    introCofaceConverted:
      "Valores em R$ milhões, convertidos pela cotação do dia. A variação compara com o ano anterior; verde = melhora, vermelho = piora.",
    dreHeader: "DEMONSTRAÇÃO DE RESULTADOS (DRE)",
    dreHeaderCoface: "DEMONSTRAÇÃO DE RESULTADOS",
    bpHeader: "BALANÇO PATRIMONIAL",
    caption: "{title}, valores em {unit}",
    sections: {
      assets: "ATIVO",
      liabilitiesEquity: "PASSIVO E PL",
      liabilities: "PASSIVO",
      equity: "PATRIMÔNIO LÍQUIDO",
    },
    rows: {
      grossRevenue: "Receita bruta",
      deductions: "(−) Deduções da receita",
      netRevenue: "Receita líquida",
      costProducts: "(−) Custo dos produtos (CMV)",
      costServices: "(−) Custo dos serviços",
      costGoods: "(−) Custo das mercadorias",
      grossProfit: "Lucro bruto",
      operatingExpenses: "(−) Despesas operacionais",
      ebit: "Resultado operacional (EBIT)",
      financialRevenue: "(+) Receitas financeiras",
      financialExpenses: "(−) Despesas financeiras",
      nonOperatingRevenue: "(+) Receitas não operacionais",
      ebt: "Resultado antes do IR (LAIR)",
      incomeTax: "(−) IR e CSLL",
      netIncome: "Lucro líquido",
      cash: "Caixa e equivalentes",
      receivables: "Contas a receber",
      inventories: "Estoques",
      currentAssetsOther: "Outros (circulante)",
      totalCurrentAssets: "Total ativo circulante",
      longTermReceivables: "Realizável a longo prazo",
      investments: "Investimentos",
      fixedAssets: "Imobilizado",
      intangibles: "Intangível",
      nonCurrentAssetsOther: "Outros (não circulante)",
      totalNonCurrentAssets: "Total ativo não circulante",
      totalAssets: "Total ativo",
      suppliers: "Fornecedores",
      shortTermLoans: "Empréstimos curto prazo",
      taxesPayable: "Impostos a pagar",
      currentLiabilitiesOther: "Outros (circulante)",
      totalCurrentLiabilities: "Total passivo circulante",
      longTermLoans: "Empréstimos longo prazo",
      otherObligations: "Outras obrigações",
      totalNonCurrentLiabilities: "Total passivo não circulante",
      totalLiabilities: "Total passivo",
      shareCapital: "Capital social",
      reserves: "Reservas",
      retainedEarnings: "Lucros acumulados",
      equity: "Patrimônio líquido",
      operatingRevenue: "Receita operacional",
      costOfSales: "Custos de vendas",
      cofaceOperatingExpenses: "Despesas operacionais",
      operatingProfit: "Lucro operacional",
      tradeDebtors: "Clientes a receber",
      totalFixedAssets: "Total ativo imobilizado",
      shortTermDebt: "Dívida curto prazo",
      longTermDebt: "Dívida longo prazo",
      totalEquity: "Total patrimônio líquido",
    },
    variation: {
      up: "alta de {value}",
      down: "queda de {value}",
      flat: "sem variação",
    },
    footer: "Balanço consolidado · referência {year}",
    showAll: "Ver todas as linhas (outros ativos, reservas…) ⌄",
    showLess: "Ver menos ⌃",
  },
  sources: {
    title: "Fontes dos dados financeiros",
    titleCoface: "Fontes dos dados",
    description:
      "Cada envio é extraído por IA e mesclado ao ano de referência. Envios de BP e DRE separados se completam automaticamente.",
    uploadName: "Documento enviado",
    manualName: "Entrada manual",
    sentOn: "{type} · enviado em {date}",
    sentOnNoType: "Enviado em {date}",
    sentBy: "{text} por {name}",
    noDate: "Data de envio não informada",
    types: {
      full: "Balanço + DRE",
      bpOnly: "Apenas BP",
      dreOnly: "Apenas DRE",
    },
    year: "Ano {year}",
    status: {
      extracted: "✓ Extraído por IA",
      merged: "✓ Mesclado ao {side} {year}",
      manual: "✓ Preenchido manualmente",
      none: "Nenhum dado identificado",
    },
    showAll: "Ver todos os envios ({n})",
    showLess: "Ver menos",
    menu: {
      trigger: "Opções do envio",
      details: "Ver detalhes",
      replace: "Substituir",
    },
    details: {
      title: "Detalhes do envio",
      origin: "Origem",
      type: "Tipo",
      year: "Ano",
      sentAt: "Enviado em",
      status: "Status",
      sections: "Seções atualizadas",
      close: "Fechar",
    },
    coface: {
      title: "Coface URBA · Dados financeiros",
      noData:
        "Informações financeiras essenciais não disponíveis nas fontes da Coface para esta empresa. Consulta de {date}.",
      noDataNoDate:
        "Informações financeiras essenciais não disponíveis nas fontes da Coface para esta empresa.",
      withData:
        "Demonstrativos {years} em {currency}. Atualizados na consulta de {date}.",
      withDataNoDate: "Demonstrativos {years} em {currency}.",
      pending: "Previsão de entrega em {date}.",
      pendingNoDate: "A Coface ainda está processando o relatório.",
      notConsulted:
        "Solicite uma análise para incluir os dados financeiros da Coface.",
      pillNoData: "Sem dados",
      pillWithData: "✓ Com dados",
      pillPending: "Em preparação",
      pillNotConsulted: "Não consultada",
    },
    uploads: {
      title: "Balanços enviados",
      unavailableText:
        "Upload (PDF/XLSX) e entrada manual disponíveis apenas para empresas com CNPJ.",
      availableText: "Upload (PDF/XLSX) ou entrada manual do balanço e da DRE.",
      restrictedText:
        "Balanços enviados disponíveis apenas para Owner, Admin e Colaborador.",
      errorText: "Não foi possível carregar os balanços enviados.",
      retry: "Tentar novamente",
      pillUnavailable: "Indisponível",
      pillNone: "Nenhum envio",
      link: "Enviar balanço →",
    },
    tip: {
      title: "Precisa de dados mais recentes?",
      text: "Solicite uma nova análise: a Coface atualiza os demonstrativos quando a empresa publica novos balanços.",
    },
  },
  upload: {
    title: "Enviar balanço",
    cnpjOnly: "Somente CNPJ (BR)",
    docType: "Tipo de documento",
    types: {
      balanco_completo: "Balanço + DRE",
      balancete: "Balancete",
      balanco_patrimonial: "Apenas BP",
      dre: "Apenas DRE",
    },
    year: "Ano de referência",
    drop: "Arraste o PDF ou a planilha aqui",
    limits: "PDF até 10 MB · XLSX/XLS até 5 MB",
    select: "Selecionar arquivo",
    change: "Trocar arquivo",
    fileInput: "Arquivo do balanço (PDF, XLSX ou XLS)",
    howTitle: "Como funciona",
    howText:
      "1. A IA lê o documento e preenche BP e DRE.  2. Conferimos se ativo = passivo + PL e avisamos inconsistências.  3. Os indicadores e red flags são recalculados.",
    analyze: "Analisar balanço",
    overwrite: "Já existe balanço de {year}. O envio atualiza esse registro.",
    manual: "Prefere digitar? Preencher manualmente →",
    errors: {
      type: "Formato não aceito. Envie um PDF, XLSX ou XLS.",
      size: "Arquivo acima do limite: PDF até 10 MB, XLSX/XLS até 5 MB.",
      empty: "O arquivo está vazio.",
    },
  },
  manual: {
    title: "Preencher balanço manualmente",
    description: "Valores em reais. Os totais são somados automaticamente.",
    year: "Ano de referência",
    groups: {
      currentAssets: "Ativo circulante",
      nonCurrentAssets: "Ativo não circulante",
      currentLiabilities: "Passivo circulante",
      nonCurrentLiabilities: "Passivo não circulante",
      equity: "Patrimônio líquido",
      dre: "DRE",
    },
    fields: {
      ac_caixa: "Caixa",
      ac_contas_receber: "Contas a receber",
      ac_estoques: "Estoques",
      ac_outros: "Outros AC",
      anc_investimentos: "Investimentos",
      anc_imobilizado: "Imobilizado",
      anc_intangivel: "Intangível",
      anc_outros: "Outros ANC",
      pc_fornecedores: "Fornecedores",
      pc_emprestimos_cp: "Empréstimos CP",
      pc_impostos: "Impostos",
      pc_outros: "Outros PC",
      pnc_emprestimos_lp: "Empréstimos LP",
      pnc_outras: "Outras obrigações",
      pl_capital: "Capital social",
      pl_reservas: "Reservas",
      pl_lucros_acumulados: "Lucros acumulados",
      dre_receita: "Receita bruta",
      dre_custo: "Custo produtos",
      dre_lucro_bruto: "Lucro bruto",
      dre_despesas_op: "Despesas op.",
      dre_ebit: "EBIT",
      dre_lucro_liquido: "Lucro líquido",
    },
    submit: "Salvar e analisar",
    submitting: "Salvando…",
    cancel: "Cancelar",
    errors: {
      year: "Informe um ano entre 1990 e {year}.",
      range:
        "Cada valor deve ter no máximo 10 trilhões (positivo ou negativo).",
      allZero: "Preencha ao menos um valor.",
    },
    toast: {
      success: "Balanço salvo e indicadores calculados.",
      mergedTitle: "Balanço atualizado — {year}",
      mergedText: "Seções adicionadas/atualizadas: {sections}.",
      error: "Não foi possível salvar o balanço. Tente novamente.",
    },
    sections: {
      ativo_circulante: "Ativo circulante",
      ativo_nao_circulante: "Ativo não circulante",
      passivo_circulante: "Passivo circulante",
      passivo_nao_circulante: "Passivo não circulante",
      patrimonio_liquido: "Patrimônio líquido",
      dre: "DRE",
    },
  },
  empty: {
    uploadTitle: "Ainda não há dados financeiros desta empresa.",
    uploadText:
      "A Coface não tem demonstrativos publicados. Envie o balanço ou a DRE para calcular indicadores e red flags.",
    noDataTitle: "Dados financeiros indisponíveis",
    noDataText:
      "A Coface não tem demonstrativos publicados para esta empresa e o envio de balanço está disponível apenas para empresas com CNPJ (Brasil).",
    noDataHint: "Precisa de dados mais recentes?",
  },
  error: {
    loadBalances: "Não foi possível carregar os balanços enviados.",
    balanceNotice: "Não foi possível carregar os balanços enviados.",
    balanceNoticeText: "Tente novamente em alguns instantes.",
    retry: "Tentar novamente",
  },
  access: {
    balancesRestricted:
      "Balanços enviados disponíveis apenas para Owner, Admin e Colaborador.",
  },
};

const financialTabEn: FinancialTabTranslations = {
  sections: "Financial tab sections",
  listAnd: "and",
  anchors: {
    summary: "Summary",
    indicators: "Indicators",
    statements: "Statements",
    cofaceStatements: "Coface statements",
    sourcesAndUploads: "Sources and uploads",
    sources: "Sources",
  },
  sourceText: {
    balances: "Sources: submitted statements (AI)",
    manual: "Sources: submitted statements",
    coface: "Source: Coface URBA · checked {date}",
    both: "Sources: submitted statements (AI) · Coface URBA",
    cofaceNoDate: "Source: Coface URBA",
  },
  hero: {
    healthTitle: "Financial health · {year}",
    healthTitleNoYear: "Financial health",
    cofaceTitle: "Financial data · {year}",
    redFlags: "⚑ {n} red flags",
    redFlag: "⚑ 1 red flag",
    origin: "Data sources ({n})",
    upload: "Upload balance sheet",
    cofacePill: "Coface URBA · {currency}",
    lastYears: "Last {n} years available · checked {date}",
    lastYear: "Last year available · checked {date}",
    base: "Basis for this analysis",
    baseUpload: "{type} {year} extracted by AI on {date}",
    baseManual: "{type} {year} entered manually on {date}",
    baseNoDate: "{type} {year}",
    baseTypes: {
      full: "Balance sheet + P&L",
      bpOnly: "Balance sheet only",
      dreOnly: "P&L only",
    },
    comparedWith: " · compared with {years}",
    baseCoface: "Statements published by Coface in {currency}.",
    cofaceNoDataTitle: "Coface: no financial data",
    cofaceNoDataText:
      "Common for privately held companies in Brazil. Indicators therefore use submitted statements.",
    cofacePendingTitle: "Coface: financial data in preparation",
    cofacePendingDate: "Expected delivery on {date}.",
    cofacePendingNoDate: "Coface is still processing the report.",
    cofaceNotConsultedTitle: "Coface: not consulted",
    cofaceNotConsultedText:
      "Request an analysis to include Coface financial data.",
    cofaceAvailableTitle: "Coface: financial data available",
    cofaceAvailableText:
      "Statements {years} in {currency}. See the Coface table below.",
    uploadUnavailableTitle: "Balance sheet upload unavailable",
    uploadUnavailableText:
      "Balance sheet upload and manual entry are only available for companies with a Brazilian CNPJ.",
    uploadAvailableTitle: "Complete with the company's balance sheet",
    uploadAvailableText:
      "Upload the balance sheet or P&L to calculate all indicators and red flags.",
  },
  kpi: {
    netRevenue: "Net revenue",
    grossRevenue: "Gross revenue",
    operatingRevenue: "Operating revenue",
    netIncome: "Net income",
    totalAssets: "Total assets",
    equity: "Equity",
    debt: "Debt (ST + LT)",
    bankDebt: "Bank debt (ST + LT)",
    netMargin: "Net margin",
    cash: "Cash and cash equivalents",
    vs: "{arrow} {value} vs {year}",
    vsPp: "{arrow} {value} pp vs {year}",
  },
  health: {
    excellent: "Excellent",
    good: "Good",
    acceptable: "Acceptable",
    weak: "Weak",
    critical: "Critical",
    attention: "Attention",
  },
  summary: {
    healthy: "Indicators are within reference in {year}.",
    noData: "There is no financial data for this company yet.",
    positives: {
      profitability: "positive profitability",
      liquidity: "healthy current ratio",
      debt: "controlled debt",
    },
    attentionSentence: "Watch out for {items}.",
    attention: {
      liquidez_corrente: "the current ratio",
      liquidez_seca: "the quick ratio",
      liquidez_geral: "general liquidity",
      capacidade_pagamento: "debt service capacity",
      endividamento_geral: "the total debt ratio",
      endividamento_cp: "short-term debt concentration",
      debt_equity: "the debt-to-equity ratio",
      rotacao_ativo: "asset turnover",
      roa: "return on assets (ROA)",
      roe: "return on equity (ROE)",
      margem_liquida: "the net margin",
      margem_operacional: "the operating margin",
      redFlags: "the red flags detected",
    },
    cofaceNoComparison: "No revenue comparison in {year}.",
    cofaceRevenue: {
      up: "Revenue is growing",
      down: "Revenue is falling",
      stable: "Revenue is flat",
    },
    cofaceClause: {
      butFall: ", but {items} are falling",
      butFallOne: ", but {items} is falling",
      butRise: ", but {items} are rising",
      butRiseOne: ", but {items} is rising",
      alsoFall: ", and {items} are also falling",
      alsoFallOne: ", and {items} is also falling",
      alsoRise: ", and {items} are also rising",
      alsoRiseOne: ", and {items} is also rising",
    },
    cofaceItems: { margin: "margin", cash: "cash" },
    cofaceDebt: {
      shortTerm: "Short-term debt rose {pct}% in {year}.",
      longTerm: "Long-term debt rose {pct}% in {year}.",
    },
  },
  indicators: {
    title: "Financial indicators",
    titleCoface: "Calculated indicators",
    yearSuffix: "— {year}",
    yearSelector: "Indicator year",
    cofacePill: "Calculated by TR360 from Coface data",
    cofaceNote:
      "These indicators use the same reference rules as balance sheets submitted in Brazil.",
    legend: {
      within: "Within reference",
      attention: "Attention",
      outside: "Outside reference",
      noData: "No data",
    },
    groups: {
      liquidity: "LIQUIDITY",
      debt: "DEBT",
      profitability: "PROFITABILITY",
    },
    groupStatus: {
      healthy: "Healthy",
      moderateRisk: "Moderate risk",
      criticalAttention: "Critical attention",
      controlled: "Controlled",
      mediumRisk: "Medium risk",
      highRisk: "High risk",
      positive: "Positive",
      negative: "Negative",
      noData: "No data",
    },
    names: {
      liquidez_corrente: "CURRENT RATIO",
      liquidez_seca: "QUICK RATIO",
      liquidez_geral: "GENERAL LIQUIDITY",
      capacidade_pagamento: "DEBT SERVICE CAP.",
      endividamento_geral: "TOTAL DEBT RATIO",
      endividamento_cp: "ST DEBT RATIO",
      debt_equity: "DEBT / EQUITY",
      rotacao_ativo: "ASSET TURNOVER",
      roa: "ROA",
      roe: "ROE",
      margem_liquida: "NET MARGIN",
      margem_operacional: "OPERATING MARGIN",
    },
    reference: {
      minIdeal: "Minimum > {min} · ideal > {ideal}",
      limitIdeal: "Limit < {max} · ideal < {ideal}",
    },
    plain: {
      currentRatio:
        "{m} {v} of current assets for every {m} {one} due within 12 months.",
      quickRatio: "Excluding inventory, covers {p}% of short-term debt.",
      generalLiquidity: "Assets cover {p}% of all debt (short and long term).",
      paymentCapacity: "Operating income covers {p}% of short-term debt.",
      paymentCapacityNegative:
        "Negative operating income: no cash generated for short-term debt.",
      totalDebt: "{p}% of assets are financed by third parties.",
      shortTermDebt:
        "{p}% of assets are committed to debt due within 12 months.",
      debtEquity: "{m} {v} of debt for every {m} {one} of equity.",
      debtEquityNegativeEquity: "Negative equity.",
      assetTurnover:
        "Every {m} {one} of assets generated {m} {v} of revenue in the year.",
      roa: "Profit of {m} {v} for every {m} 100 of assets.",
      roaNegative: "Loss of {m} {v} for every {m} 100 of assets.",
      roe: "Return of {p}% on shareholders' capital.",
      roeNegative: "Loss of {p}% on shareholders' capital.",
      netMargin: "{m} {v} of profit left for every {m} 100 sold.",
      netMarginNegative: "Loss of {m} {v} for every {m} 100 sold.",
      netMarginWithPrevious:
        "{m} {v} of profit left for every {m} 100 sold ({prev}% in {prevYear}).",
      netMarginNegativeWithPrevious:
        "Loss of {m} {v} for every {m} 100 sold ({prev}% in {prevYear}).",
      operatingMargin:
        "Operations generate {m} {v} of profit for every {m} 100 sold, before interest and taxes.",
      operatingMarginNegative:
        "Operations lose {m} {v} for every {m} 100 sold, before interest and taxes.",
    },
    noDataUpload: "No data in the uploaded document.",
    noDataCoface: "No data from Coface.",
    barLabel: "{name}: {value}, {state}",
  },
  redFlags: {
    title: "⚑ RED FLAGS DETECTED ({n})",
    severity: { high: "HIGH", medium: "MEDIUM", low: "LOW" },
  },
  revenue: {
    title: "Revenue by year",
    statNet: "Net revenue {year}",
    statGross: "Gross revenue {year}",
    statOperating: "Operating revenue {year}",
    cagr: "{value}% p.a.",
    ll: "NI {value} · {margin}%",
    llNoMargin: "NI {value}",
    llEmpty: "NI —",
    million: "M",
    legend: {
      net: "Net revenue",
      gross: "Gross revenue",
      deductions: "Deductions (gross − net revenue)",
      ll: "NI = net income · margin",
      operating: "Operating revenue",
      cofaceCurrency: "Amounts in {currency} (Coface)",
      otherBasis: {
        net: "net = year with net revenue",
        gross: "gross = year with gross revenue only (no net revenue reported)",
      },
    },
    basisTag: { net: "net", gross: "gross" },
    empty: "Revenue not reported in the available data.",
    chartLabel: "Revenue by year. {items}",
    barLabel:
      "{year}: revenue {revenue}, deductions {deductions}, net income {netIncome}",
    barLabelNoDeductions: "{year}: revenue {revenue}, net income {netIncome}",
  },
  monthly: {
    title: "Monthly revenue · {year}",
    titleEmpty: "Monthly revenue",
    stat: "Gross revenue for the year",
    months: "{n} months",
    monthsOne: "1 month",
    peak: "Peak: {month}",
    average: "average {value}",
    monthNames: {
      jan: "Jan",
      fev: "Feb",
      mar: "Mar",
      abr: "Apr",
      mai: "May",
      jun: "Jun",
      jul: "Jul",
      ago: "Aug",
      set: "Sep",
      out: "Oct",
      nov: "Nov",
      dez: "Dec",
    },
    footer:
      "Source: monthly revenue reported in the uploaded balance sheet. The dashed line is the yearly average.",
    emptyTitle: "Monthly revenue not reported",
    emptyText:
      "Upload a trial balance or revenue statement with monthly amounts.",
    chartLabel:
      "Monthly revenue for {year}: total {total} over {n} months, peak in {month} at {peak}, average {average}.",
  },
  statements: {
    title: "Financial statements",
    titleCoface: "Coface financial data (last 3 years)",
    origin: "⟲ Origin ({n})",
    unitSelector: "Value unit",
    unit: { brlMi: "R$ M", brl: "R$", currencyMi: "{currency} M" },
    intro:
      "Amounts in R$ millions. Change compares with the previous year. Rows with no value in the document are hidden.",
    introUnit:
      "Amounts in Brazilian reais. Change compares with the previous year. Rows with no value in the document are hidden.",
    introCoface:
      "Amounts in {symbol} millions, as published by Coface. Change compares with the previous year; green = improvement, red = deterioration.",
    introCofaceConverted:
      "Amounts in R$ millions, converted at today's exchange rate. Change compares with the previous year; green = improvement, red = deterioration.",
    dreHeader: "INCOME STATEMENT",
    dreHeaderCoface: "INCOME STATEMENT",
    bpHeader: "BALANCE SHEET",
    caption: "{title}, amounts in {unit}",
    sections: {
      assets: "ASSETS",
      liabilitiesEquity: "LIABILITIES AND EQUITY",
      liabilities: "LIABILITIES",
      equity: "EQUITY",
    },
    rows: {
      grossRevenue: "Gross revenue",
      deductions: "(−) Revenue deductions",
      netRevenue: "Net revenue",
      costProducts: "(−) Cost of goods sold (COGS)",
      costServices: "(−) Cost of services",
      costGoods: "(−) Cost of merchandise",
      grossProfit: "Gross profit",
      operatingExpenses: "(−) Operating expenses",
      ebit: "Operating income (EBIT)",
      financialRevenue: "(+) Financial income",
      financialExpenses: "(−) Financial expenses",
      nonOperatingRevenue: "(+) Non-operating income",
      ebt: "Earnings before taxes (EBT)",
      incomeTax: "(−) Income taxes",
      netIncome: "Net income",
      cash: "Cash and equivalents",
      receivables: "Accounts receivable",
      inventories: "Inventories",
      currentAssetsOther: "Other (current)",
      totalCurrentAssets: "Total current assets",
      longTermReceivables: "Long-term receivables",
      investments: "Investments",
      fixedAssets: "Property, plant and equipment",
      intangibles: "Intangible assets",
      nonCurrentAssetsOther: "Other (non-current)",
      totalNonCurrentAssets: "Total non-current assets",
      totalAssets: "Total assets",
      suppliers: "Suppliers",
      shortTermLoans: "Short-term loans",
      taxesPayable: "Taxes payable",
      currentLiabilitiesOther: "Other (current)",
      totalCurrentLiabilities: "Total current liabilities",
      longTermLoans: "Long-term loans",
      otherObligations: "Other obligations",
      totalNonCurrentLiabilities: "Total non-current liabilities",
      totalLiabilities: "Total liabilities",
      shareCapital: "Share capital",
      reserves: "Reserves",
      retainedEarnings: "Retained earnings",
      equity: "Shareholders' equity",
      operatingRevenue: "Operating revenue",
      costOfSales: "Cost of sales",
      cofaceOperatingExpenses: "Operating expenses",
      operatingProfit: "Operating profit",
      tradeDebtors: "Trade receivables",
      totalFixedAssets: "Total fixed assets",
      shortTermDebt: "Short-term debt",
      longTermDebt: "Long-term debt",
      totalEquity: "Total shareholders' equity",
    },
    variation: { up: "up {value}", down: "down {value}", flat: "no change" },
    footer: "Consolidated balance sheet · reference {year}",
    showAll: "Show all rows (other assets, reserves…) ⌄",
    showLess: "Show less ⌃",
  },
  sources: {
    title: "Financial data sources",
    titleCoface: "Data sources",
    description:
      "Each upload is extracted by AI and merged into its reference year. Separate balance sheet and P&L uploads complete each other automatically.",
    uploadName: "Uploaded document",
    manualName: "Manual entry",
    sentOn: "{type} · sent on {date}",
    sentOnNoType: "Sent on {date}",
    sentBy: "{text} by {name}",
    noDate: "Upload date not available",
    types: {
      full: "Balance sheet + P&L",
      bpOnly: "Balance sheet only",
      dreOnly: "P&L only",
    },
    year: "Year {year}",
    status: {
      extracted: "✓ Extracted by AI",
      merged: "✓ Merged into {side} {year}",
      manual: "✓ Filled in manually",
      none: "No data identified",
    },
    showAll: "Show all uploads ({n})",
    showLess: "Show less",
    menu: {
      trigger: "Upload options",
      details: "View details",
      replace: "Replace",
    },
    details: {
      title: "Upload details",
      origin: "Origin",
      type: "Type",
      year: "Year",
      sentAt: "Sent on",
      status: "Status",
      sections: "Updated sections",
      close: "Close",
    },
    coface: {
      title: "Coface URBA · Financial data",
      noData:
        "Essential financial information is not available in Coface sources for this company. Checked on {date}.",
      noDataNoDate:
        "Essential financial information is not available in Coface sources for this company.",
      withData:
        "Statements {years} in {currency}. Updated on the check of {date}.",
      withDataNoDate: "Statements {years} in {currency}.",
      pending: "Expected delivery on {date}.",
      pendingNoDate: "Coface is still processing the report.",
      notConsulted: "Request an analysis to include Coface financial data.",
      pillNoData: "No data",
      pillWithData: "✓ With data",
      pillPending: "In preparation",
      pillNotConsulted: "Not checked",
    },
    uploads: {
      title: "Submitted statements",
      unavailableText:
        "Upload (PDF/XLSX) and manual entry are available only for companies with a Brazilian CNPJ.",
      availableText:
        "Upload (PDF/XLSX) or manual entry of the balance sheet and P&L.",
      restrictedText:
        "Submitted balance sheets are only available to Owner, Admin and Collaborator.",
      errorText: "Unable to load submitted balance sheets.",
      retry: "Try again",
      pillUnavailable: "Unavailable",
      pillNone: "No uploads",
      link: "Upload balance sheet →",
    },
    tip: {
      title: "Need more recent data?",
      text: "Request a new analysis: Coface updates the statements when the company publishes new balance sheets.",
    },
  },
  upload: {
    title: "Upload balance sheet",
    cnpjOnly: "CNPJ only (BR)",
    docType: "Document type",
    types: {
      balanco_completo: "Balance sheet + P&L",
      balancete: "Trial balance",
      balanco_patrimonial: "Balance sheet only",
      dre: "P&L only",
    },
    year: "Reference year",
    drop: "Drag the PDF or spreadsheet here",
    limits: "PDF up to 10 MB · XLSX/XLS up to 5 MB",
    select: "Select file",
    change: "Change file",
    fileInput: "Balance sheet file (PDF, XLSX or XLS)",
    howTitle: "How it works",
    howText:
      "1. AI reads the document and fills in the balance sheet and P&L.  2. We check that assets = liabilities + equity and flag inconsistencies.  3. Indicators and red flags are recalculated.",
    analyze: "Analyze balance sheet",
    overwrite:
      "A balance sheet for {year} already exists. This upload updates that record.",
    manual: "Prefer typing? Fill in manually →",
    errors: {
      type: "Format not accepted. Upload a PDF, XLSX or XLS file.",
      size: "File over the limit: PDF up to 10 MB, XLSX/XLS up to 5 MB.",
      empty: "The file is empty.",
    },
  },
  manual: {
    title: "Fill in balance sheet manually",
    description:
      "Amounts in Brazilian reais. Totals are added up automatically.",
    year: "Reference year",
    groups: {
      currentAssets: "Current assets",
      nonCurrentAssets: "Non-current assets",
      currentLiabilities: "Current liabilities",
      nonCurrentLiabilities: "Non-current liabilities",
      equity: "Equity",
      dre: "P&L",
    },
    fields: {
      ac_caixa: "Cash",
      ac_contas_receber: "Accounts receivable",
      ac_estoques: "Inventories",
      ac_outros: "Other current assets",
      anc_investimentos: "Investments",
      anc_imobilizado: "Property, plant and equipment",
      anc_intangivel: "Intangible assets",
      anc_outros: "Other non-current assets",
      pc_fornecedores: "Suppliers",
      pc_emprestimos_cp: "Short-term loans",
      pc_impostos: "Taxes payable",
      pc_outros: "Other current liabilities",
      pnc_emprestimos_lp: "Long-term loans",
      pnc_outras: "Other obligations",
      pl_capital: "Share capital",
      pl_reservas: "Reserves",
      pl_lucros_acumulados: "Retained earnings",
      dre_receita: "Gross revenue",
      dre_custo: "Cost of goods sold",
      dre_lucro_bruto: "Gross profit",
      dre_despesas_op: "Operating expenses",
      dre_ebit: "EBIT",
      dre_lucro_liquido: "Net income",
    },
    submit: "Save and analyze",
    submitting: "Saving…",
    cancel: "Cancel",
    errors: {
      year: "Enter a year between 1990 and {year}.",
      range: "Each amount must be at most 10 trillion (positive or negative).",
      allZero: "Fill in at least one amount.",
    },
    toast: {
      success: "Balance sheet saved and indicators calculated.",
      mergedTitle: "Balance sheet updated — {year}",
      mergedText: "Sections added/updated: {sections}.",
      error: "Unable to save the balance sheet. Please try again.",
    },
    sections: {
      ativo_circulante: "Current assets",
      ativo_nao_circulante: "Non-current assets",
      passivo_circulante: "Current liabilities",
      passivo_nao_circulante: "Non-current liabilities",
      patrimonio_liquido: "Equity",
      dre: "P&L",
    },
  },
  empty: {
    uploadTitle: "There is no financial data for this company yet.",
    uploadText:
      "Coface has no published statements. Upload the balance sheet or P&L to calculate indicators and red flags.",
    noDataTitle: "Financial data unavailable",
    noDataText:
      "Coface has no published statements for this company, and balance sheet upload is available only for companies with a Brazilian CNPJ.",
    noDataHint: "Need more recent data?",
  },
  error: {
    loadBalances: "Unable to load submitted balance sheets.",
    balanceNotice: "Unable to load submitted balance sheets.",
    balanceNoticeText: "Please try again in a few moments.",
    retry: "Try again",
  },
  access: {
    balancesRestricted:
      "Submitted balance sheets are only available to Owner, Admin and Collaborator.",
  },
};

const financialTabEs: FinancialTabTranslations = {
  sections: "Secciones de la pestaña Financiero",
  listAnd: "y",
  anchors: {
    summary: "Resumen",
    indicators: "Indicadores",
    statements: "Estados financieros",
    cofaceStatements: "Estados Coface",
    sourcesAndUploads: "Fuentes y envíos",
    sources: "Fuentes",
  },
  sourceText: {
    balances: "Fuentes: balances enviados (IA)",
    manual: "Fuentes: balances enviados",
    coface: "Fuente: Coface URBA · consulta {date}",
    both: "Fuentes: balances enviados (IA) · Coface URBA",
    cofaceNoDate: "Fuente: Coface URBA",
  },
  hero: {
    healthTitle: "Salud financiera · {year}",
    healthTitleNoYear: "Salud financiera",
    cofaceTitle: "Datos financieros · {year}",
    redFlags: "⚑ {n} red flags",
    redFlag: "⚑ 1 red flag",
    origin: "Origen de los datos ({n})",
    upload: "Enviar balance",
    cofacePill: "Coface URBA · {currency}",
    lastYears: "Últimos {n} años disponibles · consulta {date}",
    lastYear: "Último año disponible · consulta {date}",
    base: "Base de este análisis",
    baseUpload: "{type} {year} extraído por IA el {date}",
    baseManual: "{type} {year} completado manualmente el {date}",
    baseNoDate: "{type} {year}",
    baseTypes: {
      full: "Balance + Estado de resultados",
      bpOnly: "Solo balance",
      dreOnly: "Solo estado de resultados",
    },
    comparedWith: " · comparado con {years}",
    baseCoface: "Estados publicados por Coface en {currency}.",
    cofaceNoDataTitle: "Coface: sin datos financieros",
    cofaceNoDataText:
      "Es habitual en empresas privadas de Brasil. Por eso los indicadores usan balances enviados.",
    cofacePendingTitle: "Coface: datos financieros en preparación",
    cofacePendingDate: "Entrega prevista para {date}.",
    cofacePendingNoDate: "Coface todavía está procesando el informe.",
    cofaceNotConsultedTitle: "Coface: no consultada",
    cofaceNotConsultedText:
      "Solicite un análisis para incluir los datos financieros de Coface.",
    cofaceAvailableTitle: "Coface: datos financieros disponibles",
    cofaceAvailableText:
      "Estados {years} en {currency}. Vea la tabla Coface abajo.",
    uploadUnavailableTitle: "Carga de balance no disponible",
    uploadUnavailableText:
      "La carga y entrada manual de balance están disponibles solo para empresas con CNPJ de Brasil.",
    uploadAvailableTitle: "Complete con el balance de la empresa",
    uploadAvailableText:
      "Envíe el balance o el Estado de resultados para calcular todos los indicadores y red flags.",
  },
  kpi: {
    netRevenue: "Ingresos netos",
    grossRevenue: "Ingresos brutos",
    operatingRevenue: "Ingresos operativos",
    netIncome: "Beneficio neto",
    totalAssets: "Total de activos",
    equity: "Patrimonio neto",
    debt: "Deuda (CP + LP)",
    bankDebt: "Deuda bancaria (CP + LP)",
    netMargin: "Margen neto",
    cash: "Caja y equivalentes",
    vs: "{arrow} {value} vs {year}",
    vsPp: "{arrow} {value} p.p. vs {year}",
  },
  health: {
    excellent: "Excelente",
    good: "Buena",
    acceptable: "Aceptable",
    weak: "Débil",
    critical: "Crítica",
    attention: "Atención",
  },
  summary: {
    healthy: "Los indicadores están dentro de referencia en {year}.",
    noData: "Aún no hay datos financieros de esta empresa.",
    positives: {
      profitability: "rentabilidad positiva",
      liquidity: "liquidez corriente saludable",
      debt: "endeudamiento controlado",
    },
    attentionSentence: "Atención {items}.",
    attention: {
      liquidez_corrente: "a la liquidez corriente",
      liquidez_seca: "a la prueba ácida",
      liquidez_geral: "a la liquidez general",
      capacidade_pagamento: "a la capacidad de pago",
      endividamento_geral: "al endeudamiento general",
      endividamento_cp: "a la concentración de deuda a corto plazo",
      debt_equity: "a la relación entre deuda y patrimonio",
      rotacao_ativo: "a la rotación del activo",
      roa: "al retorno sobre activos (ROA)",
      roe: "al retorno sobre patrimonio (ROE)",
      margem_liquida: "al margen neto",
      margem_operacional: "al margen operativo",
      redFlags: "a las red flags detectadas",
    },
    cofaceNoComparison: "No hay comparación de ingresos en {year}.",
    cofaceRevenue: {
      up: "Los ingresos crecen",
      down: "Los ingresos caen",
      stable: "Los ingresos se mantienen estables",
    },
    cofaceClause: {
      butFall: ", pero {items} caen",
      butFallOne: ", pero {items} cae",
      butRise: ", pero {items} suben",
      butRiseOne: ", pero {items} sube",
      alsoFall: ", y {items} también caen",
      alsoFallOne: ", y {items} también cae",
      alsoRise: ", y {items} también suben",
      alsoRiseOne: ", y {items} también sube",
    },
    cofaceItems: { margin: "el margen", cash: "la caja" },
    cofaceDebt: {
      shortTerm: "La deuda a corto plazo subió un {pct}% en {year}.",
      longTerm: "La deuda a largo plazo subió un {pct}% en {year}.",
    },
  },
  indicators: {
    title: "Indicadores financieros",
    titleCoface: "Indicadores calculados",
    yearSuffix: "— {year}",
    yearSelector: "Año de los indicadores",
    cofacePill: "Calculado por TR360 a partir de Coface",
    cofaceNote:
      "Estos indicadores usan las mismas reglas de referencia que los balances enviados en Brasil.",
    legend: {
      within: "Dentro de la referencia",
      attention: "Atención",
      outside: "Fuera de la referencia",
      noData: "Sin datos",
    },
    groups: {
      liquidity: "LIQUIDEZ",
      debt: "ENDEUDAMIENTO",
      profitability: "RENTABILIDAD",
    },
    groupStatus: {
      healthy: "Saludable",
      moderateRisk: "Riesgo moderado",
      criticalAttention: "Atención crítica",
      controlled: "Controlado",
      mediumRisk: "Riesgo medio",
      highRisk: "Riesgo alto",
      positive: "Positiva",
      negative: "Negativa",
      noData: "Sin datos",
    },
    names: {
      liquidez_corrente: "LIQUIDEZ CORRIENTE",
      liquidez_seca: "PRUEBA ÁCIDA",
      liquidez_geral: "LIQUIDEZ GENERAL",
      capacidade_pagamento: "CAP. DE PAGO",
      endividamento_geral: "ENDEUD. GENERAL",
      endividamento_cp: "ENDEUD. CP",
      debt_equity: "DEBT / EQUITY",
      rotacao_ativo: "ROTACIÓN DEL ACTIVO",
      roa: "ROA",
      roe: "ROE",
      margem_liquida: "MARGEN NETO",
      margem_operacional: "MARGEN OPERATIVO",
    },
    reference: {
      minIdeal: "Mínimo > {min} · ideal > {ideal}",
      limitIdeal: "Límite < {max} · ideal < {ideal}",
    },
    plain: {
      currentRatio:
        "{m} {v} de activo corriente por cada {m} {one} a pagar en hasta 12 meses.",
      quickRatio:
        "Sin contar inventarios, cubre el {p}% de las deudas de corto plazo.",
      generalLiquidity:
        "Los activos cubren el {p}% de todas las deudas (corto y largo plazo).",
      paymentCapacity:
        "El resultado operativo cubre el {p}% de las deudas de corto plazo.",
      paymentCapacityNegative:
        "Resultado operativo negativo: no genera caja para las deudas de corto plazo.",
      totalDebt: "El {p}% del activo está financiado por terceros.",
      shortTermDebt:
        "El {p}% del activo está comprometido con deudas de hasta 12 meses.",
      debtEquity: "{m} {v} de deuda por cada {m} {one} de patrimonio.",
      debtEquityNegativeEquity: "Patrimonio neto negativo.",
      assetTurnover:
        "Cada {m} {one} de activo generó {m} {v} de ingresos en el año.",
      roa: "Beneficio de {m} {v} por cada {m} 100 de activos.",
      roaNegative: "Pérdida de {m} {v} por cada {m} 100 de activos.",
      roe: "Rentabilidad del {p}% sobre el capital de los socios.",
      roeNegative: "Pérdida del {p}% sobre el capital de los socios.",
      netMargin: "Quedan {m} {v} de beneficio por cada {m} 100 vendidos.",
      netMarginNegative: "Pérdida de {m} {v} por cada {m} 100 vendidos.",
      netMarginWithPrevious:
        "Quedan {m} {v} de beneficio por cada {m} 100 vendidos ({prev}% en {prevYear}).",
      netMarginNegativeWithPrevious:
        "Pérdida de {m} {v} por cada {m} 100 vendidos ({prev}% en {prevYear}).",
      operatingMargin:
        "La operación genera {m} {v} de beneficio por cada {m} 100 vendidos, antes de intereses e impuestos.",
      operatingMarginNegative:
        "La operación pierde {m} {v} por cada {m} 100 vendidos, antes de intereses e impuestos.",
    },
    noDataUpload: "Sin datos en el documento enviado.",
    noDataCoface: "Sin datos de Coface.",
    barLabel: "{name}: {value}, {state}",
  },
  redFlags: {
    title: "⚑ RED FLAGS DETECTADOS ({n})",
    severity: { high: "ALTA", medium: "MEDIA", low: "BAJA" },
  },
  revenue: {
    title: "Ingresos por año",
    statNet: "Ingresos netos {year}",
    statGross: "Ingresos brutos {year}",
    statOperating: "Ingresos operativos {year}",
    cagr: "{value}% anual",
    ll: "UN {value} · {margin}%",
    llNoMargin: "UN {value}",
    llEmpty: "UN —",
    million: "M",
    legend: {
      net: "Ingresos netos",
      gross: "Ingresos brutos",
      deductions: "Deducciones (ingresos brutos − netos)",
      ll: "UN = utilidad neta · margen",
      operating: "Ingresos operativos",
      cofaceCurrency: "Valores en {currency} (Coface)",
      otherBasis: {
        net: "neta = año con ingresos netos",
        gross:
          "bruta = año solo con ingresos brutos (sin ingresos netos informados)",
      },
    },
    basisTag: { net: "neta", gross: "bruta" },
    empty: "Ingresos no informados en los datos disponibles.",
    chartLabel: "Ingresos por año. {items}",
    barLabel:
      "{year}: ingresos {revenue}, deducciones {deductions}, utilidad neta {netIncome}",
    barLabelNoDeductions:
      "{year}: ingresos {revenue}, utilidad neta {netIncome}",
  },
  monthly: {
    title: "Facturación mensual · {year}",
    titleEmpty: "Facturación mensual",
    stat: "Ingresos brutos del año",
    months: "{n} meses",
    monthsOne: "1 mes",
    peak: "Pico: {month}",
    average: "promedio {value}",
    monthNames: {
      jan: "Ene",
      fev: "Feb",
      mar: "Mar",
      abr: "Abr",
      mai: "May",
      jun: "Jun",
      jul: "Jul",
      ago: "Ago",
      set: "Sep",
      out: "Oct",
      nov: "Nov",
      dez: "Dic",
    },
    footer:
      "Fuente: facturación mensual informada en el balance enviado. La línea discontinua es el promedio del año.",
    emptyTitle: "Facturación mensual no informada",
    emptyText:
      "Envíe un balance de comprobación o una declaración de facturación con los valores por mes.",
    chartLabel:
      "Facturación mensual de {year}: total {total} en {n} meses, pico en {month} con {peak}, promedio {average}.",
  },
  statements: {
    title: "Estados financieros",
    titleCoface: "Datos financieros Coface (últimos 3 años)",
    origin: "⟲ Origen ({n})",
    unitSelector: "Unidad de los valores",
    unit: { brlMi: "R$ M", brl: "R$", currencyMi: "{currency} M" },
    intro:
      "Valores en millones de R$. La variación compara con el año anterior. Las filas sin valor en el documento se ocultan.",
    introUnit:
      "Valores en reales. La variación compara con el año anterior. Las filas sin valor en el documento se ocultan.",
    introCoface:
      "Valores en millones de {symbol}, tal como los publica Coface. La variación compara con el año anterior; verde = mejora, rojo = empeora.",
    introCofaceConverted:
      "Valores en millones de R$, convertidos al tipo de cambio del día. La variación compara con el año anterior; verde = mejora, rojo = empeora.",
    dreHeader: "ESTADO DE RESULTADOS",
    dreHeaderCoface: "ESTADO DE RESULTADOS",
    bpHeader: "BALANCE GENERAL",
    caption: "{title}, valores en {unit}",
    sections: {
      assets: "ACTIVO",
      liabilitiesEquity: "PASIVO Y PATRIMONIO",
      liabilities: "PASIVO",
      equity: "PATRIMONIO NETO",
    },
    rows: {
      grossRevenue: "Ingresos brutos",
      deductions: "(−) Deducciones de ingresos",
      netRevenue: "Ingresos netos",
      costProducts: "(−) Costo de los productos vendidos",
      costServices: "(−) Costo de los servicios",
      costGoods: "(−) Costo de las mercaderías",
      grossProfit: "Utilidad bruta",
      operatingExpenses: "(−) Gastos operativos",
      ebit: "Resultado operativo (EBIT)",
      financialRevenue: "(+) Ingresos financieros",
      financialExpenses: "(−) Gastos financieros",
      nonOperatingRevenue: "(+) Ingresos no operativos",
      ebt: "Resultado antes de impuestos (EBT)",
      incomeTax: "(−) Impuestos sobre la renta",
      netIncome: "Utilidad neta",
      cash: "Caja y equivalentes",
      receivables: "Cuentas por cobrar",
      inventories: "Inventarios",
      currentAssetsOther: "Otros (corriente)",
      totalCurrentAssets: "Total activo corriente",
      longTermReceivables: "Realizable a largo plazo",
      investments: "Inversiones",
      fixedAssets: "Propiedad, planta y equipo",
      intangibles: "Intangibles",
      nonCurrentAssetsOther: "Otros (no corriente)",
      totalNonCurrentAssets: "Total activo no corriente",
      totalAssets: "Total activo",
      suppliers: "Proveedores",
      shortTermLoans: "Préstamos a corto plazo",
      taxesPayable: "Impuestos por pagar",
      currentLiabilitiesOther: "Otros (corriente)",
      totalCurrentLiabilities: "Total pasivo corriente",
      longTermLoans: "Préstamos a largo plazo",
      otherObligations: "Otras obligaciones",
      totalNonCurrentLiabilities: "Total pasivo no corriente",
      totalLiabilities: "Total pasivo",
      shareCapital: "Capital social",
      reserves: "Reservas",
      retainedEarnings: "Utilidades acumuladas",
      equity: "Patrimonio neto",
      operatingRevenue: "Ingresos operativos",
      costOfSales: "Costo de ventas",
      cofaceOperatingExpenses: "Gastos operativos",
      operatingProfit: "Utilidad operativa",
      tradeDebtors: "Clientes por cobrar",
      totalFixedAssets: "Total activo fijo",
      shortTermDebt: "Deuda a corto plazo",
      longTermDebt: "Deuda a largo plazo",
      totalEquity: "Total patrimonio neto",
    },
    variation: {
      up: "aumento de {value}",
      down: "caída de {value}",
      flat: "sin variación",
    },
    footer: "Balance consolidado · referencia {year}",
    showAll: "Ver todas las filas (otros activos, reservas…) ⌄",
    showLess: "Ver menos ⌃",
  },
  sources: {
    title: "Fuentes de los datos financieros",
    titleCoface: "Fuentes de los datos",
    description:
      "Cada envío se extrae con IA y se combina con el año de referencia. Los envíos separados de balance y estado de resultados se completan automáticamente.",
    uploadName: "Documento enviado",
    manualName: "Carga manual",
    sentOn: "{type} · enviado el {date}",
    sentOnNoType: "Enviado el {date}",
    sentBy: "{text} por {name}",
    noDate: "Fecha de envío no informada",
    types: {
      full: "Balance + estado de resultados",
      bpOnly: "Solo balance",
      dreOnly: "Solo estado de resultados",
    },
    year: "Año {year}",
    status: {
      extracted: "✓ Extraído con IA",
      merged: "✓ Combinado con {side} {year}",
      manual: "✓ Cargado manualmente",
      none: "Ningún dato identificado",
    },
    showAll: "Ver todos los envíos ({n})",
    showLess: "Ver menos",
    menu: {
      trigger: "Opciones del envío",
      details: "Ver detalles",
      replace: "Reemplazar",
    },
    details: {
      title: "Detalles del envío",
      origin: "Origen",
      type: "Tipo",
      year: "Año",
      sentAt: "Enviado el",
      status: "Estado",
      sections: "Secciones actualizadas",
      close: "Cerrar",
    },
    coface: {
      title: "Coface URBA · Datos financieros",
      noData:
        "La información financiera esencial no está disponible en las fuentes de Coface para esta empresa. Consulta del {date}.",
      noDataNoDate:
        "La información financiera esencial no está disponible en las fuentes de Coface para esta empresa.",
      withData:
        "Estados {years} en {currency}. Actualizados en la consulta del {date}.",
      withDataNoDate: "Estados {years} en {currency}.",
      pending: "Entrega prevista el {date}.",
      pendingNoDate: "Coface todavía está procesando el informe.",
      notConsulted:
        "Solicite un análisis para incluir los datos financieros de Coface.",
      pillNoData: "Sin datos",
      pillWithData: "✓ Con datos",
      pillPending: "En preparación",
      pillNotConsulted: "No consultada",
    },
    uploads: {
      title: "Balances enviados",
      unavailableText:
        "La carga (PDF/XLSX) y la carga manual están disponibles solo para empresas con CNPJ de Brasil.",
      availableText:
        "Carga (PDF/XLSX) o carga manual del balance y del estado de resultados.",
      restrictedText:
        "Los balances enviados están disponibles solo para Owner, Admin y Colaborador.",
      errorText: "No fue posible cargar los balances enviados.",
      retry: "Intentar de nuevo",
      pillUnavailable: "No disponible",
      pillNone: "Ningún envío",
      link: "Enviar balance →",
    },
    tip: {
      title: "¿Necesita datos más recientes?",
      text: "Solicite un nuevo análisis: Coface actualiza los estados cuando la empresa publica nuevos balances.",
    },
  },
  upload: {
    title: "Enviar balance",
    cnpjOnly: "Solo CNPJ (BR)",
    docType: "Tipo de documento",
    types: {
      balanco_completo: "Balance + estado de resultados",
      balancete: "Balance de comprobación",
      balanco_patrimonial: "Solo balance",
      dre: "Solo estado de resultados",
    },
    year: "Año de referencia",
    drop: "Arrastre el PDF o la hoja de cálculo aquí",
    limits: "PDF hasta 10 MB · XLSX/XLS hasta 5 MB",
    select: "Seleccionar archivo",
    change: "Cambiar archivo",
    fileInput: "Archivo del balance (PDF, XLSX o XLS)",
    howTitle: "Cómo funciona",
    howText:
      "1. La IA lee el documento y completa el balance y el estado de resultados.  2. Verificamos que activo = pasivo + patrimonio y avisamos inconsistencias.  3. Se recalculan los indicadores y las red flags.",
    analyze: "Analizar balance",
    overwrite:
      "Ya existe un balance de {year}. El envío actualiza ese registro.",
    manual: "¿Prefiere escribir? Completar manualmente →",
    errors: {
      type: "Formato no aceptado. Envíe un PDF, XLSX o XLS.",
      size: "Archivo por encima del límite: PDF hasta 10 MB, XLSX/XLS hasta 5 MB.",
      empty: "El archivo está vacío.",
    },
  },
  manual: {
    title: "Completar balance manualmente",
    description: "Valores en reales. Los totales se suman automáticamente.",
    year: "Año de referencia",
    groups: {
      currentAssets: "Activo corriente",
      nonCurrentAssets: "Activo no corriente",
      currentLiabilities: "Pasivo corriente",
      nonCurrentLiabilities: "Pasivo no corriente",
      equity: "Patrimonio neto",
      dre: "Estado de resultados",
    },
    fields: {
      ac_caixa: "Caja",
      ac_contas_receber: "Cuentas por cobrar",
      ac_estoques: "Inventarios",
      ac_outros: "Otros activos corrientes",
      anc_investimentos: "Inversiones",
      anc_imobilizado: "Propiedad, planta y equipo",
      anc_intangivel: "Intangibles",
      anc_outros: "Otros activos no corrientes",
      pc_fornecedores: "Proveedores",
      pc_emprestimos_cp: "Préstamos CP",
      pc_impostos: "Impuestos por pagar",
      pc_outros: "Otros pasivos corrientes",
      pnc_emprestimos_lp: "Préstamos LP",
      pnc_outras: "Otras obligaciones",
      pl_capital: "Capital social",
      pl_reservas: "Reservas",
      pl_lucros_acumulados: "Utilidades acumuladas",
      dre_receita: "Ingresos brutos",
      dre_custo: "Costo de productos",
      dre_lucro_bruto: "Utilidad bruta",
      dre_despesas_op: "Gastos operativos",
      dre_ebit: "EBIT",
      dre_lucro_liquido: "Utilidad neta",
    },
    submit: "Guardar y analizar",
    submitting: "Guardando…",
    cancel: "Cancelar",
    errors: {
      year: "Indique un año entre 1990 y {year}.",
      range:
        "Cada valor debe ser como máximo 10 billones (positivo o negativo).",
      allZero: "Complete al menos un valor.",
    },
    toast: {
      success: "Balance guardado e indicadores calculados.",
      mergedTitle: "Balance actualizado — {year}",
      mergedText: "Secciones agregadas/actualizadas: {sections}.",
      error: "No fue posible guardar el balance. Inténtelo de nuevo.",
    },
    sections: {
      ativo_circulante: "Activo corriente",
      ativo_nao_circulante: "Activo no corriente",
      passivo_circulante: "Pasivo corriente",
      passivo_nao_circulante: "Pasivo no corriente",
      patrimonio_liquido: "Patrimonio neto",
      dre: "Estado de resultados",
    },
  },
  empty: {
    uploadTitle: "Aún no hay datos financieros de esta empresa.",
    uploadText:
      "Coface no tiene estados publicados. Envíe el balance o el estado de resultados para calcular indicadores y red flags.",
    noDataTitle: "Datos financieros no disponibles",
    noDataText:
      "Coface no tiene estados publicados para esta empresa y la carga de balance está disponible solo para empresas con CNPJ de Brasil.",
    noDataHint: "¿Necesita datos más recientes?",
  },
  error: {
    loadBalances: "No fue posible cargar los balances enviados.",
    balanceNotice: "No fue posible cargar los balances enviados.",
    balanceNoticeText: "Inténtelo de nuevo en unos momentos.",
    retry: "Intentar de nuevo",
  },
  access: {
    balancesRestricted:
      "Los balances enviados están disponibles solo para Owner, Admin y Colaborador.",
  },
};

(translations.pt as any).companyDetail.overview = {
  draScore: "Score Coface (DRA)",
  quodScore: "Score Quod",
  creditRecommendation: "Recomendação de crédito",
  creditHistory: "Histórico da recomendação",
  scoreHistory: "Evolução do score DRA",
  noData: "Sem dados",
  noHistory: "Ainda sem histórico",
};
(translations.es as any).companyDetail.overview = {
  draScore: "Puntuación Coface (DRA)",
  quodScore: "Puntuación Quod",
  creditRecommendation: "Recomendación de crédito",
  creditHistory: "Historial de la recomendación",
  scoreHistory: "Evolución de la puntuación DRA",
  noData: "Sin datos",
  noHistory: "Aún sin historial",
};
(translations.en as any).companyDetail.overview = {
  draScore: "Coface score (DRA)",
  quodScore: "Quod score",
  creditRecommendation: "Credit recommendation",
  creditHistory: "Recommendation history",
  scoreHistory: "DRA score evolution",
  noData: "No data",
  noHistory: "No history yet",
};

(translations.pt as any).auth = auth_pt.auth;
(translations.es as any).auth = auth_es.auth;
(translations.en as any).auth = auth_en.auth;

// Merge app translations into main translations
(translations.es as any).app = translations_es_app.app;
(translations.pt as any).app = translations_pt_app.app;

export const detectLanguage = (): Language => {
  // Check localStorage first
  const stored = localStorage.getItem("language") as Language;
  if (stored && ["pt", "es", "en"].includes(stored)) {
    return stored;
  }

  // Detect from browser
  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith("pt")) {
    return "pt";
  } else if (browserLang.startsWith("es")) {
    return "es";
  }

  return "en";
};

export const setLanguage = (lang: Language) => {
  localStorage.setItem("language", lang);
};

const creditDetailDetailTranslations = {
  pt: {
    history: {
      title: "Histórico da recomendação",
      periodsLabel: "Período do histórico",
      periods: {
        "12": "12 meses",
        "24": "24 meses",
        "36": "36 meses",
        all: "Tudo",
      },
      empty: "Ainda sem histórico",
      positive: "↗ Tendência positiva ({amount})",
      negative: "↘ Tendência negativa ({amount})",
      stable: "— Estável no período",
      reduced: "Limite reduzido",
      issuance: "nova emissão",
      legend:
        "Cada ponto é uma emissão da Coface. A linha mostra o limite recomendado vigente em cada data.",
    },
    usage: {
      title: "Uso do limite recomendado",
      openRisk: "Ver exposição e risco",
      currentExposure: "Exposição atual",
      exposureOf: "{exposure} de {limit}",
      usageAria: "Uso do limite: {usage}%",
      available: "Disponível na recomendação",
      aboveLabel: "Acima da recomendação",
      within: {
        title: "Dentro da recomendação Coface",
        text: "Novos pedidos de até {available} mantêm a exposição dentro do limite recomendado.",
      },
      near: {
        title: "Próximo do limite recomendado",
        text: "Restam {available}. Acima disso a exposição supera a recomendação Coface: considere garantias ou pagamento antecipado.",
      },
      above: {
        title: "Acima da recomendação Coface",
        text: "A exposição supera o limite recomendado em {excess}. Considere garantias, pagamento antecipado ou redução da exposição.",
      },
      none: {
        title: "Exposição não informada",
        text: "Informe a exposição atual para acompanhar o uso do limite.",
      },
      unavailable: {
        title: "Limite indisponível",
        text: "O limite recomendado não está disponível para calcular o uso.",
      },
    },
    fx: {
      title: "Variação recente do câmbio · últimos 7 dias",
      source: "Fonte: AwesomeAPI · atualizado {date}",
      descriptionBrl:
        "Mostra quanto o limite recomendado de {limit} vale em cada moeda, dia a dia. A recomendação Coface não muda com o câmbio; muda apenas o equivalente.",
      descriptionIntl:
        "A Coface emitiu o limite em {sourceName} ({limit}). A tabela mostra quanto ele vale em {targetNameA} e em {targetNameB} a cada dia; a recomendação não muda com o câmbio.",
      sourceNames: { BRL: "reais", USD: "dólar", EUR: "euro" },
      currency: { BRL: "Real (BRL)", USD: "Dólar (USD)", EUR: "Euro (EUR)" },
      limitToday: "Limite hoje: {value}",
      date: "Data",
      rate: "Cotação",
      variation: "Variação",
      limitIn: "Limite em {currency}",
    },
  },
  en: {
    history: {
      title: "Recommendation history",
      periodsLabel: "History period",
      periods: {
        "12": "12 months",
        "24": "24 months",
        "36": "36 months",
        all: "All",
      },
      empty: "No history yet",
      positive: "↗ Positive trend ({amount})",
      negative: "↘ Negative trend ({amount})",
      stable: "— Stable in the period",
      reduced: "Limit reduced",
      issuance: "new issuance",
      legend:
        "Each point is a Coface issuance. The line shows the recommended limit in force on each date.",
    },
    usage: {
      title: "Recommended limit usage",
      openRisk: "View exposure and risk",
      currentExposure: "Current exposure",
      exposureOf: "{exposure} of {limit}",
      usageAria: "Limit usage: {usage}%",
      available: "Available in recommendation",
      aboveLabel: "Above recommendation",
      within: {
        title: "Within Coface recommendation",
        text: "New orders up to {available} keep exposure within the recommended limit.",
      },
      near: {
        title: "Near recommended limit",
        text: "{available} remains. Above that exposure exceeds the Coface recommendation: consider guarantees or advance payment.",
      },
      above: {
        title: "Above Coface recommendation",
        text: "Exposure exceeds the recommended limit by {excess}. Consider guarantees, advance payment or reducing exposure.",
      },
      none: {
        title: "Exposure not provided",
        text: "Provide current exposure to track limit usage.",
      },
      unavailable: {
        title: "Limit unavailable",
        text: "The recommended limit is unavailable to calculate usage.",
      },
    },
    fx: {
      title: "Recent exchange rate variation · last 7 days",
      source: "Source: AwesomeAPI · updated {date}",
      descriptionBrl:
        "Shows what the recommended limit of {limit} is worth in each currency, day by day. The Coface recommendation does not change with exchange rates; only the equivalent does.",
      descriptionIntl:
        "Coface issued the limit in {sourceName} ({limit}). The table shows what it is worth in {targetNameA} and {targetNameB} each day; the recommendation does not change with exchange rates.",
      sourceNames: { BRL: "Brazilian real", USD: "US dollar", EUR: "euro" },
      currency: {
        BRL: "Brazilian real (BRL)",
        USD: "US dollar (USD)",
        EUR: "Euro (EUR)",
      },
      limitToday: "Limit today: {value}",
      date: "Date",
      rate: "Rate",
      variation: "Variation",
      limitIn: "Limit in {currency}",
    },
  },
  es: {
    history: {
      title: "Historial de la recomendación",
      periodsLabel: "Período del historial",
      periods: {
        "12": "12 meses",
        "24": "24 meses",
        "36": "36 meses",
        all: "Todo",
      },
      empty: "Aún sin historial",
      positive: "↗ Tendencia positiva ({amount})",
      negative: "↘ Tendencia negativa ({amount})",
      stable: "— Estable en el período",
      reduced: "Límite reducido",
      issuance: "nueva emisión",
      legend:
        "Cada punto es una emisión de Coface. La línea muestra el límite recomendado vigente en cada fecha.",
    },
    usage: {
      title: "Uso del límite recomendado",
      openRisk: "Ver exposición y riesgo",
      currentExposure: "Exposición actual",
      exposureOf: "{exposure} de {limit}",
      usageAria: "Uso del límite: {usage}%",
      available: "Disponible en la recomendación",
      aboveLabel: "Por encima de la recomendación",
      within: {
        title: "Dentro de la recomendación Coface",
        text: "Nuevos pedidos de hasta {available} mantienen la exposición dentro del límite recomendado.",
      },
      near: {
        title: "Cerca del límite recomendado",
        text: "Quedan {available}. Por encima de eso la exposición supera la recomendación Coface: considere garantías o pago anticipado.",
      },
      above: {
        title: "Por encima de la recomendación Coface",
        text: "La exposición supera el límite recomendado en {excess}. Considere garantías, pago anticipado o reducción de la exposición.",
      },
      none: {
        title: "Exposición no informada",
        text: "Informe la exposición actual para acompañar el uso del límite.",
      },
      unavailable: {
        title: "Límite no disponible",
        text: "El límite recomendado no está disponible para calcular el uso.",
      },
    },
    fx: {
      title: "Variación reciente del cambio · últimos 7 días",
      source: "Fuente: AwesomeAPI · actualizado {date}",
      descriptionBrl:
        "Muestra cuánto vale el límite recomendado de {limit} en cada moneda, día a día. La recomendación Coface no cambia con el cambio; solo cambia el equivalente.",
      descriptionIntl:
        "Coface emitió el límite en {sourceName} ({limit}). La tabla muestra cuánto vale en {targetNameA} y en {targetNameB} cada día; la recomendación no cambia con el cambio.",
      sourceNames: { BRL: "reales", USD: "dólar", EUR: "euro" },
      currency: { BRL: "Real (BRL)", USD: "Dólar (USD)", EUR: "Euro (EUR)" },
      limitToday: "Límite hoy: {value}",
      date: "Fecha",
      rate: "Cotización",
      variation: "Variación",
      limitIn: "Límite en {currency}",
    },
  },
} as const;

const appTranslations = {
  pt: translations_pt_app.app,
  en: translations.en.app,
  es: translations_es_app.app,
};

const exposureRiskHistoryTranslations = {
  pt: {
    countryTitle: "Histórico do risco país · {country}",
    sectorTitle: "Histórico do risco setorial · {sector}",
    sectorTitleNoName: "Histórico do risco setorial",
    periodsLabel: "Período do histórico",
    periods: { y1: "1 ano", y3: "3 anos", y5: "5 anos", all: "Tudo" },
    stableSince: "— Estável desde {year} T{quarter}",
    stableShort: "— Estável",
    tooltipPeriod: "{year} T{quarter}",
    improved: "↗ Melhorou em {year} T{quarter}",
    worsened: "↘ Piorou em {year} T{quarter} ({from} → {to})",
    legendBase: "Cada degrau é uma revisão trimestral da Coface.",
    changeImproved: "Melhorou de {from} para {to} em {year} T{quarter}.",
    changeWorsened: "Piorou de {from} para {to} em {year} T{quarter}.",
    countryStable: "A nota se manteve em {level} no período.",
    sectorStable: "{sector} em {region} se mantém em risco {level} no período.",
    sectorStableNoRegion: "{sector} se mantém em risco {level} no período.",
    empty: "Ainda sem histórico",
    chartAria: "{title}. Nota atual: {level}",
    levelsLower: { L: "baixo", M: "médio", H: "alto", VH: "muito alto" },
  },
  en: {
    countryTitle: "Country risk history · {country}",
    sectorTitle: "Sector risk history · {sector}",
    sectorTitleNoName: "Sector risk history",
    periodsLabel: "History period",
    periods: { y1: "1 year", y3: "3 years", y5: "5 years", all: "All" },
    stableSince: "— Stable since {year} Q{quarter}",
    stableShort: "— Stable",
    tooltipPeriod: "{year} Q{quarter}",
    improved: "↗ Improved in {year} Q{quarter}",
    worsened: "↘ Worsened in {year} Q{quarter} ({from} → {to})",
    legendBase: "Each step is a quarterly Coface review.",
    changeImproved: "Improved from {from} to {to} in {year} Q{quarter}.",
    changeWorsened: "Worsened from {from} to {to} in {year} Q{quarter}.",
    countryStable: "The rating stayed at {level} in the period.",
    sectorStable: "{sector} in {region} remains at {level} risk in the period.",
    sectorStableNoRegion: "{sector} remains at {level} risk in the period.",
    empty: "No history yet",
    chartAria: "{title}. Current level: {level}",
    levelsLower: { L: "low", M: "medium", H: "high", VH: "very high" },
  },
  es: {
    countryTitle: "Historial del riesgo país · {country}",
    sectorTitle: "Historial del riesgo sectorial · {sector}",
    sectorTitleNoName: "Historial del riesgo sectorial",
    periodsLabel: "Período del historial",
    periods: { y1: "1 año", y3: "3 años", y5: "5 años", all: "Todo" },
    stableSince: "— Estable desde {year} T{quarter}",
    stableShort: "— Estable",
    tooltipPeriod: "{year} T{quarter}",
    improved: "↗ Mejoró en {year} T{quarter}",
    worsened: "↘ Empeoró en {year} T{quarter} ({from} → {to})",
    legendBase: "Cada escalón es una revisión trimestral de Coface.",
    changeImproved: "Mejoró de {from} a {to} en {year} T{quarter}.",
    changeWorsened: "Empeoró de {from} a {to} en {year} T{quarter}.",
    countryStable: "La nota se mantuvo en {level} en el período.",
    sectorStable:
      "{sector} en {region} se mantiene en riesgo {level} en el período.",
    sectorStableNoRegion:
      "{sector} se mantiene en riesgo {level} en el período.",
    empty: "Aún sin historial",
    chartAria: "{title}. Nivel actual: {level}",
    levelsLower: { L: "bajo", M: "medio", H: "alto", VH: "muy alto" },
  },
} as const;

Object.assign(
  appTranslations.pt.companyDetail.creditAnalysis.detail,
  creditDetailDetailTranslations.pt,
);
Object.assign(
  appTranslations.en.companyDetail.creditAnalysis.detail,
  creditDetailDetailTranslations.en,
);
Object.assign(
  appTranslations.es.companyDetail.creditAnalysis.detail,
  creditDetailDetailTranslations.es,
);
Object.assign(appTranslations.pt.companyDetail.exposureRisk, {
  history: exposureRiskHistoryTranslations.pt,
});
Object.assign(appTranslations.en.companyDetail.exposureRisk, {
  history: exposureRiskHistoryTranslations.en,
});
Object.assign(appTranslations.es.companyDetail.exposureRisk, {
  history: exposureRiskHistoryTranslations.es,
});

const exposureRiskMapTranslations = {
  pt: {
    title: "Mapa de risco país · Coface",
    openFull: "Abrir mapa de risco completo",
    modesLabel: "Modo do mapa",
    modeCountry: "Risco país",
    modeBusiness: "Ambiente de negócios",
    thisCompanyCountry: "País desta empresa",
    countryRisk: "Risco país",
    businessClimate: "Ambiente de negócios",
    region: "Região",
    continent: "Continente",
    hint: "Passe o mouse sobre outros países para comparar. Clique para ver o detalhe do país.",
    thisCompany: "{level} · esta empresa",
    lowerRisk: "Menor risco",
    higherRisk: "Maior risco",
    noData: "s/ dado",
    mapAria: "Mapa-múndi colorido pelo {mode} da Coface",
    detailTitle: "Detalhe do país",
  },
  en: {
    title: "Country risk map · Coface",
    openFull: "Open full risk map",
    modesLabel: "Map mode",
    modeCountry: "Country risk",
    modeBusiness: "Business climate",
    thisCompanyCountry: "Country of this company",
    countryRisk: "Country risk",
    businessClimate: "Business climate",
    region: "Region",
    continent: "Continent",
    hint: "Hover over other countries to compare. Click to see the country details.",
    thisCompany: "{level} · this company",
    lowerRisk: "Lower risk",
    higherRisk: "Higher risk",
    noData: "no data",
    mapAria: "World map colored by Coface {mode}",
    detailTitle: "Country details",
  },
  es: {
    title: "Mapa de riesgo país · Coface",
    openFull: "Abrir el mapa de riesgo completo",
    modesLabel: "Modo del mapa",
    modeCountry: "Riesgo país",
    modeBusiness: "Entorno de negocios",
    thisCompanyCountry: "País de esta empresa",
    countryRisk: "Riesgo país",
    businessClimate: "Entorno de negocios",
    region: "Región",
    continent: "Continente",
    hint: "Pase el mouse sobre otros países para comparar. Haga clic para ver el detalle del país.",
    thisCompany: "{level} · esta empresa",
    lowerRisk: "Menor riesgo",
    higherRisk: "Mayor riesgo",
    noData: "s/ dato",
    mapAria: "Mapa mundial coloreado por el {mode} de Coface",
    detailTitle: "Detalle del país",
  },
} as const;

Object.assign(appTranslations.pt.companyDetail.exposureRisk, {
  map: exposureRiskMapTranslations.pt,
});
Object.assign(appTranslations.en.companyDetail.exposureRisk, {
  map: exposureRiskMapTranslations.en,
});
Object.assign(appTranslations.es.companyDetail.exposureRisk, {
  map: exposureRiskMapTranslations.es,
});

const kycRegistry = {
  cnpj: "CNPJ",
  legalName: "Razão social",
  tradeName: "Nome fantasia",
  opening: "Abertura",
  openingValue: "{date} · {years} anos",
  legalNature: "Natureza jurídica",
  taxRegime: "Regime tributário",
  capital: "Capital social",
  headquarters: "Sede",
  isHeadquarters: "é sede",
  isBranch: "filial",
  mainActivity: "Atividade principal",
};
const kycInfluence = {
  lawsuits: "PROCESSOS",
  total: "Total",
  asAuthor: "Como autor",
  asDefendant: "Como réu",
  last365: "Últimos 365 dias",
  media: "MÍDIA",
  news: "Notícias",
  celebrity: "Nível de celebridade",
  exposure: "Nível de exposição",
  political: "POLÍTICO",
  pep: "PEP",
  donations: "Doações realizadas",
  elections: "Eleições",
  inOffice: "Em cargo atual",
  business: "NEGÓCIOS",
  ownerships: "Participações em empresas",
};
const kycContact = {
  emails: "E-mails",
  phones: "Telefones",
  addresses: "Endereços",
  more: "+{n}",
  viewAll: "Ver todos →",
  footer:
    "Contatos aparecem mascarados por padrão (LGPD). Ver todos registra o acesso no log de auditoria.",
  dialogTitle: "Registro e contatos",
  dialogDescription: "Este acesso foi registrado no log de auditoria.",
  revealError: "Não foi possível registrar o acesso. Tente novamente.",
};
const kycTranslations = {
  pt: {
    title: "KYC · Conheça seu cliente",
    brazilOnly: "Só Brasil",
    lastRun: "Última execução: {date}",
    ok: "Sem pendências",
    attention: "Requer atenção",
    noData: "Sem dados",
    notRun: "KYC ainda não executado para esta empresa.",
    kpi: {
      registry: "Dados cadastrais",
      registryActive: "CNPJ regular na Receita",
      registryOther: "Situação cadastral: {status}",
      contacts: "Registro e contatos",
      contactsDetail: "e-mails, telefones e endereços",
      lawsuits: "Processos judiciais",
      lawsuitsDetail: "como réu · quadro societário: {m} como réu",
      lawsuitsOwnersOnly: "quadro societário: {m} como réu",
      lawsuitsCompanyOnly: "como réu",
      lawsuitsAria: "Abrir processos judiciais",
      political: "Exposição política e mídia",
      pep: "{n} PEP",
      politicalDetail: "{n} notícias · exposição {level}",
    },
    registry: { title: "Dados cadastrais", ...kycRegistry },
    influence: { title: "Influência do quadro societário", ...kycInfluence },
    levels: {
      masculine: {
        veryHigh: "Muito alto",
        high: "Alto",
        medium: "Médio",
        low: "Baixo",
        veryLow: "Muito baixo",
      },
      feminine: {
        veryHigh: "muito alta",
        high: "alta",
        medium: "média",
        low: "baixa",
        veryLow: "muito baixa",
      },
    },
    contacts: { title: "Registro e contatos", ...kycContact },
    unavailable: {
      title: "KYC disponível apenas para empresas brasileiras",
      text: "Dados cadastrais, processos, exposição política e mídia só existem para empresas com CNPJ. Para empresas fora do Brasil, use os dados de Sócios Coface.",
      link: "Ver Sócios Coface →",
    },
  },
  en: {
    title: "KYC · Know your customer",
    brazilOnly: "Brazil only",
    lastRun: "Last run: {date}",
    ok: "No issues",
    attention: "Requires attention",
    noData: "No data",
    notRun: "KYC has not yet been run for this company.",
    kpi: {
      registry: "Registration data",
      registryActive: "CNPJ is regular",
      registryOther: "Registration status: {status}",
      contacts: "Registration and contacts",
      contactsDetail: "emails, phones and addresses",
      lawsuits: "Lawsuits",
      lawsuitsDetail: "as defendant · ownership: {m} as defendant",
      lawsuitsOwnersOnly: "ownership: {m} as defendant",
      lawsuitsCompanyOnly: "as defendant",
      lawsuitsAria: "Open lawsuits",
      political: "Political and media exposure",
      pep: "{n} PEP",
      politicalDetail: "{n} news items · {level} exposure",
    },
    registry: {
      title: "Registration data",
      cnpj: "CNPJ",
      legalName: "Legal name",
      tradeName: "Trade name",
      opening: "Incorporation",
      openingValue: "{date} · {years} years",
      legalNature: "Legal nature",
      taxRegime: "Tax regime",
      capital: "Share capital",
      headquarters: "Headquarters",
      isHeadquarters: "headquarters",
      isBranch: "branch",
      mainActivity: "Main activity",
    },
    influence: {
      title: "Ownership influence",
      lawsuits: "LAWSUITS",
      total: "Total",
      asAuthor: "As claimant",
      asDefendant: "As defendant",
      last365: "Last 365 days",
      media: "MEDIA",
      news: "News",
      celebrity: "Celebrity level",
      exposure: "Exposure level",
      political: "POLITICAL",
      pep: "PEP",
      donations: "Donations made",
      elections: "Elections",
      inOffice: "Currently in office",
      business: "BUSINESS",
      ownerships: "Company ownerships",
    },
    levels: {
      masculine: {
        veryHigh: "Very high",
        high: "High",
        medium: "Medium",
        low: "Low",
        veryLow: "Very low",
      },
      feminine: {
        veryHigh: "very high",
        high: "high",
        medium: "medium",
        low: "low",
        veryLow: "very low",
      },
    },
    contacts: {
      title: "Registration and contacts",
      emails: "Emails",
      phones: "Phones",
      addresses: "Addresses",
      more: "+{n}",
      viewAll: "View all →",
      footer:
        "Contacts are masked by default (LGPD). View all records the access in the audit log.",
      dialogTitle: "Registration and contacts",
      dialogDescription: "This access was recorded in the audit log.",
      revealError: "Unable to record access. Please try again.",
    },
    unavailable: {
      title: "KYC is available only for Brazilian companies",
      text: "Registration data, lawsuits, political exposure and media are available only for companies with a CNPJ. For companies outside Brazil, use Coface Partners data.",
      link: "View Coface Partners →",
    },
  },
  es: {
    title: "KYC · Conozca a su cliente",
    brazilOnly: "Solo Brasil",
    lastRun: "Última ejecución: {date}",
    ok: "Sin pendientes",
    attention: "Requiere atención",
    noData: "Sin datos",
    notRun: "KYC aún no se ejecutó para esta empresa.",
    kpi: {
      registry: "Datos registrales",
      registryActive: "CNPJ regular",
      registryOther: "Situación registral: {status}",
      contacts: "Registro y contactos",
      contactsDetail: "correos, teléfonos y direcciones",
      lawsuits: "Procesos judiciales",
      lawsuitsDetail: "como demandado · socios: {m} como demandado",
      lawsuitsOwnersOnly: "socios: {m} como demandado",
      lawsuitsCompanyOnly: "como demandado",
      lawsuitsAria: "Abrir procesos judiciales",
      political: "Exposición política y mediática",
      pep: "{n} PEP",
      politicalDetail: "{n} noticias · exposición {level}",
    },
    registry: {
      title: "Datos registrales",
      cnpj: "CNPJ",
      legalName: "Razón social",
      tradeName: "Nombre comercial",
      opening: "Constitución",
      openingValue: "{date} · {years} años",
      legalNature: "Naturaleza jurídica",
      taxRegime: "Régimen tributario",
      capital: "Capital social",
      headquarters: "Sede",
      isHeadquarters: "es sede",
      isBranch: "sucursal",
      mainActivity: "Actividad principal",
    },
    influence: {
      title: "Influencia del cuadro societario",
      lawsuits: "PROCESOS",
      total: "Total",
      asAuthor: "Como demandante",
      asDefendant: "Como demandado",
      last365: "Últimos 365 días",
      media: "MEDIOS",
      news: "Noticias",
      celebrity: "Nivel de celebridad",
      exposure: "Nivel de exposición",
      political: "POLÍTICO",
      pep: "PEP",
      donations: "Donaciones realizadas",
      elections: "Elecciones",
      inOffice: "En cargo actual",
      business: "NEGOCIOS",
      ownerships: "Participaciones en empresas",
    },
    levels: {
      masculine: {
        veryHigh: "Muy alto",
        high: "Alto",
        medium: "Medio",
        low: "Bajo",
        veryLow: "Muy bajo",
      },
      feminine: {
        veryHigh: "muy alta",
        high: "alta",
        medium: "media",
        low: "baja",
        veryLow: "muy baja",
      },
    },
    contacts: {
      title: "Registro y contactos",
      emails: "Correos",
      phones: "Teléfonos",
      addresses: "Direcciones",
      more: "+{n}",
      viewAll: "Ver todos →",
      footer:
        "Los contactos aparecen enmascarados por defecto (LGPD). Ver todos registra el acceso en el log de auditoría.",
      dialogTitle: "Registro y contactos",
      dialogDescription: "Este acceso fue registrado en el log de auditoría.",
      revealError: "No fue posible registrar el acceso. Inténtelo de nuevo.",
    },
    unavailable: {
      title: "KYC disponible solo para empresas brasileñas",
      text: "Los datos registrales, procesos, exposición política y medios solo existen para empresas con CNPJ. Para empresas fuera de Brasil, use los datos de Socios Coface.",
      link: "Ver Socios Coface →",
    },
  },
} as const;
Object.assign(kycTranslations.pt, { empty: "—" });
Object.assign(kycTranslations.en, { empty: "—" });
Object.assign(kycTranslations.es, { empty: "—" });
Object.assign(appTranslations.pt.companyDetail.exposureRisk, {
  kyc: kycTranslations.pt,
});
Object.assign(appTranslations.en.companyDetail.exposureRisk, {
  kyc: kycTranslations.en,
});
Object.assign(appTranslations.es.companyDetail.exposureRisk, {
  kyc: kycTranslations.es,
});

const alertsTabPt: AlertsTabTranslations = {
  subTabs: {
    summary: "Resumo",
    protestsDebts: "Protestos e dívidas",
    lawsuits: "Processos",
    mediaReputation: "Mídia e reputação",
  },
  navAria: "Seções da aba Alertas",
  sources: "Fontes: Quod · cartórios · PGFN · atualizado {date}",
  liabilityRestricted:
    "Passivo em processos disponível apenas para Owner, Admin e Colaborador.",
  summary: {
    panel: "Painel de alertas",
    active: "alertas ativos",
    activeTitle: "Alertas ativos",
    noExecution: "nenhuma verificação BigDataCorp realizada",
    noActive: "Nenhum alerta ativo nesta consulta.",
    checked: "em {n} verificações",
    checkedSingular: "em 1 verificação",
    cleanChecks: "{n} verificações sem ocorrência: {list}.",
    lastChecked: "Última verificação: {date}",
    nextDays: " · próxima em {n} dias (monitoramento)",
    nextToday: " · próxima hoje (monitoramento)",
    protestsDebts: "Protestos e dívidas",
    liability: "Passivo em processos",
    distribution: "Distribuição de processos",
    media: "Exposição na mídia",
    reputation: "Avaliações e reputação",
    liabilityError: "Não foi possível consultar os processos.",
    liabilityEmpty: "Nenhum valor expressivo de processos foi localizado.",
    liabilitySource: "Fonte: comunicações processuais (PJe).",
    bdcSource:
      "Fonte: BigDataCorp. Os números de processos também aparecem no KYC, em Exposição e risco.",
    overview: "Visão geral",
    detailed: "Detalhada",
    recentNews: "Notícias recentes",
    newsUnavailable:
      "As notícias detalhadas não vieram nesta consulta. Os totais acima são da BigDataCorp.",
    brazilOnly:
      "Alertas e reputação só estão disponíveis para empresas brasileiras.",
    records: "Registros em sites de reclamação e avaliação",
    noNegative: "Sem evidências negativas em sites de reclamação e avaliação.",
    totalValue: "Valor total identificado",
    sumCauses: "Soma das causas em R$ · {n} processos",
    sumCausesSingular: "Soma das causas em R$ · 1 processo",
    defendant: "Como réu (polo passivo)",
    author: "Como autor (polo ativo)",
    processes: "{n} processos",
    processesSingular: "1 processo",
    valueFloor:
      "O total é um piso: o valor aparece em {p}% dos processos ({a} de {b}). Processos sem valor não entram na soma.",
    party: "POR POLO",
    status: "POR STATUS",
    courtType: "POR TIPO DE JUSTIÇA",
    noBdcExecution: "Ainda não há consulta BigDataCorp para esta empresa.",
    activeSingular: "alerta ativo",
    checksClean: "{n} verificações · {m} sem ocorrência",
    connector: " e ",
    newsCount: "{n} notícias",
    newsCountSingular: "1 notícia",
    viewSection: "Ver {section}",
    mediaExposure: "Nível de exposição",
    celebrity: "Celebridade",
    last30: "Últimos 30 dias",
    last365: "Últimos 365 dias",
  },
  alerts: {
    "alerts.protests.title": "Protestos",
    "alerts.protests.main": "{n} protestos · {value}",
    "alerts.protests.mainWithoutValue": "{n} protestos",
    "alerts.protests.description.statesCount":
      "Títulos protestados em {k} estados.",
    "alerts.protests.link": "Ver protestos",
    "alerts.pgfn.title": "Dívida ativa (PGFN)",
    "alerts.pgfn.main": "Certidão positiva · {value}",
    "alerts.pgfn.description":
      "A empresa consta na lista de devedores da PGFN.",
    "alerts.pgfn.link": "Ver PGFN",
    "alerts.debtors.title": "Devedores do governo",
    "alerts.debtors.main": "{n} débitos · {value}",
    "alerts.debtors.description": "Débitos com a União, estados ou municípios.",
    "alerts.debtors.link": "Ver débitos",
    "alerts.collections.title": "Cobranças",
    "alerts.collections.main.inCollection": "Em cobrança há {m} meses",
    "alerts.collections.main.last90": "{n} ocorrências nos últimos 90 dias",
    "alerts.collections.description":
      "Ocorrências de cobrança registradas no mercado.",
    "alerts.collections.link": "Ver cobranças",
    "alerts.appointments.title": "Apontamentos (Quod)",
    "alerts.appointments.main": "{n} apontamentos ativos",
    "alerts.appointments.description":
      "Restrições financeiras ativas no Cadastro Positivo.",
    "alerts.appointments.link": "Ver apontamentos",
    "alerts.negativeFlag.title": "Indicador negativo (Quod)",
    "alerts.negativeFlag.main": "Indicador negativo ativo",
    "alerts.negativeFlag.description": "Há restrição financeira registrada.",
    "alerts.negativeFlag.link": "Ver indicadores",
    "alerts.lawsuits.title": "Processos judiciais",
    "alerts.lawsuits.main.withLiability":
      "{n} processos como réu · passivo identificado de {value}",
    "alerts.lawsuits.main.withoutLiability": "{n} processos como réu",
    "alerts.lawsuits.description":
      "O valor é um piso: só {pct}% dos processos informam valor.",
    "alerts.lawsuits.link": "Ver processos",
    "alerts.inquiries.title": "Consultas ao CNPJ (Quod)",
    "alerts.inquiries.main": "{n} consultas de crédito nos últimos 30 dias",
    "alerts.inquiries.description":
      "Volume acima do habitual pode indicar que a empresa está buscando crédito em outros fornecedores ou bancos.",
    "alerts.inquiries.link": "Ver consultas",
    "alerts.media.title": "Mídia",
    "alerts.media.main.withExposure":
      "{n} notícias nos últimos 30 dias · exposição {level}",
    "alerts.media.main.withoutExposure": "{n} notícias nos últimos 30 dias",
    "alerts.media.description.negativeCount": "{n} notícias com tom negativo.",
    "alerts.media.description.noNegative":
      "Nenhuma notícia negativa identificada.",
    "alerts.media.link": "Ver notícias",
    "alerts.reputation.title": "Reputação",
    "alerts.reputation.main":
      "{n} registros em sites de reclamação e avaliação",
    "alerts.reputation.description":
      "Confira as evidências na sub-aba Mídia e reputação.",
    "alerts.reputation.link": "Ver reputação",
    all: "Todos · {n}",
    high: "Alta",
    medium: "Média",
    low: "Baixa",
    consulted: "Consulta de {date}",
    emptySeverity: "Nenhum alerta de severidade {severity}.",
  },
  tiles: {
    protests: "Protestos",
    pgfn: "PGFN / Dívida ativa",
    debtors: "Devedores do governo",
    collections: "Cobranças",
    restrictive: "Dados restritivos",
    negative: "Indicadores negativos",
    states: "Estados consultados",
    registries: "Cartórios",
    all: "Todos",
    status: "Status",
    regular: "Regular",
    irregular: "Irregular",
    issued: "Certidão emitida em {date}",
    debtCount: "{n} débitos",
    debtCountSingular: "1 débito",
    debtCountLabel: "Qtd. débitos",
    debtTotal: "Total da dívida",
    lastWindows: "Últimos 30 / 90 dias",
    oldWindows: "180 / 365 dias",
    inquiries: "Consultas 30 / 60 / 90 dias",
    more90: "Mais de 90 dias",
    minimumRecord: "Cadastro mínimo",
    inquiryIndicator: "Indicador de consulta",
    yes: "Sim",
    no: "Não",
    noDebts: "Sem débitos",
    nothingFound: "Nada consta",
    found: "Consta",
    occurrences: "ocorrências de cobrança",
    withoutNegative: "sem indicador negativo",
    withNegative: "com indicador negativo",
    sourceQuod: "Quod",
    sourcePgfn: "PGFN",
    sourceRegistry: "Cartórios",
    notConsulted: "Não consultado",
    unavailable: "Indisponível nesta consulta.",
    notYetConsulted: "Ainda não há consulta para esta empresa.",
    protestedValue: "{value} em títulos protestados",
    appointmentsCount: "{n} apontamentos",
    appointmentsSingular: "1 apontamento",
    inquiries30: "{n} consultas em 30 dias",
    inquiries30Singular: "1 consulta em 30 dias",
    governmentLevels: "Federal, estadual e municipal",
  },
  common: {
    protests: "protestos",
    pgfn: "dívida ativa (PGFN)",
    debtors: "devedores do governo",
    collections: "cobranças",
    appointments: "apontamentos",
    negativeFlag: "indicador negativo",
    lawsuits: "processos judiciais",
    inquiries: "consultas ao CNPJ",
    media: "mídia",
    reputation: "reputação",
    "lawsuits.distribution.party.claimed": "Reclamante",
    "lawsuits.distribution.party.claimant": "Reclamante",
    "lawsuits.distribution.party.defendant": "Réu",
    "lawsuits.distribution.party.defendantMultiple": "Réu",
    "lawsuits.distribution.party.author": "Autor",
    "lawsuits.distribution.party.thirdParty": "Terceiro",
    "lawsuits.distribution.party.interested": "Interessado",
    "lawsuits.distribution.party.witness": "Testemunha",
    "lawsuits.distribution.party.other": "Outros",
    "lawsuits.distribution.status.active": "Em andamento",
    "lawsuits.distribution.status.archived": "Arquivado",
    "lawsuits.distribution.status.closed": "Encerrado",
    "lawsuits.distribution.status.pending": "Pendente",
    "lawsuits.distribution.status.suspended": "Suspenso",
    "lawsuits.distribution.status.finished": "Finalizado",
    "lawsuits.distribution.status.processed": "Processado",
    "lawsuits.distribution.status.distributed": "Distribuído",
    "lawsuits.distribution.court.federal": "Federal",
    "lawsuits.distribution.court.state": "Estadual",
    "lawsuits.distribution.court.civil": "Cível",
    "lawsuits.distribution.court.labor": "Trabalhista",
    "lawsuits.distribution.court.electoral": "Eleitoral",
    "lawsuits.distribution.court.military": "Militar",
    "lawsuits.distribution.court.constitutional": "Constitucional",
    "lawsuits.distribution.court.superior": "Superior",
    "lawsuits.observations.courtType": "{pct}% são {label}",
    "lawsuits.observations.fromHeadquarters": "{pct}% na UF da sede",
    "lawsuits.observations.byState": "{pct}% em {ufName}",
    "lawsuits.observations.fiscal": "{n} execuções fiscais",
    levels: {
      feminine: {
        veryHigh: "muito alta",
        high: "alta",
        medium: "média",
        low: "baixa",
        veryLow: "muito baixa",
      },
      masculine: {
        veryHigh: "Muito alto",
        high: "Alto",
        medium: "Médio",
        low: "Baixo",
        veryLow: "Muito baixo",
      },
    },
  },
  protestsDebts: {
    stripSummary: "{ok} de {n} verificações sem ocorrência",
    stripAttention: " · {k} ponto de atenção",
    stripAttentionPlural: " · {k} pontos de atenção",
    stripProtests: "Protestos",
    stripPgfn: "PGFN",
    stripDebtors: "Devedores do governo",
    stripRestrictive: "Dados restritivos",
    stripCollections: "Cobranças",
    stripNegative: "Indicadores negativos",
    registries: "Cartórios",
    noProtests: "Nenhum protesto",
    protestsCount: "{n} protestos",
    protestsCountSingular: "1 protesto",
    totalProtests: "Total de protestos",
    totalValue: "Valor total",
    statesConsulted: "Estados consultados",
    statesOf: "{a} de {b}",
    query: "Consulta",
    statesNoProtests: "Estados consultados · sem protestos",
    statesWithProtests: "Estados consultados · com protestos",
    footer:
      "Quando houver protestos, eles aparecem agrupados por estado e cartório, com data e valor de cada título.",
    footerHq: "{uf} é o estado da sede.",
    failure: "Falha na consulta",
    lastAttempt: "Última tentativa em {date}.",
    quodFallback:
      "Total informado pela Quod. O detalhe por estado e cartório não está disponível nesta consulta.",
    titlesCount: "{n} títulos",
    titlesCountSingular: "1 título",
    date: "Data",
    value: "Valor",
    pgfnTitle: "PGFN / Dívida ativa",
    nothingFound: "Nada consta",
    found: "Consta",
    totalDebt: "Total da dívida",
    emissionDate: "Data de emissão",
    certNegative: "Certidão negativa de débitos",
    certPositive: "Certidão positiva de débitos",
    validUntil: "Liberação PGFN: válida até {date}",
    debtorsTitle: "Devedores do governo",
    noDebts: "Sem débitos",
    debtsCount: "{n} débitos",
    debtsCountSingular: "1 débito",
    debtsQty: "Qtd. débitos",
    byOrigin: "Por origem",
    origin: "Origem",
    debts: "Débitos",
    federal: "Federal",
    state: "Estadual",
    municipal: "Municipal",
    restrictiveTitle: "Dados restritivos",
    inquiries30: "{n} consultas em 30 dias",
    inquiries30Singular: "1 consulta em 30 dias",
    appointments: "{n} apontamentos",
    appointmentsSingular: "1 apontamento",
    byPeriod: "Consultas por período",
    d30: "30 dias",
    d60: "60 dias",
    d90: "90 dias",
    more90: "+90 dias",
    periodNote:
      "Consultas acumuladas por janela. Pico recente pode indicar que a empresa está buscando crédito em vários lugares.",
    bySegment: "Consultas por segmento",
    others: "Outros",
    byType: "Apontamentos por tipo",
    noAppointments: "Nenhum apontamento registrado",
    judicialTitle: "Apontamentos judiciais",
    type: "Tipo",
    justice: "Justiça",
    author: "Autor",
    noJudicial: "Nenhum apontamento judicial encontrado",
    branchesTitle: "Consultas da empresa e filiais",
    cnpj: "CNPJ",
    c30: "30d",
    c60: "60d",
    c90: "90d",
    cMore90: "+90d",
    matrix: "matriz",
    branch: "filial",
    collectionsTitle: "Cobranças",
    noOccurrences: "Sem ocorrências",
    occurrencesCount: "{n} ocorrências",
    occurrencesSingular: "1 ocorrência",
    totalOccurrences: "Total de ocorrências",
    consecutiveMonths: "Meses consecutivos",
    inCollection: "Em cobrança",
    window: "Janela",
    occurrences: "Ocorrências",
    last30: "Últimos 30 dias",
    last90: "Últimos 90 dias",
    last180: "Últimos 180 dias",
    last365: "Últimos 365 dias",
    negativeTitle: "Indicadores negativos",
    noNegative: "Sem indicador negativo",
    hasNegative: "Indicador negativo",
    minRegister: "Cadastro mínimo",
    minYes: "A empresa tem dados cadastrais suficientes no Cadastro Positivo.",
    minNo:
      "A empresa não tem dados cadastrais suficientes no Cadastro Positivo.",
    inquiryIndicator: "Indicador de consulta",
    inqYes: "Houve consultas recentes ao CNPJ por outras empresas.",
    inqNo: "Sem consultas recentes ao CNPJ por outras empresas.",
    negativeIndicator: "Indicador negativo",
    negYes: "Há restrição financeira registrada.",
    negNo: "Nenhuma restrição financeira registrada.",
    noAlert: "Sem alerta",
    fallbackAfterFailure:
      "A consulta aos cartórios falhou (última tentativa em {date}).",
    ufHqAria: "{uf}, estado da sede",
    ufProtestedAria: "{uf}, com protestos",
    ufHqProtestedAria: "{uf}, estado da sede, com protestos",
  },
  lawsuits: {
    pillAlert: "{severity} · {n} como réu",
    pillNone: "Nenhum processo como réu",
    splitCount: "{n} processos · {p}% do valor",
    splitCountSingular: "1 processo · {p}% do valor",
    valueFloor:
      "O total é um piso: valores extraídos do texto das comunicações aparecem em {p}% dos processos ({a} de {b}). Processos sem valor não entram na soma.",
    observationsTitle: "O que observar",
    courtTitle: "{pct}% {type}",
    courtText: "{n} de {total} processos são {type}.",
    courtTextSingular: "1 de {total} processos é {type}.",
    courtTextOnly: "O único processo é {type}.",
    "courtPlural.federal": "federais",
    "courtPlural.state": "estaduais",
    "courtPlural.civil": "cíveis",
    "courtPlural.labor": "trabalhistas",
    "courtPlural.electoral": "eleitorais",
    "courtPlural.military": "militares",
    "courtPlural.constitutional": "constitucionais",
    "courtPlural.superior": "de tribunais superiores",
    stateTitle: "{pct}% em {ufName}",
    hqMain: "{n} processos na UF da sede",
    hqMainSingular: "1 processo na UF da sede",
    stateMain: "{n} processos em {uf}",
    stateMainSingular: "1 processo em {uf}",
    restItem: "{n} em {uf}",
    sentence: "{main}.",
    sentenceWithRest: "{main}; {rest}.",
    fiscalTitle: "{n} execuções fiscais",
    fiscalTitleSingular: "1 execução fiscal",
    fiscalText:
      "Cobrança de tributos pela Justiça Federal. Acompanhe junto com a PGFN.",
    viewAria: "Modo de exibição da distribuição",
    search: "Buscar categoria…",
    searchAria: "Buscar categoria",
    sectionAria: "Filtrar por seção",
    sectionAll: "Todas as seções",
    sectionParty: "Por polo",
    sectionStatus: "Por status",
    sectionCourt: "Por tipo de justiça",
    sectionState: "Por UF",
    sectionType: "Por tipo",
    tableState: "POR UF",
    tableType: "POR TIPO",
    categories: "{n} categorias",
    categoriesSingular: "1 categoria",
    category: "Categoria",
    quantity: "Quantidade",
    percent: "%",
    noCategory: "Nenhuma categoria encontrada.",
    distributionEmpty: "Nenhum processo na distribuição desta consulta.",
  },
  media: {
    pillExposure: "Exposição {level}",
    pillNoLevel: "Exposição não informada",
    totalCaption: "notícias nos últimos 12 meses",
    totalCaptionSingular: "notícia nos últimos 12 meses",
    exposureLevel: "Nível de exposição",
    exposureHint: "Quanto a empresa aparece na mídia",
    celebrityLevel: "Nível de celebridade",
    celebrityHint: "Alcance dos veículos que citam",
    newsUnit: "notícias",
    newsUnitSingular: "notícia",
    scale: "Escala de exposição",
    scaleAria: "Escala de exposição, nível atual: {level}",
    scaleAriaNone: "Escala de exposição, nível não informado",
    chartTitle: "Notícias por mês",
    chartAria: "Notícias por mês nos últimos 12 meses: {list}.",
    chartItemAria: "{month}/{year}, {n}",
    chartNote:
      "Barras escuras = últimos 30 dias. Picos de notícias costumam acompanhar fatos relevantes (expansão, mudança societária, processos).",
    newsTitle: "Notícias",
    periodAria: "Período das notícias",
    period30: "30 dias",
    period12: "12 meses",
    periodAll: "Todas",
    search: "Buscar por título ou fonte…",
    searchAria: "Buscar notícias por título ou fonte",
    open: "Abrir ↗",
    newTab: "(abre em nova aba)",
    noDate: "Sem data",
    range: "{a}–{b} de {n} notícias",
    rangeSingular: "{a}–{b} de 1 notícia",
    paginationAria: "Paginação das notícias",
    prevPage: "Página anterior",
    nextPage: "Próxima página",
    pageAria: "Página {n}",
    empty: "Nenhuma notícia encontrada.",
    reputationNone: "Sem registros",
    reputationCount: "{n} registros",
    reputationCountSingular: "1 registro",
    reputationBlock: "Sites de reclamação e avaliações públicas",
    reputationClean:
      "Nenhuma reclamação ou avaliação negativa registrada contra o CNPJ.",
    reputationFound: "{n} registros encontrados.",
    reputationFoundSingular: "1 registro encontrado.",
    reputationFooter:
      "Quando houver registros, cada evidência aparece com a fonte, a data e o resumo, e gera um alerta de severidade baixa ou média no Resumo.",
    months: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
  },
};

const alertsTabEn: AlertsTabTranslations = {
  subTabs: {
    summary: "Summary",
    protestsDebts: "Protests and debts",
    lawsuits: "Legal proceedings",
    mediaReputation: "Media and reputation",
  },
  navAria: "Alerts tab sections",
  sources: "Sources: Quod · registries · PGFN · updated {date}",
  liabilityRestricted:
    "Lawsuit liability is available only to Owner, Admin and Collaborator.",
  summary: {
    panel: "Alerts panel",
    active: "active alerts",
    activeSingular: "active alert",
    activeTitle: "Active alerts",
    noExecution: "no BigDataCorp check performed",
    noActive: "No active alerts in this check.",
    checked: "in {n} checks",
    checkedSingular: "in 1 check",
    cleanChecks: "{n} checks with no findings: {list}.",
    connector: " and ",
    lastChecked: "Last check: {date}",
    nextDays: " · next in {n} days (monitoring)",
    nextToday: " · next today (monitoring)",
    protestsDebts: "Protests and debts",
    liability: "Lawsuit liability",
    distribution: "Lawsuit distribution",
    media: "Media exposure",
    reputation: "Reviews and reputation",
    liabilityError: "Unable to retrieve lawsuits.",
    liabilityEmpty: "No material lawsuit amounts were found.",
    liabilitySource: "Source: court notices (PJe).",
    bdcSource:
      "Source: BigDataCorp. Lawsuit numbers also appear in KYC, under Exposure and risk.",
    overview: "Overview",
    detailed: "Detailed",
    recentNews: "Recent news",
    newsUnavailable:
      "Detailed news was not returned in this check. The totals above are from BigDataCorp.",
    brazilOnly:
      "Alerts and reputation are available only for Brazilian companies.",
    records: "Records on complaint and review sites",
    noNegative: "No negative evidence on complaint and review sites.",
    totalValue: "Total identified amount",
    sumCauses: "Sum of claims in R$ · {n} lawsuits",
    sumCausesSingular: "Sum of claims in R$ · 1 lawsuit",
    defendant: "As defendant",
    author: "As claimant",
    processes: "{n} lawsuits",
    processesSingular: "1 lawsuit",
    valueFloor:
      "The total is a floor: an amount is reported in {p}% of lawsuits ({a} of {b}). Lawsuits without an amount are not included in the sum.",
    party: "BY PARTY",
    status: "BY STATUS",
    courtType: "BY COURT TYPE",
    noBdcExecution: "There is no BigDataCorp check for this company yet.",
    checksClean: "{n} checks · {m} with no findings",
    newsCount: "{n} news items",
    newsCountSingular: "1 news item",
    viewSection: "View {section}",
    mediaExposure: "Exposure level",
    celebrity: "Celebrity",
    last30: "Last 30 days",
    last365: "Last 365 days",
  },
  alerts: {
    "alerts.protests.title": "Protests",
    "alerts.protests.main": "{n} protests · {value}",
    "alerts.protests.mainWithoutValue": "{n} protests",
    "alerts.protests.description.statesCount":
      "Protested titles in {k} states.",
    "alerts.protests.link": "View protests",
    "alerts.pgfn.title": "Tax debt (PGFN)",
    "alerts.pgfn.main": "Positive certificate · {value}",
    "alerts.pgfn.description": "The company appears on PGFN's debtor list.",
    "alerts.pgfn.link": "View PGFN",
    "alerts.debtors.title": "Government debtors",
    "alerts.debtors.main": "{n} debts · {value}",
    "alerts.debtors.description":
      "Debts with the federal, state, or municipal governments.",
    "alerts.debtors.link": "View debts",
    "alerts.collections.title": "Collections",
    "alerts.collections.main.inCollection": "In collection for {m} months",
    "alerts.collections.main.last90": "{n} occurrences in the last 90 days",
    "alerts.collections.description":
      "Collection occurrences recorded in the market.",
    "alerts.collections.link": "View collections",
    "alerts.appointments.title": "Entries (Quod)",
    "alerts.appointments.main": "{n} active entries",
    "alerts.appointments.description":
      "Active financial restrictions in the Positive Registry.",
    "alerts.appointments.link": "View entries",
    "alerts.negativeFlag.title": "Negative indicator (Quod)",
    "alerts.negativeFlag.main": "Active negative indicator",
    "alerts.negativeFlag.description":
      "There is a registered financial restriction.",
    "alerts.negativeFlag.link": "View indicators",
    "alerts.lawsuits.title": "Lawsuits",
    "alerts.lawsuits.main.withLiability":
      "{n} lawsuits as defendant · identified liability of {value}",
    "alerts.lawsuits.main.withoutLiability": "{n} lawsuits as defendant",
    "alerts.lawsuits.description":
      "The amount is a floor: only {pct}% of lawsuits report an amount.",
    "alerts.lawsuits.link": "View lawsuits",
    "alerts.inquiries.title": "CNPJ inquiries (Quod)",
    "alerts.inquiries.main": "{n} credit inquiries in the last 30 days",
    "alerts.inquiries.description":
      "An above-normal volume may indicate that the company is seeking credit from other suppliers or banks.",
    "alerts.inquiries.link": "View inquiries",
    "alerts.media.title": "Media",
    "alerts.media.main.withExposure":
      "{n} news items in the last 30 days · {level} exposure",
    "alerts.media.main.withoutExposure": "{n} news items in the last 30 days",
    "alerts.media.description.negativeCount":
      "{n} news items with a negative tone.",
    "alerts.media.description.noNegative": "No negative news identified.",
    "alerts.media.link": "View news",
    "alerts.reputation.title": "Reputation",
    "alerts.reputation.main": "{n} records on complaint and review sites",
    "alerts.reputation.description":
      "Check the evidence in the Media and reputation sub-tab.",
    "alerts.reputation.link": "View reputation",
    all: "All · {n}",
    high: "High",
    medium: "Medium",
    low: "Low",
    consulted: "Check on {date}",
    emptySeverity: "No {severity}-severity alerts.",
  },
  tiles: {
    protests: "Protests",
    pgfn: "PGFN / Tax debt",
    debtors: "Government debtors",
    collections: "Collections",
    restrictive: "Restrictive data",
    negative: "Negative indicators",
    states: "States checked",
    registries: "Registries",
    all: "All",
    status: "Status",
    regular: "Regular",
    irregular: "Irregular",
    issued: "Certificate issued on {date}",
    debtCount: "{n} debts",
    debtCountSingular: "1 debt",
    debtCountLabel: "Debt count",
    debtTotal: "Total debt",
    governmentLevels: "Federal, state and municipal",
    occurrences: "collection occurrences",
    lastWindows: "Last 30 / 90 days",
    oldWindows: "180 / 365 days",
    appointmentsCount: "{n} entries",
    appointmentsSingular: "1 entry",
    inquiries30: "{n} inquiries in 30 days",
    inquiries30Singular: "1 inquiry in 30 days",
    inquiries: "Inquiries 30 / 60 / 90 days",
    more90: "More than 90 days",
    yes: "Yes",
    no: "No",
    withoutNegative: "without negative indicator",
    withNegative: "with negative indicator",
    minimumRecord: "Minimum record",
    inquiryIndicator: "Inquiry indicator",
    sourceQuod: "Quod",
    sourcePgfn: "PGFN",
    sourceRegistry: "Registries",
    notConsulted: "Not checked",
    unavailable: "Unavailable in this check.",
    notYetConsulted: "There is no check for this company yet.",
    protestedValue: "{value} in protested titles",
    noDebts: "No debts",
    nothingFound: "Nothing found",
    found: "Found",
  },
  common: {
    protests: "protests",
    pgfn: "tax debt (PGFN)",
    debtors: "government debtors",
    collections: "collections",
    appointments: "entries",
    negativeFlag: "negative indicator",
    lawsuits: "lawsuits",
    inquiries: "CNPJ inquiries",
    media: "media",
    reputation: "reputation",
    "lawsuits.distribution.party.claimed": "Claimant",
    "lawsuits.distribution.party.claimant": "Claimant",
    "lawsuits.distribution.party.defendant": "Defendant",
    "lawsuits.distribution.party.defendantMultiple": "Defendant",
    "lawsuits.distribution.party.author": "Author",
    "lawsuits.distribution.party.thirdParty": "Third party",
    "lawsuits.distribution.party.interested": "Interested party",
    "lawsuits.distribution.party.witness": "Witness",
    "lawsuits.distribution.party.other": "Other",
    "lawsuits.distribution.status.active": "Active",
    "lawsuits.distribution.status.archived": "Archived",
    "lawsuits.distribution.status.closed": "Closed",
    "lawsuits.distribution.status.pending": "Pending",
    "lawsuits.distribution.status.suspended": "Suspended",
    "lawsuits.distribution.status.finished": "Finished",
    "lawsuits.distribution.status.processed": "Processed",
    "lawsuits.distribution.status.distributed": "Distributed",
    "lawsuits.distribution.court.federal": "Federal",
    "lawsuits.distribution.court.state": "State",
    "lawsuits.distribution.court.civil": "Civil",
    "lawsuits.distribution.court.labor": "Labor",
    "lawsuits.distribution.court.electoral": "Electoral",
    "lawsuits.distribution.court.military": "Military",
    "lawsuits.distribution.court.constitutional": "Constitutional",
    "lawsuits.distribution.court.superior": "Superior",
    "lawsuits.observations.courtType": "{pct}% are {label}",
    "lawsuits.observations.fromHeadquarters":
      "{pct}% in the headquarters state",
    "lawsuits.observations.byState": "{pct}% in {ufName}",
    "lawsuits.observations.fiscal": "{n} tax enforcement actions",
    levels: {
      feminine: {
        veryHigh: "very high",
        high: "high",
        medium: "medium",
        low: "low",
        veryLow: "very low",
      },
      masculine: {
        veryHigh: "Very high",
        high: "High",
        medium: "Medium",
        low: "Low",
        veryLow: "Very low",
      },
    },
  },
  protestsDebts: {
    stripSummary: "{ok} of {n} checks with no findings",
    stripAttention: " · {k} point of attention",
    stripAttentionPlural: " · {k} points of attention",
    stripProtests: "Protests",
    stripPgfn: "PGFN",
    stripDebtors: "Government debtors",
    stripRestrictive: "Restrictive data",
    stripCollections: "Collections",
    stripNegative: "Negative indicators",
    registries: "Registries",
    noProtests: "No protests",
    protestsCount: "{n} protests",
    protestsCountSingular: "1 protest",
    totalProtests: "Total protests",
    totalValue: "Total amount",
    statesConsulted: "States checked",
    statesOf: "{a} of {b}",
    query: "Checked on",
    statesNoProtests: "States checked · no protests",
    statesWithProtests: "States checked · with protests",
    footer:
      "When there are protests, they are grouped by state and registry, with the date and amount of each item.",
    footerHq: "{uf} is the headquarters state.",
    failure: "Check failed",
    lastAttempt: "Last attempt on {date}.",
    quodFallback:
      "Total reported by Quod. The breakdown by state and registry is not available in this check.",
    titlesCount: "{n} items",
    titlesCountSingular: "1 item",
    date: "Date",
    value: "Amount",
    pgfnTitle: "PGFN / Federal tax debt",
    nothingFound: "Nothing found",
    found: "Found",
    totalDebt: "Total debt",
    emissionDate: "Issue date",
    certNegative: "Clearance certificate (no debts)",
    certPositive: "Certificate with debts",
    validUntil: "PGFN clearance: valid until {date}",
    debtorsTitle: "Government debtors",
    noDebts: "No debts",
    debtsCount: "{n} debts",
    debtsCountSingular: "1 debt",
    debtsQty: "Debts",
    byOrigin: "By origin",
    origin: "Origin",
    debts: "Debts",
    federal: "Federal",
    state: "State",
    municipal: "Municipal",
    restrictiveTitle: "Restrictive data",
    inquiries30: "{n} inquiries in 30 days",
    inquiries30Singular: "1 inquiry in 30 days",
    appointments: "{n} records",
    appointmentsSingular: "1 record",
    byPeriod: "Inquiries by period",
    d30: "30 days",
    d60: "60 days",
    d90: "90 days",
    more90: "+90 days",
    periodNote:
      "Cumulative inquiries per window. A recent spike may indicate the company is seeking credit in several places.",
    bySegment: "Inquiries by segment",
    others: "Others",
    byType: "Records by type",
    noAppointments: "No records registered",
    judicialTitle: "Court records",
    type: "Type",
    justice: "Court",
    author: "Plaintiff",
    noJudicial: "No court records found",
    branchesTitle: "Inquiries for the company and branches",
    cnpj: "CNPJ",
    c30: "30d",
    c60: "60d",
    c90: "90d",
    cMore90: "+90d",
    matrix: "head office",
    branch: "branch",
    collectionsTitle: "Collections",
    noOccurrences: "No occurrences",
    occurrencesCount: "{n} occurrences",
    occurrencesSingular: "1 occurrence",
    totalOccurrences: "Total occurrences",
    consecutiveMonths: "Consecutive months",
    inCollection: "In collection",
    window: "Window",
    occurrences: "Occurrences",
    last30: "Last 30 days",
    last90: "Last 90 days",
    last180: "Last 180 days",
    last365: "Last 365 days",
    negativeTitle: "Negative indicators",
    noNegative: "No negative indicator",
    hasNegative: "Negative indicator",
    minRegister: "Minimum record",
    minYes:
      "The company has enough registration data in the Positive Credit Registry.",
    minNo:
      "The company does not have enough registration data in the Positive Credit Registry.",
    inquiryIndicator: "Inquiry indicator",
    inqYes: "Other companies recently looked up this CNPJ.",
    inqNo: "No recent lookups of this CNPJ by other companies.",
    negativeIndicator: "Negative indicator",
    negYes: "A financial restriction is registered.",
    negNo: "No financial restriction registered.",
    noAlert: "No alert",
    fallbackAfterFailure: "The registry check failed (last attempt on {date}).",
    ufHqAria: "{uf}, headquarters state",
    ufProtestedAria: "{uf}, with protests",
    ufHqProtestedAria: "{uf}, headquarters state, with protests",
  },
  lawsuits: {
    pillAlert: "{severity} · {n} as defendant",
    pillNone: "No lawsuits as defendant",
    splitCount: "{n} lawsuits · {p}% of the amount",
    splitCountSingular: "1 lawsuit · {p}% of the amount",
    valueFloor:
      "The total is a floor: amounts extracted from the text of court notices appear in {p}% of lawsuits ({a} of {b}). Lawsuits without an amount are not included in the sum.",
    observationsTitle: "What to watch",
    courtTitle: "{pct}% {type}",
    courtText: "{n} of {total} lawsuits are {type} cases.",
    courtTextSingular: "1 of {total} lawsuits is a {type} case.",
    courtTextOnly: "The only lawsuit is a {type} case.",
    "courtPlural.federal": "federal",
    "courtPlural.state": "state court",
    "courtPlural.civil": "civil",
    "courtPlural.labor": "labor",
    "courtPlural.electoral": "electoral",
    "courtPlural.military": "military",
    "courtPlural.constitutional": "constitutional",
    "courtPlural.superior": "higher court",
    stateTitle: "{pct}% in {ufName}",
    hqMain: "{n} lawsuits in the headquarters state",
    hqMainSingular: "1 lawsuit in the headquarters state",
    stateMain: "{n} lawsuits in {uf}",
    stateMainSingular: "1 lawsuit in {uf}",
    restItem: "{n} in {uf}",
    sentence: "{main}.",
    sentenceWithRest: "{main}; {rest}.",
    fiscalTitle: "{n} tax enforcement actions",
    fiscalTitleSingular: "1 tax enforcement action",
    fiscalText:
      "Tax collection through the Federal Courts. Review it together with PGFN.",
    viewAria: "Distribution display mode",
    search: "Search category…",
    searchAria: "Search category",
    sectionAria: "Filter by section",
    sectionAll: "All sections",
    sectionParty: "By party",
    sectionStatus: "By status",
    sectionCourt: "By court type",
    sectionState: "By state",
    sectionType: "By type",
    tableState: "BY STATE",
    tableType: "BY TYPE",
    categories: "{n} categories",
    categoriesSingular: "1 category",
    category: "Category",
    quantity: "Count",
    percent: "%",
    noCategory: "No categories found.",
    distributionEmpty: "No lawsuits in the distribution for this check.",
  },
  media: {
    pillExposure: "Exposure: {level}",
    pillNoLevel: "Exposure not reported",
    totalCaption: "news items in the last 12 months",
    totalCaptionSingular: "news item in the last 12 months",
    exposureLevel: "Exposure level",
    exposureHint: "How often the company appears in the media",
    celebrityLevel: "Celebrity level",
    celebrityHint: "Reach of the outlets that mention it",
    newsUnit: "news items",
    newsUnitSingular: "news item",
    scale: "Exposure scale",
    scaleAria: "Exposure scale, current level: {level}",
    scaleAriaNone: "Exposure scale, level not reported",
    chartTitle: "News by month",
    chartAria: "News by month over the last 12 months: {list}.",
    chartItemAria: "{month}/{year}, {n}",
    chartNote:
      "Dark bars = last 30 days. News spikes usually follow relevant events (expansion, ownership changes, lawsuits).",
    newsTitle: "News",
    periodAria: "News period",
    period30: "30 days",
    period12: "12 months",
    periodAll: "All",
    search: "Search by title or source…",
    searchAria: "Search news by title or source",
    open: "Open ↗",
    newTab: "(opens in a new tab)",
    noDate: "No date",
    range: "{a}–{b} of {n} news items",
    rangeSingular: "{a}–{b} of 1 news item",
    paginationAria: "News pagination",
    prevPage: "Previous page",
    nextPage: "Next page",
    pageAria: "Page {n}",
    empty: "No news found.",
    reputationNone: "No records",
    reputationCount: "{n} records",
    reputationCountSingular: "1 record",
    reputationBlock: "Complaint and public review websites",
    reputationClean:
      "No complaints or negative reviews recorded against the CNPJ.",
    reputationFound: "{n} records found.",
    reputationFoundSingular: "1 record found.",
    reputationFooter:
      "When there are records, each piece of evidence appears with its source, date and summary, and raises a low or medium severity alert in the Summary.",
    months: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
  },
};

const alertsTabEs: AlertsTabTranslations = {
  subTabs: {
    summary: "Resumen",
    protestsDebts: "Protestas y deudas",
    lawsuits: "Procesos",
    mediaReputation: "Medios y reputación",
  },
  navAria: "Secciones de la pestaña Alertas",
  sources: "Fuentes: Quod · registros · PGFN · actualizado {date}",
  liabilityRestricted:
    "El pasivo en procesos está disponible solo para Owner, Admin y Colaborador.",
  summary: {
    panel: "Panel de alertas",
    active: "alertas activas",
    activeSingular: "alerta activa",
    activeTitle: "Alertas activas",
    noExecution: "no se realizó ninguna verificación de BigDataCorp",
    noActive: "No hay alertas activas en esta consulta.",
    checked: "en {n} verificaciones",
    checkedSingular: "en 1 verificación",
    cleanChecks: "{n} verificaciones sin ocurrencias: {list}.",
    connector: " y ",
    lastChecked: "Última verificación: {date}",
    nextDays: " · próxima en {n} días (monitoreo)",
    nextToday: " · próxima hoy (monitoreo)",
    protestsDebts: "Protestas y deudas",
    liability: "Pasivo en procesos",
    distribution: "Distribución de procesos",
    media: "Exposición mediática",
    reputation: "Reseñas y reputación",
    liabilityError: "No fue posible consultar los procesos.",
    liabilityEmpty: "No se encontraron montos relevantes de procesos.",
    liabilitySource: "Fuente: comunicaciones procesales (PJe).",
    bdcSource:
      "Fuente: BigDataCorp. Los números de procesos también aparecen en KYC, en Exposición y riesgo.",
    overview: "Vista general",
    detailed: "Detallada",
    recentNews: "Noticias recientes",
    newsUnavailable:
      "Las noticias detalladas no llegaron en esta consulta. Los totales anteriores son de BigDataCorp.",
    brazilOnly:
      "Las alertas y la reputación solo están disponibles para empresas brasileñas.",
    records: "Registros en sitios de reclamaciones y reseñas",
    noNegative:
      "No hay evidencias negativas en sitios de reclamaciones y reseñas.",
    totalValue: "Valor total detectado",
    sumCauses: "Suma de causas en R$ · {n} procesos",
    sumCausesSingular: "Suma de causas en R$ · 1 proceso",
    defendant: "Como demandado",
    author: "Como demandante",
    processes: "{n} procesos",
    processesSingular: "1 proceso",
    valueFloor:
      "El total es un piso: el valor aparece en {p}% de los procesos ({a} de {b}). Los procesos sin valor no se incluyen en la suma.",
    party: "POR PARTE",
    status: "POR ESTADO",
    courtType: "POR TIPO DE JUSTICIA",
    noBdcExecution: "Todavía no hay consulta de BigDataCorp para esta empresa.",
    checksClean: "{n} verificaciones · {m} sin ocurrencias",
    newsCount: "{n} noticias",
    newsCountSingular: "1 noticia",
    viewSection: "Ver la sección {section}",
    mediaExposure: "Nivel de exposición",
    celebrity: "Celebridad",
    last30: "Últimos 30 días",
    last365: "Últimos 365 días",
  },
  alerts: {
    "alerts.protests.title": "Protestos",
    "alerts.protests.main": "{n} protestas · {value}",
    "alerts.protests.mainWithoutValue": "{n} protestas",
    "alerts.protests.description.statesCount":
      "Títulos protestados en {k} estados.",
    "alerts.protests.link": "Ver protestas",
    "alerts.pgfn.title": "Deuda activa (PGFN)",
    "alerts.pgfn.main": "Certificado positivo · {value}",
    "alerts.pgfn.description":
      "La empresa consta en la lista de deudores de la PGFN.",
    "alerts.pgfn.link": "Ver la PGFN",
    "alerts.debtors.title": "Deudores del gobierno",
    "alerts.debtors.main": "{n} deudas · {value}",
    "alerts.debtors.description":
      "Deudas con la Unión, los estados o municipios.",
    "alerts.debtors.link": "Ver deudas",
    "alerts.collections.title": "Cobranzas",
    "alerts.collections.main.inCollection": "En cobranza hace {m} meses",
    "alerts.collections.main.last90": "{n} ocurrencias en los últimos 90 días",
    "alerts.collections.description":
      "Ocurrencias de cobranza registradas en el mercado.",
    "alerts.collections.link": "Ver cobranzas",
    "alerts.appointments.title": "Anotaciones (Quod)",
    "alerts.appointments.main": "{n} anotaciones activas",
    "alerts.appointments.description":
      "Restricciones financieras activas en el Registro Positivo.",
    "alerts.appointments.link": "Ver anotaciones",
    "alerts.negativeFlag.title": "Indicador adverso (Quod)",
    "alerts.negativeFlag.main": "Indicador negativo activo",
    "alerts.negativeFlag.description":
      "Hay una restricción financiera registrada.",
    "alerts.negativeFlag.link": "Ver indicadores negativos",
    "alerts.lawsuits.title": "Procesos judiciales",
    "alerts.lawsuits.main.withLiability":
      "{n} procesos como demandado · pasivo identificado de {value}",
    "alerts.lawsuits.main.withoutLiability": "{n} procesos como demandado",
    "alerts.lawsuits.description":
      "El valor es un piso: solo {pct}% de los procesos informan valor.",
    "alerts.lawsuits.link": "Ver procesos",
    "alerts.inquiries.title": "Consultas al CNPJ (Quod)",
    "alerts.inquiries.main": "{n} consultas de crédito en los últimos 30 días",
    "alerts.inquiries.description":
      "Un volumen superior a lo habitual puede indicar que la empresa busca crédito en otros proveedores o bancos.",
    "alerts.inquiries.link": "Consultar consultas",
    "alerts.media.title": "Medios",
    "alerts.media.main.withExposure":
      "{n} noticias en los últimos 30 días · exposición {level}",
    "alerts.media.main.withoutExposure": "{n} noticias en los últimos 30 días",
    "alerts.media.description.negativeCount": "{n} noticias con tono negativo.",
    "alerts.media.description.noNegative":
      "No se identificaron noticias negativas.",
    "alerts.media.link": "Ver noticias",
    "alerts.reputation.title": "Reputación",
    "alerts.reputation.main":
      "{n} registros en sitios de reclamaciones y reseñas",
    "alerts.reputation.description":
      "Consulte las evidencias en la subpestaña Medios y reputación.",
    "alerts.reputation.link": "Ver reputación",
    all: "Todo · {n}",
    high: "Alta",
    medium: "Media",
    low: "Baja",
    consulted: "Verificación del {date}",
    emptySeverity: "No hay alertas de severidad {severity}.",
  },
  tiles: {
    protests: "Protestos",
    pgfn: "PGFN / Deuda activa",
    debtors: "Deudores del gobierno",
    collections: "Cobranzas",
    restrictive: "Datos restrictivos",
    negative: "Indicadores adversos",
    states: "Estados verificados",
    registries: "Registros",
    all: "Todos",
    status: "Estado",
    regular: "Regular",
    irregular: "Irregular",
    issued: "Certificado emitido el {date}",
    debtCount: "{n} deudas",
    debtCountSingular: "1 deuda",
    debtCountLabel: "Cant. de deudas",
    debtTotal: "Total de la deuda",
    governmentLevels: "Federal, estatal y municipal",
    occurrences: "ocurrencias de cobranza",
    lastWindows: "Últimos 30 / 90 días",
    oldWindows: "180 / 365 días",
    appointmentsCount: "{n} anotaciones",
    appointmentsSingular: "1 anotación",
    inquiries30: "{n} consultas en 30 días",
    inquiries30Singular: "1 consulta en 30 días",
    inquiries: "Consultas 30 / 60 / 90 días",
    more90: "Más de 90 días",
    yes: "Sí",
    no: "No",
    withoutNegative: "sin indicador negativo",
    withNegative: "con indicador negativo",
    minimumRecord: "Registro mínimo",
    inquiryIndicator: "Indicador de consultas",
    sourceQuod: "Quod",
    sourcePgfn: "PGFN",
    sourceRegistry: "Registros",
    notConsulted: "No consultado",
    unavailable: "No disponible en esta consulta.",
    notYetConsulted: "Todavía no hay consulta para esta empresa.",
    protestedValue: "{value} en títulos protestados",
    noDebts: "Sin deudas",
    nothingFound: "No se encontró nada",
    found: "Encontrado",
  },
  common: {
    protests: "protestos",
    pgfn: "deuda activa (PGFN)",
    debtors: "deudores del gobierno",
    collections: "cobranzas",
    appointments: "anotaciones",
    negativeFlag: "indicador adverso",
    lawsuits: "procesos judiciales",
    inquiries: "consultas al CNPJ",
    media: "medios",
    reputation: "reputación",
    "lawsuits.distribution.party.claimed": "Reclamante",
    "lawsuits.distribution.party.claimant": "Reclamante",
    "lawsuits.distribution.party.defendant": "Demandado",
    "lawsuits.distribution.party.defendantMultiple": "Demandado",
    "lawsuits.distribution.party.author": "Demandante",
    "lawsuits.distribution.party.thirdParty": "Tercero",
    "lawsuits.distribution.party.interested": "Interesado",
    "lawsuits.distribution.party.witness": "Testigo",
    "lawsuits.distribution.party.other": "Otros",
    "lawsuits.distribution.status.active": "En curso",
    "lawsuits.distribution.status.archived": "Archivado",
    "lawsuits.distribution.status.closed": "Cerrado",
    "lawsuits.distribution.status.pending": "Pendiente",
    "lawsuits.distribution.status.suspended": "Suspendido",
    "lawsuits.distribution.status.finished": "Concluido",
    "lawsuits.distribution.status.processed": "Procesado",
    "lawsuits.distribution.status.distributed": "Distribuido",
    "lawsuits.distribution.court.federal": "Federal",
    "lawsuits.distribution.court.state": "Estatal",
    "lawsuits.distribution.court.civil": "Civil",
    "lawsuits.distribution.court.labor": "Laboral",
    "lawsuits.distribution.court.electoral": "Electoral",
    "lawsuits.distribution.court.military": "Militar",
    "lawsuits.distribution.court.constitutional": "Constitucional",
    "lawsuits.distribution.court.superior": "Superior",
    "lawsuits.observations.courtType": "{pct}% son {label}",
    "lawsuits.observations.fromHeadquarters": "{pct}% en la UF de la sede",
    "lawsuits.observations.byState": "{pct}% en {ufName}",
    "lawsuits.observations.fiscal": "{n} ejecuciones fiscales",
    levels: {
      feminine: {
        veryHigh: "muy alta",
        high: "alta",
        medium: "media",
        low: "baja",
        veryLow: "muy baja",
      },
      masculine: {
        veryHigh: "Muy alto",
        high: "Alto",
        medium: "Medio",
        low: "Bajo",
        veryLow: "Muy bajo",
      },
    },
  },
  protestsDebts: {
    stripSummary: "{ok} de {n} verificaciones sin incidencias",
    stripAttention: " · {k} punto de atención",
    stripAttentionPlural: " · {k} puntos de atención",
    stripProtests: "Protestos",
    stripPgfn: "PGFN",
    stripDebtors: "Deudores del gobierno",
    stripRestrictive: "Datos restrictivos",
    stripCollections: "Cobranzas",
    stripNegative: "Indicadores negativos",
    registries: "Notarías",
    noProtests: "Ningún protesto",
    protestsCount: "{n} protestos",
    protestsCountSingular: "1 protesto",
    totalProtests: "Total de protestos",
    totalValue: "Valor total",
    statesConsulted: "Estados consultados",
    statesOf: "{a} de {b}",
    query: "Consulta",
    statesNoProtests: "Estados consultados · sin protestos",
    statesWithProtests: "Estados consultados · con protestos",
    footer:
      "Cuando haya protestos, aparecerán agrupados por estado y notaría, con la fecha y el valor de cada título.",
    footerHq: "{uf} es el estado de la sede.",
    failure: "Error en la consulta",
    lastAttempt: "Último intento el {date}.",
    quodFallback:
      "Total informado por Quod. El detalle por estado y notaría no está disponible en esta consulta.",
    titlesCount: "{n} títulos",
    titlesCountSingular: "1 título",
    date: "Fecha",
    value: "Valor",
    pgfnTitle: "PGFN / Deuda activa",
    nothingFound: "Sin registros",
    found: "Con registros",
    totalDebt: "Total de la deuda",
    emissionDate: "Fecha de emisión",
    certNegative: "Certificado negativo de deudas",
    certPositive: "Certificado positivo de deudas",
    validUntil: "Liberación PGFN: válida hasta {date}",
    debtorsTitle: "Deudores del gobierno",
    noDebts: "Sin deudas",
    debtsCount: "{n} deudas",
    debtsCountSingular: "1 deuda",
    debtsQty: "Cant. deudas",
    byOrigin: "Por origen",
    origin: "Origen",
    debts: "Deudas",
    federal: "Federal",
    state: "Estatal",
    municipal: "Municipal",
    restrictiveTitle: "Datos restrictivos",
    inquiries30: "{n} consultas en 30 días",
    inquiries30Singular: "1 consulta en 30 días",
    appointments: "{n} anotaciones",
    appointmentsSingular: "1 anotación",
    byPeriod: "Consultas por período",
    d30: "30 días",
    d60: "60 días",
    d90: "90 días",
    more90: "+90 días",
    periodNote:
      "Consultas acumuladas por ventana. Un pico reciente puede indicar que la empresa busca crédito en varios lugares.",
    bySegment: "Consultas por segmento",
    others: "Otros",
    byType: "Anotaciones por tipo",
    noAppointments: "Ninguna anotación registrada",
    judicialTitle: "Anotaciones judiciales",
    type: "Tipo",
    justice: "Justicia",
    author: "Demandante",
    noJudicial: "No se encontraron anotaciones judiciales",
    branchesTitle: "Consultas de la empresa y sucursales",
    cnpj: "CNPJ",
    c30: "30d",
    c60: "60d",
    c90: "90d",
    cMore90: "+90d",
    matrix: "matriz",
    branch: "sucursal",
    collectionsTitle: "Cobranzas",
    noOccurrences: "Sin incidencias",
    occurrencesCount: "{n} incidencias",
    occurrencesSingular: "1 incidencia",
    totalOccurrences: "Total de incidencias",
    consecutiveMonths: "Meses consecutivos",
    inCollection: "En cobranza",
    window: "Ventana",
    occurrences: "Incidencias",
    last30: "Últimos 30 días",
    last90: "Últimos 90 días",
    last180: "Últimos 180 días",
    last365: "Últimos 365 días",
    negativeTitle: "Indicadores negativos",
    noNegative: "Sin indicador negativo",
    hasNegative: "Indicador negativo",
    minRegister: "Registro mínimo",
    minYes:
      "La empresa tiene datos de registro suficientes en el Registro Positivo.",
    minNo:
      "La empresa no tiene datos de registro suficientes en el Registro Positivo.",
    inquiryIndicator: "Indicador de consulta",
    inqYes: "Otras empresas consultaron este CNPJ recientemente.",
    inqNo: "Sin consultas recientes a este CNPJ por otras empresas.",
    negativeIndicator: "Indicador negativo",
    negYes: "Hay una restricción financiera registrada.",
    negNo: "Ninguna restricción financiera registrada.",
    noAlert: "Sin alerta",
    fallbackAfterFailure:
      "La consulta a las notarías falló (último intento el {date}).",
    ufHqAria: "{uf}, estado de la sede",
    ufProtestedAria: "{uf}, con protestos",
    ufHqProtestedAria: "{uf}, estado de la sede, con protestos",
  },
  lawsuits: {
    pillAlert: "{severity} · {n} como demandado",
    pillNone: "Ningún proceso como demandado",
    splitCount: "{n} procesos · {p}% del valor",
    splitCountSingular: "1 proceso · {p}% del valor",
    valueFloor:
      "El total es un piso: los valores extraídos del texto de las comunicaciones aparecen en {p}% de los procesos ({a} de {b}). Los procesos sin valor no se incluyen en la suma.",
    observationsTitle: "Qué observar",
    courtTitle: "{pct}% {type}",
    courtText: "{n} de {total} procesos son {type}.",
    courtTextSingular: "1 de {total} procesos es {type}.",
    courtTextOnly: "El único proceso es {type}.",
    "courtPlural.federal": "federales",
    "courtPlural.state": "estatales",
    "courtPlural.civil": "civiles",
    "courtPlural.labor": "laborales",
    "courtPlural.electoral": "electorales",
    "courtPlural.military": "militares",
    "courtPlural.constitutional": "constitucionales",
    "courtPlural.superior": "de tribunales superiores",
    stateTitle: "{pct}% en {ufName}",
    hqMain: "{n} procesos en el estado de la sede",
    hqMainSingular: "1 proceso en el estado de la sede",
    stateMain: "{n} procesos en {uf}",
    stateMainSingular: "1 proceso en {uf}",
    restItem: "{n} en {uf}",
    sentence: "{main}.",
    sentenceWithRest: "{main}; {rest}.",
    fiscalTitle: "{n} ejecuciones fiscales",
    fiscalTitleSingular: "1 ejecución fiscal",
    fiscalText:
      "Cobro de tributos a través de la Justicia Federal. Revísela junto con la PGFN.",
    viewAria: "Modo de visualización de la distribución",
    search: "Buscar categoría…",
    searchAria: "Buscar categoría",
    sectionAria: "Filtrar por sección",
    sectionAll: "Todas las secciones",
    sectionParty: "Por parte",
    sectionStatus: "Por estado",
    sectionCourt: "Por tipo de justicia",
    sectionState: "Por UF",
    sectionType: "Por tipo",
    tableState: "POR UF",
    tableType: "POR TIPO",
    categories: "{n} categorías",
    categoriesSingular: "1 categoría",
    category: "Categoría",
    quantity: "Cantidad",
    percent: "%",
    noCategory: "No se encontraron categorías.",
    distributionEmpty: "No hay procesos en la distribución de esta consulta.",
  },
  media: {
    pillExposure: "Exposición {level}",
    pillNoLevel: "Exposición no informada",
    totalCaption: "noticias en los últimos 12 meses",
    totalCaptionSingular: "noticia en los últimos 12 meses",
    exposureLevel: "Nivel de exposición",
    exposureHint: "Cuánto aparece la empresa en los medios",
    celebrityLevel: "Nivel de celebridad",
    celebrityHint: "Alcance de los medios que la citan",
    newsUnit: "noticias",
    newsUnitSingular: "noticia",
    scale: "Escala de exposición",
    scaleAria: "Escala de exposición, nivel actual: {level}",
    scaleAriaNone: "Escala de exposición, nivel no informado",
    chartTitle: "Noticias por mes",
    chartAria: "Noticias por mes en los últimos 12 meses: {list}.",
    chartItemAria: "{month}/{year}, {n}",
    chartNote:
      "Barras oscuras = últimos 30 días. Los picos de noticias suelen acompañar hechos relevantes (expansión, cambios societarios, procesos).",
    newsTitle: "Noticias",
    periodAria: "Período de las noticias",
    period30: "30 días",
    period12: "12 meses",
    periodAll: "Todas",
    search: "Buscar por título o fuente…",
    searchAria: "Buscar noticias por título o fuente",
    open: "Abrir ↗",
    newTab: "(se abre en una pestaña nueva)",
    noDate: "Sin fecha",
    range: "{a}–{b} de {n} noticias",
    rangeSingular: "{a}–{b} de 1 noticia",
    paginationAria: "Paginación de las noticias",
    prevPage: "Página anterior",
    nextPage: "Página siguiente",
    pageAria: "Página {n}",
    empty: "No se encontraron noticias.",
    reputationNone: "Sin registros",
    reputationCount: "{n} registros",
    reputationCountSingular: "1 registro",
    reputationBlock: "Sitios de reclamos y reseñas públicas",
    reputationClean:
      "No hay reclamos ni reseñas negativas registrados contra el CNPJ.",
    reputationFound: "{n} registros encontrados.",
    reputationFoundSingular: "1 registro encontrado.",
    reputationFooter:
      "Cuando haya registros, cada evidencia aparece con la fuente, la fecha y el resumen, y genera una alerta de severidad baja o media en el Resumen.",
    months: [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ],
  },
};

Object.assign(appTranslations.pt.companyDetail, { alertsTab: alertsTabPt });
Object.assign(appTranslations.en.companyDetail, { alertsTab: alertsTabEn });
Object.assign(appTranslations.es.companyDetail, { alertsTab: alertsTabEs });
Object.assign(appTranslations.pt.companyDetail, {
  financialTab: financialTabPt,
});
Object.assign(appTranslations.en.companyDetail, {
  financialTab: financialTabEn,
});
Object.assign(appTranslations.es.companyDetail, {
  financialTab: financialTabEs,
});

const balanceWizardPt: BalanceWizardTranslations = {
  title: "Enviar balanço",
  breadcrumb: "Navegação estrutural",
  financial: "Financeiro",
  back: "Voltar",
  cancel: "Cancelar",
  dateAt: "{date} às {time}",
  stepper: {
    label: "Etapas do envio",
    current: "etapa atual",
    done: "concluída",
    steps: {
      document: { title: "Documento", text: "Tipo, ano e arquivo" },
      extraction: { title: "Extração com IA", text: "Leitura e validação" },
      review: { title: "Revisão", text: "Confira os valores" },
      done: { title: "Concluído", text: "Indicadores atualizados" },
    },
  },
  extraction: {
    title: "Extraindo dados com IA…",
    errorTitle: "Não foi possível extrair os dados",
    fileLine: "{type} · ano {year}",
    progress: "Progresso da extração",
    stages: {
      upload: {
        title: "Arquivo enviado",
        text: "{size} recebidos com segurança",
        textPending: "Enviando o arquivo com segurança",
      },
      read: {
        title: "Lendo o documento",
        text: "A IA identifica as contas do BP e da DRE na coluna de {year}",
      },
      validate: {
        title: "Validando as contas",
        text: "Conferimos ativo = passivo + PL e os subtotais",
      },
      indicators: {
        title: "Calculando indicadores",
        text: "Liquidez, endividamento, rentabilidade e red flags",
      },
    },
    status: {
      done: "concluído",
      running: "em andamento",
      waiting: "aguardando",
      failed: "falhou",
    },
    seconds: "{n}s",
    errors: {
      invalidCompany: "Empresa inválida para envio de balanço.",
      invalidFile: "Formato não aceito. Envie um PDF, XLSX ou XLS.",
      upload:
        "Não foi possível enviar o arquivo. Verifique a conexão e tente novamente.",
      extract:
        "A IA não conseguiu ler o documento. Tente novamente ou preencha o balanço manualmente.",
    },
    cancel: "Cancelar extração",
    retry: "Tentar novamente",
    back: "Voltar",
  },
  summary: {
    title: "Resumo do envio",
    company: "Empresa",
    document: "Documento",
    year: "Ano de referência",
    newRecord: "{year} (novo registro)",
    updateRecord: "{year} (atualiza registro)",
    file: "Arquivo",
    sentAt: "Enviado em",
    overwrite:
      "Já existe balanço de {year}. Ao salvar, os valores revisados atualizam esse registro.",
  },
  review: {
    title: "Confira os valores extraídos",
    meta: "{type} · {year} · R$",
    validation: "Validação contábil",
    warnings: "{n} avisos",
    warningsOne: "1 aviso",
    noWarnings: "sem avisos",
    checks: {
      assetsSum: {
        ok: "Soma do ativo confere com o total do ativo do documento",
        fail: "Soma do ativo não confere com o total do ativo do documento",
      },
      liabilitiesSum: {
        ok: "Soma do passivo confere com o total do passivo do documento",
        fail: "Soma do passivo não confere com o total do passivo do documento",
      },
      equation: {
        ok: "Ativo total confere com passivo + PL",
        fail: "Ativo total ({left}) difere de passivo + PL ({right}) em {diff}",
      },
      subtotals: {
        ok: "Subtotais conferem com a soma dos itens",
        fail: "Subtotal do documento difere da soma dos itens em: {groups}",
      },
      negatives: {
        ok: "Nenhum valor negativo em contas que não admitem",
        fail: "Há valor negativo em conta que não admite",
      },
    },
    note: "Avisos não bloqueiam o envio. Ajuste o valor abaixo se a IA leu errado, ou salve assim e confira depois no documento original.",
    sections: {
      assets: "ATIVO",
      liabilities: "PASSIVO E PL",
      dre: "DRE",
      monthly: "FATURAMENTO MENSAL",
    },
    fields: {
      ativo_circulante: {
        caixa: "Caixa e equivalentes",
        contas_a_receber: "Contas a receber",
        estoques: "Estoques",
        outros: "Outros ativos circulantes",
        total: "Total ativo circulante",
      },
      ativo_nao_circulante: {
        realizavel_lp: "Realizável a longo prazo",
        investimentos: "Investimentos",
        imobilizado: "Imobilizado",
        intangivel: "Intangível",
        outros: "Outros ativos não circulantes",
        total: "Total ativo não circulante",
      },
      ativo_total: "Total ativo",
      passivo_circulante: {
        fornecedores: "Fornecedores",
        emprestimos_cp: "Empréstimos curto prazo",
        impostos: "Impostos",
        outros: "Outros passivos circulantes",
        total: "Total passivo circulante",
      },
      passivo_nao_circulante: {
        emprestimos_lp: "Empréstimos longo prazo",
        outras_obrigacoes: "Outras obrigações",
        total: "Total passivo não circulante",
      },
      passivo_total: "Total passivo",
      patrimonio_liquido: {
        capital_social: "Capital social",
        reservas: "Reservas",
        lucros_acumulados: "Lucros acumulados",
        total: "Patrimônio líquido",
      },
      dre: {
        receita_bruta: "Receita bruta",
        deducoes_receita: "(-) Deduções",
        receita_liquida: "Receita líquida",
        custo_produtos: "(-) CMV",
        custo_servicos: "(-) Custo dos serviços",
        custo_mercadorias: "(-) Custo das mercadorias",
        lucro_bruto: "Lucro bruto",
        despesas_operacionais: "(-) Despesas operacionais",
        ebit: "EBIT",
        receitas_financeiras: "(+) Receitas financeiras",
        despesas_financeiras: "(-) Despesas financeiras",
        receitas_nao_operacionais: "(+/-) Resultado não operacional",
        resultado_antes_ir: "Resultado antes do IR",
        ir_csll: "(-) IR e CSLL",
        lucro_liquido: "Lucro líquido",
      },
    },
    months: {
      jan: "Janeiro",
      fev: "Fevereiro",
      mar: "Março",
      abr: "Abril",
      mai: "Maio",
      jun: "Junho",
      jul: "Julho",
      ago: "Agosto",
      set: "Setembro",
      out: "Outubro",
      nov: "Novembro",
      dez: "Dezembro",
    },
    monthRange: "{from} a {to}",
    monthsPresent: "Meses informados ({n})",
    monthlyTotal: "Total do ano (soma dos meses)",
    monthMissing: "sem valor no documento",
    computed: "recalculado a partir dos itens",
    warningField: "confira este valor",
    invalid: "Valor inválido",
    invalidSummary: "Corrija os campos com valor inválido para salvar.",
    footer:
      "Totais em cinza são recalculados a partir dos itens. Campos sem valor no documento ficam vazios e não aparecem nos demonstrativos.",
    discard: "Descartar envio",
    back: "Voltar",
    save: "Salvar e calcular indicadores",
    saving: "Salvando…",
    discardDialog: {
      title: "Descartar este envio?",
      text: "Os valores extraídos e as correções serão descartados. Nada será gravado nos dados da empresa.",
      confirm: "Descartar envio",
      cancel: "Continuar revisando",
    },
    doc: {
      title: "Documento original",
      frameTitle: "Documento original (PDF)",
      pdfOnly: "Pré-visualização disponível só para PDF",
      unavailable: "Não foi possível exibir a pré-visualização deste arquivo.",
      loading: "Carregando pré-visualização…",
    },
  },
  done: {
    title: "Balanço {year} salvo",
    text: "Os indicadores, a saúde financeira e os red flags foram recalculados com os dados de {year}.",
    tiles: {
      health: "Saúde financeira",
      currentRatio: "Liquidez corrente",
      debt: "Endiv. geral",
      netMargin: "Margem líquida",
    },
    redFlags: "{n} red flags",
    redFlagsOne: "1 red flag",
    noRedFlags: "Sem red flags",
    noValue: "—",
    warning: "{n} avisos de validação foram registrados na origem dos dados.",
    warningOne: "1 aviso de validação foi registrado na origem dos dados.",
    uploadAnother: "Enviar outro documento",
    viewIndicators: "Ver indicadores",
    originTitle: "Origem dos dados · {year}",
    originMeta: "{date} · extraído por IA e revisado",
    sections: "Seções: {sections}",
    noSections: "Nenhuma seção atualizada",
  },
  toast: {
    expired: "Envio expirado. Selecione o arquivo novamente.",
    saved: "Balanço {year} salvo.",
    saveError:
      "Não foi possível salvar o balanço. Os valores continuam aqui; tente novamente.",
  },
};

const balanceWizardEn: BalanceWizardTranslations = {
  title: "Upload balance sheet",
  breadcrumb: "Breadcrumb",
  financial: "Financials",
  back: "Back",
  cancel: "Cancel",
  dateAt: "{date} at {time}",
  stepper: {
    label: "Upload steps",
    current: "current step",
    done: "completed",
    steps: {
      document: { title: "Document", text: "Type, year and file" },
      extraction: { title: "AI extraction", text: "Reading and validation" },
      review: { title: "Review", text: "Check the values" },
      done: { title: "Done", text: "Indicators updated" },
    },
  },
  extraction: {
    title: "Extracting data with AI…",
    errorTitle: "Could not extract the data",
    fileLine: "{type} · year {year}",
    progress: "Extraction progress",
    stages: {
      upload: {
        title: "File uploaded",
        text: "{size} received securely",
        textPending: "Uploading the file securely",
      },
      read: {
        title: "Reading the document",
        text: "AI identifies the balance sheet and P&L accounts in the {year} column",
      },
      validate: {
        title: "Validating accounts",
        text: "We check assets = liabilities + equity and the subtotals",
      },
      indicators: {
        title: "Calculating indicators",
        text: "Liquidity, debt, profitability and red flags",
      },
    },
    status: {
      done: "done",
      running: "in progress",
      waiting: "waiting",
      failed: "failed",
    },
    seconds: "{n}s",
    errors: {
      invalidCompany: "Invalid company for balance sheet upload.",
      invalidFile: "Format not accepted. Upload a PDF, XLSX or XLS.",
      upload: "Could not upload the file. Check your connection and try again.",
      extract:
        "AI could not read the document. Try again or fill in the balance sheet manually.",
    },
    cancel: "Cancel extraction",
    retry: "Try again",
    back: "Back",
  },
  summary: {
    title: "Upload summary",
    company: "Company",
    document: "Document",
    year: "Reference year",
    newRecord: "{year} (new record)",
    updateRecord: "{year} (updates record)",
    file: "File",
    sentAt: "Sent on",
    overwrite:
      "A balance sheet for {year} already exists. Saving updates that record with the reviewed values.",
  },
  review: {
    title: "Check the extracted values",
    meta: "{type} · {year} · R$",
    validation: "Accounting validation",
    warnings: "{n} warnings",
    warningsOne: "1 warning",
    noWarnings: "no warnings",
    checks: {
      assetsSum: {
        ok: "Sum of assets matches the document's total assets",
        fail: "Sum of assets does not match the document's total assets",
      },
      liabilitiesSum: {
        ok: "Sum of liabilities matches the document's total liabilities",
        fail: "Sum of liabilities does not match the document's total liabilities",
      },
      equation: {
        ok: "Total assets match liabilities + equity",
        fail: "Total assets ({left}) differ from liabilities + equity ({right}) by {diff}",
      },
      subtotals: {
        ok: "Subtotals match the sum of items",
        fail: "Document subtotal differs from the sum of items in: {groups}",
      },
      negatives: {
        ok: "No negative values in accounts that do not allow them",
        fail: "There is a negative value in an account that does not allow it",
      },
    },
    note: "Warnings do not block the upload. Adjust the value below if the AI misread it, or save as is and check the original document later.",
    sections: {
      assets: "ASSETS",
      liabilities: "LIABILITIES AND EQUITY",
      dre: "P&L",
      monthly: "MONTHLY REVENUE",
    },
    fields: {
      ativo_circulante: {
        caixa: "Cash and equivalents",
        contas_a_receber: "Accounts receivable",
        estoques: "Inventories",
        outros: "Other current assets",
        total: "Total current assets",
      },
      ativo_nao_circulante: {
        realizavel_lp: "Long-term receivables",
        investimentos: "Investments",
        imobilizado: "Property, plant and equipment",
        intangivel: "Intangible assets",
        outros: "Other non-current assets",
        total: "Total non-current assets",
      },
      ativo_total: "Total assets",
      passivo_circulante: {
        fornecedores: "Suppliers",
        emprestimos_cp: "Short-term loans",
        impostos: "Taxes",
        outros: "Other current liabilities",
        total: "Total current liabilities",
      },
      passivo_nao_circulante: {
        emprestimos_lp: "Long-term loans",
        outras_obrigacoes: "Other obligations",
        total: "Total non-current liabilities",
      },
      passivo_total: "Total liabilities",
      patrimonio_liquido: {
        capital_social: "Share capital",
        reservas: "Reserves",
        lucros_acumulados: "Retained earnings",
        total: "Equity",
      },
      dre: {
        receita_bruta: "Gross revenue",
        deducoes_receita: "(-) Deductions",
        receita_liquida: "Net revenue",
        custo_produtos: "(-) COGS",
        custo_servicos: "(-) Cost of services",
        custo_mercadorias: "(-) Cost of goods",
        lucro_bruto: "Gross profit",
        despesas_operacionais: "(-) Operating expenses",
        ebit: "EBIT",
        receitas_financeiras: "(+) Financial income",
        despesas_financeiras: "(-) Financial expenses",
        receitas_nao_operacionais: "(+/-) Non-operating result",
        resultado_antes_ir: "Earnings before tax",
        ir_csll: "(-) Income taxes",
        lucro_liquido: "Net income",
      },
    },
    months: {
      jan: "January",
      fev: "February",
      mar: "March",
      abr: "April",
      mai: "May",
      jun: "June",
      jul: "July",
      ago: "August",
      set: "September",
      out: "October",
      nov: "November",
      dez: "December",
    },
    monthRange: "{from} to {to}",
    monthsPresent: "Months reported ({n})",
    monthlyTotal: "Year total (sum of months)",
    monthMissing: "no value in the document",
    computed: "recalculated from the items",
    warningField: "check this value",
    invalid: "Invalid value",
    invalidSummary: "Fix the fields with invalid values to save.",
    footer:
      "Grey totals are recalculated from the items. Fields without a value in the document stay empty and do not appear in the statements.",
    discard: "Discard upload",
    back: "Back",
    save: "Save and calculate indicators",
    saving: "Saving…",
    discardDialog: {
      title: "Discard this upload?",
      text: "The extracted values and your corrections will be discarded. Nothing will be saved to the company data.",
      confirm: "Discard upload",
      cancel: "Keep reviewing",
    },
    doc: {
      title: "Original document",
      frameTitle: "Original document (PDF)",
      pdfOnly: "Preview available for PDF only",
      unavailable: "Could not display a preview of this file.",
      loading: "Loading preview…",
    },
  },
  done: {
    title: "{year} balance sheet saved",
    text: "Indicators, financial health and red flags were recalculated with the {year} data.",
    tiles: {
      health: "Financial health",
      currentRatio: "Current ratio",
      debt: "Total debt",
      netMargin: "Net margin",
    },
    redFlags: "{n} red flags",
    redFlagsOne: "1 red flag",
    noRedFlags: "No red flags",
    noValue: "—",
    warning: "{n} validation warnings were recorded in the data origin.",
    warningOne: "1 validation warning was recorded in the data origin.",
    uploadAnother: "Upload another document",
    viewIndicators: "View indicators",
    originTitle: "Data origin · {year}",
    originMeta: "{date} · extracted by AI and reviewed",
    sections: "Sections: {sections}",
    noSections: "No section updated",
  },
  toast: {
    expired: "Upload expired. Select the file again.",
    saved: "{year} balance sheet saved.",
    saveError:
      "Could not save the balance sheet. Your values are still here; try again.",
  },
};

const balanceWizardEs: BalanceWizardTranslations = {
  title: "Enviar balance",
  breadcrumb: "Ruta de navegación",
  financial: "Financiero",
  back: "Volver",
  cancel: "Cancelar",
  dateAt: "{date} a las {time}",
  stepper: {
    label: "Etapas del envío",
    current: "etapa actual",
    done: "completada",
    steps: {
      document: { title: "Documento", text: "Tipo, año y archivo" },
      extraction: { title: "Extracción con IA", text: "Lectura y validación" },
      review: { title: "Revisión", text: "Revisa los valores" },
      done: { title: "Completado", text: "Indicadores actualizados" },
    },
  },
  extraction: {
    title: "Extrayendo datos con IA…",
    errorTitle: "No fue posible extraer los datos",
    fileLine: "{type} · año {year}",
    progress: "Progreso de la extracción",
    stages: {
      upload: {
        title: "Archivo enviado",
        text: "{size} recibidos con seguridad",
        textPending: "Enviando el archivo con seguridad",
      },
      read: {
        title: "Leyendo el documento",
        text: "La IA identifica las cuentas del balance y del estado de resultados en la columna de {year}",
      },
      validate: {
        title: "Validando las cuentas",
        text: "Verificamos activo = pasivo + patrimonio y los subtotales",
      },
      indicators: {
        title: "Calculando indicadores",
        text: "Liquidez, endeudamiento, rentabilidad y red flags",
      },
    },
    status: {
      done: "completado",
      running: "en curso",
      waiting: "en espera",
      failed: "falló",
    },
    seconds: "{n}s",
    errors: {
      invalidCompany: "Empresa no válida para envío de balance.",
      invalidFile: "Formato no aceptado. Envía un PDF, XLSX o XLS.",
      upload:
        "No fue posible enviar el archivo. Verifica la conexión e inténtalo de nuevo.",
      extract:
        "La IA no pudo leer el documento. Inténtalo de nuevo o completa el balance manualmente.",
    },
    cancel: "Cancelar extracción",
    retry: "Intentar de nuevo",
    back: "Volver",
  },
  summary: {
    title: "Resumen del envío",
    company: "Empresa",
    document: "Documento",
    year: "Año de referencia",
    newRecord: "{year} (nuevo registro)",
    updateRecord: "{year} (actualiza registro)",
    file: "Archivo",
    sentAt: "Enviado el",
    overwrite:
      "Ya existe un balance de {year}. Al guardar, los valores revisados actualizan ese registro.",
  },
  review: {
    title: "Revisa los valores extraídos",
    meta: "{type} · {year} · R$",
    validation: "Validación contable",
    warnings: "{n} avisos",
    warningsOne: "1 aviso",
    noWarnings: "sin avisos",
    checks: {
      assetsSum: {
        ok: "La suma del activo coincide con el total del activo del documento",
        fail: "La suma del activo no coincide con el total del activo del documento",
      },
      liabilitiesSum: {
        ok: "La suma del pasivo coincide con el total del pasivo del documento",
        fail: "La suma del pasivo no coincide con el total del pasivo del documento",
      },
      equation: {
        ok: "El activo total coincide con pasivo + patrimonio",
        fail: "El activo total ({left}) difiere de pasivo + patrimonio ({right}) en {diff}",
      },
      subtotals: {
        ok: "Los subtotales coinciden con la suma de las partidas",
        fail: "El subtotal del documento difiere de la suma de las partidas en: {groups}",
      },
      negatives: {
        ok: "Ningún valor negativo en cuentas que no lo admiten",
        fail: "Hay un valor negativo en una cuenta que no lo admite",
      },
    },
    note: "Los avisos no bloquean el envío. Ajusta el valor abajo si la IA lo leyó mal, o guarda así y revisa después el documento original.",
    sections: {
      assets: "ACTIVO",
      liabilities: "PASIVO Y PATRIMONIO",
      dre: "ESTADO DE RESULTADOS",
      monthly: "FACTURACIÓN MENSUAL",
    },
    fields: {
      ativo_circulante: {
        caixa: "Caja y equivalentes",
        contas_a_receber: "Cuentas por cobrar",
        estoques: "Inventarios",
        outros: "Otros activos corrientes",
        total: "Total activo corriente",
      },
      ativo_nao_circulante: {
        realizavel_lp: "Realizable a largo plazo",
        investimentos: "Inversiones",
        imobilizado: "Inmovilizado",
        intangivel: "Intangible",
        outros: "Otros activos no corrientes",
        total: "Total activo no corriente",
      },
      ativo_total: "Total activo",
      passivo_circulante: {
        fornecedores: "Proveedores",
        emprestimos_cp: "Préstamos a corto plazo",
        impostos: "Impuestos",
        outros: "Otros pasivos corrientes",
        total: "Total pasivo corriente",
      },
      passivo_nao_circulante: {
        emprestimos_lp: "Préstamos a largo plazo",
        outras_obrigacoes: "Otras obligaciones",
        total: "Total pasivo no corriente",
      },
      passivo_total: "Total pasivo",
      patrimonio_liquido: {
        capital_social: "Capital social",
        reservas: "Reservas",
        lucros_acumulados: "Utilidades acumuladas",
        total: "Patrimonio neto",
      },
      dre: {
        receita_bruta: "Ingresos brutos",
        deducoes_receita: "(-) Deducciones",
        receita_liquida: "Ingresos netos",
        custo_produtos: "(-) Costo de ventas",
        custo_servicos: "(-) Costo de servicios",
        custo_mercadorias: "(-) Costo de mercaderías",
        lucro_bruto: "Utilidad bruta",
        despesas_operacionais: "(-) Gastos operativos",
        ebit: "EBIT",
        receitas_financeiras: "(+) Ingresos financieros",
        despesas_financeiras: "(-) Gastos financieros",
        receitas_nao_operacionais: "(+/-) Resultado no operativo",
        resultado_antes_ir: "Resultado antes de impuestos",
        ir_csll: "(-) Impuestos sobre la renta",
        lucro_liquido: "Utilidad neta",
      },
    },
    months: {
      jan: "Enero",
      fev: "Febrero",
      mar: "Marzo",
      abr: "Abril",
      mai: "Mayo",
      jun: "Junio",
      jul: "Julio",
      ago: "Agosto",
      set: "Septiembre",
      out: "Octubre",
      nov: "Noviembre",
      dez: "Diciembre",
    },
    monthRange: "{from} a {to}",
    monthsPresent: "Meses informados ({n})",
    monthlyTotal: "Total del año (suma de los meses)",
    monthMissing: "sin valor en el documento",
    computed: "recalculado a partir de las partidas",
    warningField: "revisa este valor",
    invalid: "Valor no válido",
    invalidSummary: "Corrige los campos con valor no válido para guardar.",
    footer:
      "Los totales en gris se recalculan a partir de las partidas. Los campos sin valor en el documento quedan vacíos y no aparecen en los estados financieros.",
    discard: "Descartar envío",
    back: "Volver",
    save: "Guardar y calcular indicadores",
    saving: "Guardando…",
    discardDialog: {
      title: "¿Descartar este envío?",
      text: "Los valores extraídos y tus correcciones se descartarán. No se guardará nada en los datos de la empresa.",
      confirm: "Descartar envío",
      cancel: "Seguir revisando",
    },
    doc: {
      title: "Documento original",
      frameTitle: "Documento original (PDF)",
      pdfOnly: "Vista previa disponible solo para PDF",
      unavailable: "No fue posible mostrar la vista previa de este archivo.",
      loading: "Cargando vista previa…",
    },
  },
  done: {
    title: "Balance {year} guardado",
    text: "Los indicadores, la salud financiera y los red flags se recalcularon con los datos de {year}.",
    tiles: {
      health: "Salud financiera",
      currentRatio: "Liquidez corriente",
      debt: "Endeud. general",
      netMargin: "Margen neto",
    },
    redFlags: "{n} red flags",
    redFlagsOne: "1 red flag",
    noRedFlags: "Sin red flags",
    noValue: "—",
    warning:
      "Se registraron {n} avisos de validación en el origen de los datos.",
    warningOne: "Se registró 1 aviso de validación en el origen de los datos.",
    uploadAnother: "Enviar otro documento",
    viewIndicators: "Ver indicadores",
    originTitle: "Origen de los datos · {year}",
    originMeta: "{date} · extraído por IA y revisado",
    sections: "Secciones: {sections}",
    noSections: "Ninguna sección actualizada",
  },
  toast: {
    expired: "Envío expirado. Selecciona el archivo de nuevo.",
    saved: "Balance {year} guardado.",
    saveError:
      "No fue posible guardar el balance. Tus valores siguen aquí; inténtalo de nuevo.",
  },
};

Object.assign(appTranslations.pt.companyDetail, {
  balanceWizard: balanceWizardPt,
});
Object.assign(appTranslations.en.companyDetail, {
  balanceWizard: balanceWizardEn,
});
Object.assign(appTranslations.es.companyDetail, {
  balanceWizard: balanceWizardEs,
});

export function getCreditDetailTranslations(language: Language) {
  return {
    ...appTranslations[language].companyDetail.creditAnalysis.detail,
    ...creditDetailDetailTranslations[language],
  };
}

export function getCreditRecommendationCompact(language: Language) {
  return appTranslations[language].companyDetail.creditAnalysis.recommendation
    .compact;
}
